import { useState, useCallback, useMemo, useEffect } from "react";
import { getGapsForLevel, getObstaclePositions, getIngredientPositions } from "@/config/levels";

const DEFAULT_BOARD_SIZE = 8;

// Fruit types with their properties
export const FRUITS = {
  banana: { id: 'banana', emoji: '🍌', name: 'Banana', color: 'banana' },
  apple: { id: 'apple', emoji: '🍎', name: 'Apple', color: 'apple' },
  pear: { id: 'pear', emoji: '🍐', name: 'Pear', color: 'pear' },
  pineapple: { id: 'pineapple', emoji: '🍍', name: 'Pineapple', color: 'pineapple' },
  orange: { id: 'orange', emoji: '🍊', name: 'Orange', color: 'orange' },
  peach: { id: 'peach', emoji: '🍑', name: 'Peach', color: 'peach' },
  strawberry: { id: 'strawberry', emoji: '🍓', name: 'Strawberry', color: 'strawberry' },
  lemon: { id: 'lemon', emoji: '🍋', name: 'Lemon', color: 'lemon' },
  cherry: { id: 'cherry', emoji: '🍒', name: 'Cherry', color: 'cherry' },
  watermelon: { id: 'watermelon', emoji: '🍉', name: 'Watermelon', color: 'watermelon' },
  mango: { id: 'mango', emoji: '🥭', name: 'Mango', color: 'mango' },
  kiwi: { id: 'kiwi', emoji: '🥝', name: 'Kiwi', color: 'kiwi' },
  blueberry: { id: 'blueberry', emoji: '🫐', name: 'Blueberry', color: 'blueberry' },
  grape: { id: 'grape', emoji: '🍇', name: 'Grape', color: 'grape' },
};

// Special fruit types
export const SPECIAL_TYPES = {
  NORMAL: 'normal',
  STRIPED_H: 'striped_h', // Horizontal striped - clears row
  STRIPED_V: 'striped_v', // Vertical striped - clears column
  DISCO: 'disco',         // Disco melon - clears all of swapped fruit type
  WRAPPED: 'wrapped',     // Wrapped/Bag fruit (T/L shape) - 3x3 explosion when matched
  GAP: 'gap',             // Blocked cell - no fruit can be placed here
};

// Obstacle types for advanced levels (51-100) and Dark Forest (121-140)
export const OBSTACLE_TYPES = {
  NONE: 'none',
  ICE: 'ice',             // Ice layer - needs 1-3 matches to break
  CHAIN: 'chain',         // Chained fruit - needs 1 match to unlock
  STONE: 'stone',         // Stone block - indestructible wall
  CHOCOLATE: 'chocolate', // Spreads each turn if not cleared
  HONEY: 'honey',         // Sticky - fruit can't be swapped until cleared
  // Dark Forest obstacles (121-140)
  VINE: 'vine',           // Connects cells - must clear all connected at once
  TREE_SAP: 'tree_sap',   // Sticky amber - slows fruit falling
  BIRD_NEST: 'bird_nest', // Match next to it 3x to hatch bonus items
  HOLLOW_KNOT: 'hollow_knot', // Teleport portal - fruits fall through
  // Ocean Depths obstacles (141-160)
  BUBBLE: 'bubble',         // Fruits float UP instead of falling down
  OCTOPUS_INK: 'octopus_ink', // Covers area, blocks visibility (match to clear)
  ANCHOR: 'anchor',         // Heavy blocker that sinks to bottom
  CURRENT: 'current',       // Pushes fruits left/right each turn
  // Frozen Peaks obstacles (161-180)
  FROZEN_FRUIT: 'frozen_fruit', // Needs 2 adjacent matches to thaw
  SNOWDRIFT: 'snowdrift',   // Builds up each turn, covers bottom
  BLIZZARD: 'blizzard',     // Random freeze effect every few moves
  AVALANCHE: 'avalanche',   // Timer - clear before it buries board
  // Ancient Ruins obstacles (181-200)
  STONE_IDOL: 'stone_idol', // Indestructible, must work around
  SAND_TRAP: 'sand_trap',   // Fruits sink and disappear after 2 turns
  CURSE_TILE: 'curse_tile', // Spreads to adjacent tiles each turn
  LOCK_KEY: 'lock_key',     // Match keys to unlock locked areas
  // ===== NEW WORLDS (201-400) =====
  // Volcanic Core obstacles (201-220)
  LAVA_FLOW: 'lava_flow',       // Rising lava from bottom each turn
  MAGMA_ROCK: 'magma_rock',     // Hot rocks - explode after 3 matches nearby
  ASH_CLOUD: 'ash_cloud',       // Obscures view, clears with matches
  FIRE_GEYSER: 'fire_geyser',   // Periodic eruptions that clear/transform cells
  // Moonlight Garden obstacles (221-240)
  MOONFLOWER: 'moonflower',     // Only matchable during night phase
  SUNBLOOM: 'sunbloom',         // Only matchable during day phase
  SHADOW_VINE: 'shadow_vine',   // Grows during night, shrinks during day
  FIREFLY_SWARM: 'firefly_swarm', // Moving lights that reveal hidden fruits
  // Alchemist's Lab obstacles (241-260)
  POTION_BUBBLE: 'potion_bubble', // Contains colored potions to mix
  CAULDRON: 'cauldron',         // Drop ingredients here to brew
  TOXIC_SPILL: 'toxic_spill',   // Spreads and transforms fruits
  CRYSTAL_VIAL: 'crystal_vial', // Break to release special effects
  // Circus Carnival obstacles (261-280)
  SPINNING_WHEEL: 'spinning_wheel', // Rotates section of board
  BALLOON: 'balloon',           // Floats up, pops with matches
  CONFETTI_CANNON: 'confetti_cannon', // Triggers random clears
  JUGGLING_BALL: 'juggling_ball', // Bounces between positions
  // Pirate Shipwreck obstacles (281-300)
  WATER_LEVEL: 'water_level',   // Rising water blocks matches
  TREASURE_CHEST: 'treasure_chest', // Match to unlock rewards
  GHOST_PIRATE: 'ghost_pirate', // Blocks cells, moves each turn
  BARNACLE: 'barnacle',         // Sticky attachment, slows matches
  // Cherry Blossom Temple obstacles (301-320)
  HARMONY_STONE: 'harmony_stone', // Bonus for balanced matches
  WIND_CHIME: 'wind_chime',     // Chain reaction triggers
  KOINOBORI: 'koinobori',       // Moving target to collect
  ZEN_ROCK: 'zen_rock',         // Immovable but grants bonuses nearby
  // Storm Kingdom obstacles (321-340)
  LIGHTNING_ROD: 'lightning_rod', // Attracts lightning strikes
  STORM_CLOUD: 'storm_cloud',   // Moves and blocks cells
  THUNDER_ORB: 'thunder_orb',   // Explodes in chain when matched
  WIND_GUST: 'wind_gust',       // Pushes fruits in direction
  // Mushroom Cavern obstacles (341-360)
  SPORE_MUSHROOM: 'spore_mushroom', // Spreads spores each turn
  GLOWING_FUNGUS: 'glowing_fungus', // Reveals hidden matches
  POISON_CAP: 'poison_cap',     // Negative effect if matched
  MYCELIUM: 'mycelium',         // Underground network connecting cells
  // Casino Royale obstacles (361-380)
  SLOT_MACHINE: 'slot_machine', // Random fruit transformation
  DICE_BLOCK: 'dice_block',     // Random effect based on roll
  CARD_DECK: 'card_deck',       // Special matching rules
  JACKPOT_TILE: 'jackpot_tile', // Multiplier bonus tile
  // Cosmic Nebula obstacles (381-400)
  BLACK_HOLE: 'black_hole',     // Pulls fruits toward it
  GRAVITY_WELL: 'gravity_well', // Changes fall direction
  STAR_DUST: 'star_dust',       // Bonus particles from matches
  WORMHOLE: 'wormhole',         // Teleports fruits across board
};

// Mission types for advanced levels
export const MISSION_TYPES = {
  COLLECT_FRUIT: 'collect_fruit',           // Original - collect X target fruits
  CREATE_STRIPED: 'create_striped',         // Create X striped fruits
  CREATE_WRAPPED: 'create_wrapped',         // Create X wrapped fruits
  CREATE_DISCO: 'create_disco',             // Create X disco fruits
  BREAK_ICE: 'break_ice',                   // Break all ice blocks
  BREAK_CHAINS: 'break_chains',             // Break all chains
  CLEAR_CHOCOLATE: 'clear_chocolate',       // Clear all chocolate
  DROP_INGREDIENT: 'drop_ingredient',       // Drop ingredients to bottom row
  REACH_SCORE: 'reach_score',               // Reach a target score
  COMBO_MISSION: 'combo_mission',           // Use X special combos
};

const FRUIT_TYPES = Object.keys(FRUITS);

// Fruit sets for progressive difficulty - gradually introduce more fruits
const FRUIT_SETS = {
  // Levels 1-3: 5 basic fruits (Tutorial)
  TUTORIAL: ['banana', 'apple', 'pear', 'pineapple', 'orange'],
  // Levels 4-6: 6 fruits (+peach)
  EASY: ['banana', 'apple', 'pear', 'pineapple', 'orange', 'peach'],
  // Levels 7-10: 7 fruits (+strawberry)
  MEDIUM_EASY: ['banana', 'apple', 'pear', 'pineapple', 'orange', 'peach', 'strawberry'],
  // Levels 11-13: 9 fruits (+lemon, cherry)
  MEDIUM: ['banana', 'apple', 'pear', 'pineapple', 'orange', 'peach', 'strawberry', 'lemon', 'cherry'],
  // Levels 14-17: 14 fruits (+watermelon, mango, kiwi, blueberry, grape) - all fruits for variety
  MEDIUM_HARD: ['banana', 'apple', 'pear', 'pineapple', 'orange', 'peach', 'strawberry', 'lemon', 'cherry', 'watermelon', 'mango', 'kiwi', 'blueberry', 'grape'],
  // Levels 18-35: All 14 fruits
  HARD: ['banana', 'apple', 'pear', 'pineapple', 'orange', 'peach', 'strawberry', 'lemon', 'cherry', 'watermelon', 'mango', 'kiwi', 'blueberry', 'grape'],
  // Levels 36-50: All 13 fruits
  EXPERT: FRUIT_TYPES,
};

// Get fruit set based on level
const getFruitSetForLevel = (levelId) => {
  if (levelId <= 3) return FRUIT_SETS.TUTORIAL;      // 5 fruits
  if (levelId <= 6) return FRUIT_SETS.EASY;          // 6 fruits
  if (levelId <= 10) return FRUIT_SETS.MEDIUM_EASY;  // 7 fruits
  if (levelId <= 13) return FRUIT_SETS.MEDIUM;       // 9 fruits
  if (levelId <= 20) return FRUIT_SETS.MEDIUM_HARD;  // 13 fruits
  if (levelId <= 35) return FRUIT_SETS.HARD;         // 13 fruits
  return FRUIT_SETS.EXPERT;                          // 13 fruits (levels 36-100)
};

// Seeded PRNG (mulberry32) for deterministic PvP boards
export function createSeededRng(seedStr) {
  let h = 0;
  for (let i = 0; i < seedStr.length; i++) {
    h = Math.imul(31, h) + seedStr.charCodeAt(i) | 0;
  }
  let s = h >>> 0;
  return function() {
    s |= 0; s = s + 0x6D2B79F5 | 0;
    let t = Math.imul(s ^ s >>> 15, 1 | s);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

// Generate a random fruit from a specific fruit set
// targetFruit gets boosted probability for levels 11+
// Levels 8-20 get extra boost and striped fruit drops
const getRandomFruit = (fruitSet = FRUIT_TYPES, targetFruit = null, boostTarget = false, levelId = 1, rng = Math.random) => {
  // Levels 8-20: Higher target boost (45%) + chance for striped fruits
  const isEasyBoostLevel = levelId >= 8 && levelId <= 20;
  
  if (isEasyBoostLevel && targetFruit && fruitSet.includes(targetFruit)) {
    // 45% chance for target fruit on levels 8-20
    if (rng() < 0.45) {
      // 8% chance the target fruit is striped (horizontal or vertical)
      if (rng() < 0.08) {
        const stripedType = rng() < 0.5 ? SPECIAL_TYPES.STRIPED_H : SPECIAL_TYPES.STRIPED_V;
        return { type: targetFruit, special: stripedType };
      }
      return { type: targetFruit, special: SPECIAL_TYPES.NORMAL };
    }
    // Pick from remaining fruits (excluding target for this roll)
    const otherFruits = fruitSet.filter(f => f !== targetFruit);
    if (otherFruits.length > 0) {
      const randomIndex = Math.floor(rng() * otherFruits.length);
      // 3% chance any other fruit is striped on levels 8-20
      if (rng() < 0.03) {
        const stripedType = rng() < 0.5 ? SPECIAL_TYPES.STRIPED_H : SPECIAL_TYPES.STRIPED_V;
        return { type: otherFruits[randomIndex], special: stripedType };
      }
      return { type: otherFruits[randomIndex], special: SPECIAL_TYPES.NORMAL };
    }
  }
  
  // If boost is enabled and target fruit is in the set, give it higher probability
  if (boostTarget && targetFruit && fruitSet.includes(targetFruit)) {
    // 30% chance for target fruit, 70% for others
    if (rng() < 0.30) {
      return { type: targetFruit, special: SPECIAL_TYPES.NORMAL };
    }
    // Pick from remaining fruits (excluding target for this roll)
    const otherFruits = fruitSet.filter(f => f !== targetFruit);
    if (otherFruits.length > 0) {
      const randomIndex = Math.floor(rng() * otherFruits.length);
      return { type: otherFruits[randomIndex], special: SPECIAL_TYPES.NORMAL };
    }
  }
  // Default random selection
  const randomIndex = Math.floor(rng() * fruitSet.length);
  return { type: fruitSet[randomIndex], special: SPECIAL_TYPES.NORMAL };
};

// Create a cell
const createCell = (type, special = SPECIAL_TYPES.NORMAL, obstacle = OBSTACLE_TYPES.NONE, obstacleData = null) => ({
  type,
  special,
  obstacle,
  obstacleData, // For ice layers, etc.
  id: Math.random().toString(36).substr(2, 9)
});

// Create a gap cell (blocked, no fruit)
const createGapCell = () => ({
  type: null,
  special: SPECIAL_TYPES.GAP,
  obstacle: OBSTACLE_TYPES.NONE,
  id: 'gap-' + Math.random().toString(36).substr(2, 9),
  isGap: true
});

// Create an ice cell (fruit trapped in ice)
const createIceCell = (type, layers = 1) => ({
  type,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.ICE,
  obstacleData: { layers },
  id: 'ice-' + Math.random().toString(36).substr(2, 9)
});

// Create a chained cell (locked fruit)
const createChainCell = (type) => ({
  type,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.CHAIN,
  id: 'chain-' + Math.random().toString(36).substr(2, 9)
});

// Create a chocolate cell (spreads)
const createChocolateCell = () => ({
  type: null,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.CHOCOLATE,
  isChocolate: true,
  id: 'choco-' + Math.random().toString(36).substr(2, 9)
});

// Create a honey cell (sticky)
const createHoneyCell = (type) => ({
  type,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.HONEY,
  id: 'honey-' + Math.random().toString(36).substr(2, 9)
});

// === DARK FOREST OBSTACLES (121-140) ===

// Create a vine cell (connected to other vines - must clear all at once)
const createVineCell = (type, vineGroupId = null) => ({
  type,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.VINE,
  obstacleData: { 
    vineGroupId: vineGroupId || 'vine-group-' + Math.random().toString(36).substr(2, 6),
    isVine: true
  },
  isVine: true,
  id: 'vine-' + Math.random().toString(36).substr(2, 9)
});

// Create a tree sap cell (slows fruit falling - takes 2 turns to drop)
const createTreeSapCell = (type) => ({
  type,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.TREE_SAP,
  obstacleData: { slowDrop: true, dropDelay: 2 },
  isTreeSap: true,
  id: 'sap-' + Math.random().toString(36).substr(2, 9)
});

// Create a bird nest cell (match next to it 3x to hatch bonus)
const createBirdNestCell = () => ({
  type: null,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.BIRD_NEST,
  obstacleData: { hatchProgress: 0, hatchTarget: 3 },
  isBirdNest: true,
  id: 'nest-' + Math.random().toString(36).substr(2, 9)
});

// Create a hollow knot cell (teleport portal)
const createHollowKnotCell = (linkedKnotId = null) => ({
  type: null,
  special: SPECIAL_TYPES.GAP, // Acts like a gap but with teleport
  obstacle: OBSTACLE_TYPES.HOLLOW_KNOT,
  obstacleData: { linkedKnotId },
  isHollowKnot: true,
  isGap: false, // Not a true gap - fruits can pass through
  id: 'knot-' + Math.random().toString(36).substr(2, 9)
});

// === OCEAN DEPTHS OBSTACLES (141-160) ===

// Create a bubble cell (fruit floats UP instead of falling)
const createBubbleCell = (type) => ({
  type,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.BUBBLE,
  obstacleData: { floatsUp: true },
  isBubble: true,
  id: 'bubble-' + Math.random().toString(36).substr(2, 9)
});

// Create an octopus ink cell (blocks visibility, match to clear)
const createOctopusInkCell = (type = null) => ({
  type,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.OCTOPUS_INK,
  obstacleData: { obscured: true },
  isOctopusInk: true,
  id: 'ink-' + Math.random().toString(36).substr(2, 9)
});

// Create an anchor cell (heavy blocker that sinks)
const createAnchorCell = () => ({
  type: null,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.ANCHOR,
  obstacleData: { weight: 3 },
  isAnchor: true,
  isBlocker: true,
  id: 'anchor-' + Math.random().toString(36).substr(2, 9)
});

// Create a current cell (pushes fruits left/right)
const createCurrentCell = (type, direction = 'right') => ({
  type,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.CURRENT,
  obstacleData: { direction, strength: 1 },
  isCurrent: true,
  id: 'current-' + Math.random().toString(36).substr(2, 9)
});

// === FROZEN PEAKS OBSTACLES (161-180) ===

// Create a frozen fruit cell (needs 2 adjacent matches to thaw)
const createFrozenFruitCell = (type) => ({
  type,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.FROZEN_FRUIT,
  obstacleData: { frozenLayers: 2 }, // Needs 2 matches to fully thaw
  isFrozenFruit: true,
  id: 'frozen-' + Math.random().toString(36).substr(2, 9)
});

// Create a snowdrift cell (builds up each turn)
const createSnowdriftCell = (height = 1) => ({
  type: null,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.SNOWDRIFT,
  obstacleData: { height, maxHeight: 3 },
  isSnowdrift: true,
  isBlocker: true,
  id: 'snow-' + Math.random().toString(36).substr(2, 9)
});

// Create a blizzard marker (random freeze effect)
const createBlizzardCell = (type) => ({
  type,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.BLIZZARD,
  obstacleData: { freezeChance: 0.3 },
  isBlizzard: true,
  id: 'blizz-' + Math.random().toString(36).substr(2, 9)
});

// Create an avalanche zone (timer countdown)
const createAvalancheCell = (turnsRemaining = 5) => ({
  type: null,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.AVALANCHE,
  obstacleData: { turnsRemaining, triggered: false },
  isAvalanche: true,
  id: 'aval-' + Math.random().toString(36).substr(2, 9)
});

// === ANCIENT RUINS OBSTACLES (181-200) ===

// Create a stone idol cell (indestructible)
const createStoneIdolCell = () => ({
  type: null,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.STONE_IDOL,
  obstacleData: { indestructible: true },
  isStoneIdol: true,
  isBlocker: true,
  isGap: true, // Acts like a gap - can't be moved
  id: 'idol-' + Math.random().toString(36).substr(2, 9)
});

// Create a sand trap cell (fruit sinks after 2 turns)
const createSandTrapCell = (type) => ({
  type,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.SAND_TRAP,
  obstacleData: { turnsUntilSink: 2 },
  isSandTrap: true,
  id: 'sand-' + Math.random().toString(36).substr(2, 9)
});

// Create a curse tile (spreads to adjacent each turn)
const createCurseTileCell = (type = null) => ({
  type,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.CURSE_TILE,
  obstacleData: { cursed: true, spreadChance: 0.5 },
  isCurseTile: true,
  id: 'curse-' + Math.random().toString(36).substr(2, 9)
});

// Create a lock/key cell
const createLockKeyCell = (type, isKey = false, lockId = null) => ({
  type,
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.LOCK_KEY,
  obstacleData: { isKey, lockId, locked: !isKey },
  isLockKey: true,
  id: 'lock-' + Math.random().toString(36).substr(2, 9)
});

// Create an ingredient cell (drops to bottom)
const createIngredientCell = (emoji) => ({
  type: 'ingredient',
  special: SPECIAL_TYPES.NORMAL,
  obstacle: OBSTACLE_TYPES.NONE,
  isIngredient: true,
  ingredientEmoji: emoji,
  id: 'ing-' + Math.random().toString(36).substr(2, 9)
});

// Check if a position is within any gap
const isPositionInGap = (row, col, gaps) => {
  for (const gap of gaps) {
    if (row >= gap.row && row < gap.row + gap.height &&
        col >= gap.col && col < gap.col + gap.width) {
      return true;
    }
  }
  return false;
};

// Create initial board with configurable size, fruit set, gaps, and obstacles
// For levels 11+, boost target fruit spawn rate
// For levels 20-50, add gaps to the board
// For levels 51+, add obstacles (ice, chains, chocolate, honey)
const createBoard = (boardSize = DEFAULT_BOARD_SIZE, fruitSet = FRUIT_TYPES, targetFruit = null, levelId = 1, levelConfig = null, rng = Math.random) => {
  const boostTarget = levelId >= 11; // Boost target fruit for levels 11+
  const gaps = getGapsForLevel(levelId);
  const board = [];
  
  // Get obstacle positions for levels 51+
  const obstaclePositions = levelConfig ? getObstaclePositions(levelConfig, boardSize) : { ice: [], chain: [], chocolate: [], honey: [], vines: [], tree_sap: [], bird_nest: [], hollow_knot: [] };
  const ingredientPositions = levelConfig ? getIngredientPositions(levelConfig, boardSize) : [];
  
  // Create position lookup maps for obstacles
  const iceMap = new Map(obstaclePositions.ice.map(p => [`${p.row},${p.col}`, p.layers || 1]));
  const chainSet = new Set(obstaclePositions.chain.map(p => `${p.row},${p.col}`));
  const chocolateSet = new Set(obstaclePositions.chocolate.map(p => `${p.row},${p.col}`));
  const honeySet = new Set(obstaclePositions.honey.map(p => `${p.row},${p.col}`));
  const ingredientMap = new Map(ingredientPositions.map(p => [`${p.row},${p.col}`, p.emoji]));
  // Dark Forest obstacles
  const vineMap = new Map(obstaclePositions.vines?.map(p => [`${p.row},${p.col}`, p.vineGroupId]) || []);
  const treeSapSet = new Set(obstaclePositions.tree_sap?.map(p => `${p.row},${p.col}`) || []);
  const birdNestSet = new Set(obstaclePositions.bird_nest?.map(p => `${p.row},${p.col}`) || []);
  const hollowKnotMap = new Map(obstaclePositions.hollow_knot?.map(p => [`${p.row},${p.col}`, p.linkedKnotId]) || []);
  // Ocean Depths obstacles (141-160)
  const bubbleSet = new Set(obstaclePositions.bubbles?.map(p => `${p.row},${p.col}`) || []);
  const octopusInkSet = new Set(obstaclePositions.octopus_ink?.map(p => `${p.row},${p.col}`) || []);
  const anchorSet = new Set(obstaclePositions.anchors?.map(p => `${p.row},${p.col}`) || []);
  const currentMap = new Map(obstaclePositions.currents?.map(p => [`${p.row},${p.col}`, p.direction || 'right']) || []);
  // Frozen Peaks obstacles (161-180)
  const frozenFruitSet = new Set(obstaclePositions.frozen_fruits?.map(p => `${p.row},${p.col}`) || []);
  const snowdriftSet = new Set(obstaclePositions.snowdrift?.map(p => `${p.row},${p.col}`) || []);
  const blizzardSet = new Set(obstaclePositions.blizzard?.map(p => `${p.row},${p.col}`) || []);
  const avalancheSet = new Set(obstaclePositions.avalanche?.map(p => `${p.row},${p.col}`) || []);
  // Ancient Ruins obstacles (181-200)
  const stoneIdolSet = new Set(obstaclePositions.stone_idols?.map(p => `${p.row},${p.col}`) || []);
  const sandTrapSet = new Set(obstaclePositions.sand_traps?.map(p => `${p.row},${p.col}`) || []);
  const curseTileSet = new Set(obstaclePositions.curse_tiles?.map(p => `${p.row},${p.col}`) || []);
  const lockKeyMap = new Map(obstaclePositions.lock_key?.map(p => [`${p.row},${p.col}`, { isKey: p.isKey, lockId: p.lockId }]) || []);
  
  for (let row = 0; row < boardSize; row++) {
    const rowArray = [];
    for (let col = 0; col < boardSize; col++) {
      const posKey = `${row},${col}`;
      
      // Check if this position is a gap
      if (isPositionInGap(row, col, gaps)) {
        rowArray.push(createGapCell());
        continue;
      }
      
      // Check if this is an ingredient position
      if (ingredientMap.has(posKey)) {
        rowArray.push(createIngredientCell(ingredientMap.get(posKey)));
        continue;
      }
      
      // Check if this is a chocolate position (no fruit, just chocolate)
      if (chocolateSet.has(posKey)) {
        rowArray.push(createChocolateCell());
        continue;
      }
      
      // Check if this is a bird nest position (no fruit, acts as blocker)
      if (birdNestSet.has(posKey)) {
        rowArray.push(createBirdNestCell());
        continue;
      }
      
      // Check if this is a hollow knot position (teleport portal)
      if (hollowKnotMap.has(posKey)) {
        rowArray.push(createHollowKnotCell(hollowKnotMap.get(posKey)));
        continue;
      }
      
      // Check for Ocean Depths blockers
      if (anchorSet.has(posKey)) {
        rowArray.push(createAnchorCell());
        continue;
      }
      
      // Check for Frozen Peaks blockers
      if (snowdriftSet.has(posKey)) {
        rowArray.push(createSnowdriftCell());
        continue;
      }
      
      if (avalancheSet.has(posKey)) {
        rowArray.push(createAvalancheCell());
        continue;
      }
      
      // Check for Ancient Ruins blockers
      if (stoneIdolSet.has(posKey)) {
        rowArray.push(createStoneIdolCell());
        continue;
      }
      
      let fruit;
      do {
        fruit = getRandomFruit(fruitSet, targetFruit, boostTarget, levelId, rng);
      } while (
        // Prevent initial matches of 3 or more (skip gap cells)
        (col >= 2 && 
          rowArray[col - 1]?.type === fruit.type && !rowArray[col - 1]?.isGap &&
          rowArray[col - 2]?.type === fruit.type && !rowArray[col - 2]?.isGap) ||
        (row >= 2 && 
          board[row - 1]?.[col]?.type === fruit.type && !board[row - 1]?.[col]?.isGap &&
          board[row - 2]?.[col]?.type === fruit.type && !board[row - 2]?.[col]?.isGap)
      );
      
      // Apply obstacles to the fruit cell
      if (iceMap.has(posKey)) {
        rowArray.push(createIceCell(fruit.type, iceMap.get(posKey)));
      } else if (chainSet.has(posKey)) {
        rowArray.push(createChainCell(fruit.type));
      } else if (honeySet.has(posKey)) {
        rowArray.push(createHoneyCell(fruit.type));
      } else if (vineMap.has(posKey)) {
        rowArray.push(createVineCell(fruit.type, vineMap.get(posKey)));
      } else if (treeSapSet.has(posKey)) {
        rowArray.push(createTreeSapCell(fruit.type));
      // Ocean Depths obstacles
      } else if (bubbleSet.has(posKey)) {
        rowArray.push(createBubbleCell(fruit.type));
      } else if (octopusInkSet.has(posKey)) {
        rowArray.push(createOctopusInkCell(fruit.type));
      } else if (currentMap.has(posKey)) {
        rowArray.push(createCurrentCell(fruit.type, currentMap.get(posKey)));
      // Frozen Peaks obstacles
      } else if (frozenFruitSet.has(posKey)) {
        rowArray.push(createFrozenFruitCell(fruit.type));
      } else if (blizzardSet.has(posKey)) {
        rowArray.push(createBlizzardCell(fruit.type));
      // Ancient Ruins obstacles
      } else if (sandTrapSet.has(posKey)) {
        rowArray.push(createSandTrapCell(fruit.type));
      } else if (curseTileSet.has(posKey)) {
        rowArray.push(createCurseTileCell(fruit.type));
      } else if (lockKeyMap.has(posKey)) {
        const lockData = lockKeyMap.get(posKey);
        rowArray.push(createLockKeyCell(fruit.type, lockData.isKey, lockData.lockId));
      } else {
        rowArray.push(createCell(fruit.type, SPECIAL_TYPES.NORMAL));
      }
    }
    board.push(rowArray);
  }
  
  // Add starting special fruits if configured
  if (levelConfig?.startingSpecials) {
    const specials = levelConfig.startingSpecials;
    const availablePositions = [];
    
    // Find all non-obstacle, non-gap positions
    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize; col++) {
        const cell = board[row][col];
        if (cell && !cell.isGap && !cell.isChocolate && !cell.isIngredient && 
            !cell.hasIce && !cell.hasChain && !cell.hasHoney &&
            cell.special === SPECIAL_TYPES.NORMAL) {
          availablePositions.push({ row, col });
        }
      }
    }
    
    // Shuffle available positions
    for (let i = availablePositions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [availablePositions[i], availablePositions[j]] = [availablePositions[j], availablePositions[i]];
    }
    
    let posIndex = 0;
    
    // Add striped fruits
    if (specials.striped && posIndex < availablePositions.length) {
      for (let i = 0; i < specials.striped && posIndex < availablePositions.length; i++) {
        const pos = availablePositions[posIndex++];
        const direction = Math.random() < 0.5 ? 'horizontal' : 'vertical';
        board[pos.row][pos.col].special = direction === 'horizontal' ? SPECIAL_TYPES.STRIPED_H : SPECIAL_TYPES.STRIPED_V;
      }
    }
    
    // Add wrapped fruits
    if (specials.wrapped && posIndex < availablePositions.length) {
      for (let i = 0; i < specials.wrapped && posIndex < availablePositions.length; i++) {
        const pos = availablePositions[posIndex++];
        board[pos.row][pos.col].special = SPECIAL_TYPES.WRAPPED;
      }
    }
    
    // Add disco fruits
    if (specials.disco && posIndex < availablePositions.length) {
      for (let i = 0; i < specials.disco && posIndex < availablePositions.length; i++) {
        const pos = availablePositions[posIndex++];
        board[pos.row][pos.col].special = SPECIAL_TYPES.DISCO;
        board[pos.row][pos.col].type = 'disco';
      }
    }
  }
  
  return board;
};

// Apply gravity in any direction (up, down, left, right)
// Used for Cosmic Nebula world mechanic
const applyDirectionalGravity = (board, col, fruitSet, targetFruit, boostTarget, levelId, gravityDir) => {
  const boardSize = board.length;
  let ingredientsDropped = 0;
  
  if (gravityDir === 'up') {
    // Gravity UP - fruits fall upward
    let emptyRow = 0;
    for (let row = 0; row < boardSize; row++) {
      if (board[row][col] !== null && !board[row][col]?.isGap) {
        if (row !== emptyRow) {
          board[emptyRow][col] = board[row][col];
          board[row][col] = null;
        }
        emptyRow++;
      }
    }
    // Fill empty cells from top (new fruits come from bottom)
    for (let row = 0; row < boardSize; row++) {
      if (board[row][col] === null) {
        const randomFruit = getRandomFruit(fruitSet, targetFruit, boostTarget, levelId);
        board[row][col] = createCell(randomFruit.type, randomFruit.special);
      }
    }
  } else if (gravityDir === 'left') {
    // Gravity LEFT - fruits fall leftward (process row by row)
    // Note: This function is called per column, so we need to handle the entire row
    const row = col; // Repurpose col as row index for horizontal gravity
    let emptyCol = 0;
    for (let c = 0; c < boardSize; c++) {
      if (board[row][c] !== null && !board[row][c]?.isGap) {
        if (c !== emptyCol) {
          board[row][emptyCol] = board[row][c];
          board[row][c] = null;
        }
        emptyCol++;
      }
    }
    // Fill empty cells from left (new fruits come from right)
    for (let c = 0; c < boardSize; c++) {
      if (board[row][c] === null) {
        const randomFruit = getRandomFruit(fruitSet, targetFruit, boostTarget, levelId);
        board[row][c] = createCell(randomFruit.type, randomFruit.special);
      }
    }
  } else if (gravityDir === 'right') {
    // Gravity RIGHT - fruits fall rightward
    const row = col; // Repurpose col as row index for horizontal gravity
    let emptyCol = boardSize - 1;
    for (let c = boardSize - 1; c >= 0; c--) {
      if (board[row][c] !== null && !board[row][c]?.isGap) {
        if (c !== emptyCol) {
          board[row][emptyCol] = board[row][c];
          board[row][c] = null;
        }
        emptyCol--;
      }
    }
    // Fill empty cells from right (new fruits come from left)
    for (let c = boardSize - 1; c >= 0; c--) {
      if (board[row][c] === null) {
        const randomFruit = getRandomFruit(fruitSet, targetFruit, boostTarget, levelId);
        board[row][c] = createCell(randomFruit.type, randomFruit.special);
      }
    }
  }
  
  return ingredientsDropped;
};

// Drop fruits and fill empty cells (respecting gaps)
// This is a helper function to handle the complex drop logic with gaps
// Returns the number of ingredients that reached the bottom row
const dropAndFillColumn = (board, col, fruitSet, targetFruit, boostTarget, levelId = 1, gravityDir = 'down') => {
  const boardSize = board.length;
  let ingredientsDropped = 0;
  
  // For non-standard gravity, use the directional gravity function
  if (gravityDir !== 'down') {
    return applyDirectionalGravity(board, col, fruitSet, targetFruit, boostTarget, levelId, gravityDir);
  }
  
  // Find all gap positions and hollow knots in this column
  const gapRows = new Set();
  const hollowKnotRows = [];
  for (let row = 0; row < boardSize; row++) {
    if (board[row][col]?.isGap) {
      gapRows.add(row);
    }
    if (board[row][col]?.isHollowKnot || board[row][col]?.obstacle === OBSTACLE_TYPES.HOLLOW_KNOT) {
      hollowKnotRows.push(row);
    }
  }
  
  // Helper to check if a cell has tree sap (slows dropping)
  const hasTreeSap = (cell) => {
    return cell?.isTreeSap || cell?.obstacle === OBSTACLE_TYPES.TREE_SAP;
  };
  
  // Helper to check if cell has bubble (floats UP instead of falling)
  const hasBubble = (cell) => {
    return cell?.obstacle === OBSTACLE_TYPES.BUBBLE;
  };
  
  // First, handle BUBBLE obstacles - they float UP
  for (let row = 1; row < boardSize; row++) {
    const cell = board[row][col];
    if (cell && hasBubble(cell)) {
      // Try to float up
      const aboveRow = row - 1;
      if (aboveRow >= 0 && board[aboveRow][col] && !board[aboveRow][col].isGap && !hasBubble(board[aboveRow][col])) {
        // Swap with the cell above (bubble floats up)
        const temp = board[aboveRow][col];
        board[aboveRow][col] = cell;
        board[row][col] = temp;
      }
    }
  }
  
  // If no gaps, use simple drop logic
  if (gapRows.size === 0) {
    let emptyRow = boardSize - 1;
    for (let row = boardSize - 1; row >= 0; row--) {
      if (board[row][col] !== null) {
        // Tree sap slows movement - only drop every other turn
        if (hasTreeSap(board[row][col])) {
          const dropDelay = board[row][col].obstacleData?.dropDelay || 0;
          if (dropDelay > 0) {
            // Still delayed, reduce counter but don't move
            board[row][col] = {
              ...board[row][col],
              obstacleData: { ...board[row][col].obstacleData, dropDelay: dropDelay - 1 }
            };
            emptyRow = row - 1; // Skip this position
            continue;
          }
        }
        
        if (row !== emptyRow) {
          board[emptyRow][col] = board[row][col];
          board[row][col] = null;
        }
        emptyRow--;
      }
    }
    
    // Check if any ingredient reached the bottom row (last row)
    const bottomCell = board[boardSize - 1][col];
    if (bottomCell?.isIngredient) {
      ingredientsDropped++;
      board[boardSize - 1][col] = null; // Remove the ingredient
      // Shift everything down again since we removed the ingredient
      emptyRow = boardSize - 1;
      for (let row = boardSize - 2; row >= 0; row--) {
        if (board[row][col] !== null) {
          board[emptyRow][col] = board[row][col];
          board[row][col] = null;
          emptyRow--;
        }
      }
    }
    
    // Fill empty cells
    for (let row = boardSize - 1; row >= 0; row--) {
      if (board[row][col] === null) {
        const randomFruit = getRandomFruit(fruitSet, targetFruit, boostTarget, levelId);
        board[row][col] = createCell(randomFruit.type, randomFruit.special);
      }
    }
    return ingredientsDropped;
  }
  
  // Process each section of the column separately (gaps divide the column)
  let sectionStart = boardSize - 1;
  
  while (sectionStart >= 0) {
    // Skip gaps
    if (gapRows.has(sectionStart)) {
      sectionStart--;
      continue;
    }
    
    // Find the end of this section (next gap above or top of board)
    let sectionEnd = sectionStart;
    while (sectionEnd > 0 && !gapRows.has(sectionEnd - 1)) {
      sectionEnd--;
    }
    
    // Drop fruits within this section
    let emptyRow = sectionStart;
    for (let row = sectionStart; row >= sectionEnd; row--) {
      if (board[row][col] !== null && !board[row][col]?.isGap) {
        if (row !== emptyRow) {
          board[emptyRow][col] = board[row][col];
          board[row][col] = null;
        }
        emptyRow--;
      }
    }
    
    // Check if any ingredient is at the bottom of this section
    // For sections ending at the actual bottom row, ingredients are "dropped"
    if (sectionStart === boardSize - 1) {
      const bottomCell = board[sectionStart][col];
      if (bottomCell?.isIngredient) {
        ingredientsDropped++;
        board[sectionStart][col] = null;
        // Shift section down again
        emptyRow = sectionStart;
        for (let row = sectionStart - 1; row >= sectionEnd; row--) {
          if (board[row][col] !== null && !board[row][col]?.isGap) {
            board[emptyRow][col] = board[row][col];
            board[row][col] = null;
            emptyRow--;
          }
        }
      }
    }
    
    // Fill empty cells in this section with new fruits
    for (let row = sectionStart; row >= sectionEnd; row--) {
      if (board[row][col] === null && !gapRows.has(row)) {
        const randomFruit = getRandomFruit(fruitSet, targetFruit, boostTarget, levelId);
        board[row][col] = createCell(randomFruit.type, randomFruit.special);
      }
    }
    
    // Move to section above the gap
    sectionStart = sectionEnd - 1;
    while (sectionStart >= 0 && gapRows.has(sectionStart)) {
      sectionStart--;
    }
  }
  
  return ingredientsDropped;
};

// Check if two cells are adjacent
const areAdjacent = (cell1, cell2) => {
  if (!cell1 || !cell2) return false;
  const rowDiff = Math.abs(cell1.row - cell2.row);
  const colDiff = Math.abs(cell1.col - cell2.col);
  return (rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1);
};

// Detect T and L shapes for wrapped fruits
const detectTLShapes = (board, matchGroups) => {
  const tlShapes = [];
  const boardSize = board.length;
  
  // Find intersecting horizontal and vertical matches of same fruit type
  const horizontalMatches = matchGroups.filter(g => g.direction === 'horizontal');
  const verticalMatches = matchGroups.filter(g => g.direction === 'vertical');
  
  for (const hMatch of horizontalMatches) {
    for (const vMatch of verticalMatches) {
      // Must be same fruit type
      if (hMatch.type !== vMatch.type) continue;
      
      // Check if they intersect
      const hCells = new Set(hMatch.cells.map(c => `${c.row},${c.col}`));
      const vCells = vMatch.cells.map(c => `${c.row},${c.col}`);
      
      let intersectionCell = null;
      for (const vCell of vCells) {
        if (hCells.has(vCell)) {
          intersectionCell = vCell;
          break;
        }
      }
      
      if (intersectionCell) {
        // We have a T or L shape!
        // Combine cells from both matches (removing duplicates)
        const allCells = new Set([...hMatch.cells.map(c => `${c.row},${c.col}`), ...vMatch.cells.map(c => `${c.row},${c.col}`)]);
        
        // T or L shape needs at least 5 cells total (3+3-1 = 5 minimum)
        if (allCells.size >= 5) {
          const [intRow, intCol] = intersectionCell.split(',').map(Number);
          tlShapes.push({
            type: hMatch.type,
            intersectionRow: intRow,
            intersectionCol: intCol,
            cells: Array.from(allCells).map(pos => {
              const [r, c] = pos.split(',').map(Number);
              return { row: r, col: c };
            })
          });
        }
      }
    }
  }
  
  return tlShapes;
};

// Find all matches on the board and determine special fruits to create
const findMatches = (board) => {
  const matches = new Map(); // position -> { cells: Set, direction: 'horizontal'|'vertical', length: number }
  const matchGroups = [];
  const boardSize = board.length;
  
  // Check horizontal matches (skip gap cells and ingredients)
  for (let row = 0; row < boardSize; row++) {
    let col = 0;
    while (col < boardSize) {
      const cell = board[row][col];
      const fruit = cell?.type;
      // Skip gap cells, empty cells, and ingredient cells (ingredients are solid and cannot be matched)
      if (!fruit || cell?.isGap || cell?.isIngredient) { col++; continue; }
      
      let matchLength = 1;
      while (col + matchLength < boardSize && 
             board[row][col + matchLength]?.type === fruit &&
             !board[row][col + matchLength]?.isGap &&
             !board[row][col + matchLength]?.isIngredient) {
        matchLength++;
      }
      
      if (matchLength >= 3) {
        const group = {
          cells: [],
          direction: 'horizontal',
          length: matchLength,
          type: fruit,
          row,
          col
        };
        for (let i = 0; i < matchLength; i++) {
          const pos = `${row},${col + i}`;
          matches.set(pos, { row, col: col + i });
          group.cells.push({ row, col: col + i });
        }
        matchGroups.push(group);
      }
      col += matchLength;
    }
  }
  
  // Check vertical matches (skip gap cells and ingredients)
  for (let col = 0; col < boardSize; col++) {
    let row = 0;
    while (row < boardSize) {
      const cell = board[row][col];
      const fruit = cell?.type;
      // Skip gap cells, empty cells, and ingredient cells (ingredients are solid and cannot be matched)
      if (!fruit || cell?.isGap || cell?.isIngredient) { row++; continue; }
      
      let matchLength = 1;
      while (row + matchLength < boardSize && 
             board[row + matchLength][col]?.type === fruit &&
             !board[row + matchLength][col]?.isGap &&
             !board[row + matchLength][col]?.isIngredient) {
        matchLength++;
      }
      
      if (matchLength >= 3) {
        const group = {
          cells: [],
          direction: 'vertical',
          length: matchLength,
          type: fruit,
          row,
          col
        };
        for (let i = 0; i < matchLength; i++) {
          const pos = `${row + i},${col}`;
          if (!matches.has(pos)) {
            matches.set(pos, { row: row + i, col });
          }
          group.cells.push({ row: row + i, col });
        }
        matchGroups.push(group);
      }
      row += matchLength;
    }
  }
  
  // Detect T and L shapes
  const tlShapes = detectTLShapes(board, matchGroups);
  
  return { matches, matchGroups, tlShapes };
};

// Process matches and handle special fruits (with configurable target fruit and fruit set)
// Also handles obstacles: ice layers, chains, chocolate, honey
const processMatchesWithTarget = (board, matches, matchGroups, swappedCell = null, targetFruit = 'banana', fruitSet = FRUIT_TYPES, boostTarget = false, tlShapes = [], levelId = 1) => {
  const newBoard = board.map(row => row.map(cell => cell ? { ...cell } : null));
  const boardSize = board.length;
  let targetFruitCount = 0;
  let scoreMultiplier = 1;
  const cellsToRemove = new Set();
  
  // Track obstacle breaks for mission progress
  let iceBroken = 0;
  let chainsBroken = 0;
  let chocolateCleared = 0;
  let vinesBroken = 0;
  let nestHatched = 0;
  
  // Track vine groups that need to be cleared together
  const vineGroupsToCheck = new Map(); // vineGroupId -> Set of positions
  
  // Helper to process a cell for removal (handles obstacles)
  const processCellForRemoval = (row, col, fromVineGroup = false) => {
    const cell = newBoard[row][col];
    if (!cell || cell.isGap) return false;
    
    // IMPORTANT: Ingredients are SOLID and cannot be removed by matches!
    // They can only be dropped to the bottom
    if (cell.isIngredient) {
      return false; // Ingredients cannot be eliminated
    }
    
    // Bird nests are blockers - can't be directly matched
    if (cell.isBirdNest || cell.obstacle === OBSTACLE_TYPES.BIRD_NEST) {
      return false; // Bird nests are not removable by direct match
    }
    
    // Hollow knots are portals - can't be removed
    if (cell.isHollowKnot || cell.obstacle === OBSTACLE_TYPES.HOLLOW_KNOT) {
      return false;
    }
    
    // Handle ice obstacle - reduce layers
    if (cell.obstacle === OBSTACLE_TYPES.ICE) {
      const layers = cell.obstacleData?.layers || 1;
      if (layers > 1) {
        // Reduce ice layer, don't remove fruit yet
        newBoard[row][col] = {
          ...cell,
          obstacleData: { layers: layers - 1 }
        };
        iceBroken++;
        return false; // Cell not removed, just ice damaged
      } else {
        // Last ice layer broken, fruit is now free
        newBoard[row][col] = {
          ...cell,
          obstacle: OBSTACLE_TYPES.NONE,
          obstacleData: null
        };
        iceBroken++;
        return true; // Can now be removed
      }
    }
    
    // Handle chain obstacle - breaks on match
    if (cell.obstacle === OBSTACLE_TYPES.CHAIN) {
      newBoard[row][col] = {
        ...cell,
        obstacle: OBSTACLE_TYPES.NONE
      };
      chainsBroken++;
      return true; // Chain broken, fruit can be removed
    }
    
    // Handle honey obstacle - clears on match
    if (cell.obstacle === OBSTACLE_TYPES.HONEY) {
      newBoard[row][col] = {
        ...cell,
        obstacle: OBSTACLE_TYPES.NONE
      };
      return true; // Honey cleared, fruit can be removed
    }
    
    // Handle vine obstacle - must clear all vines in the group together
    if (cell.isVine || cell.obstacle === OBSTACLE_TYPES.VINE) {
      const vineGroupId = cell.obstacleData?.vineGroupId;
      if (vineGroupId && !fromVineGroup) {
        // Add to vine groups to check later
        if (!vineGroupsToCheck.has(vineGroupId)) {
          vineGroupsToCheck.set(vineGroupId, new Set());
        }
        vineGroupsToCheck.get(vineGroupId).add(`${row},${col}`);
        return false; // Will be processed with the group
      } else if (fromVineGroup) {
        // Being cleared as part of vine group
        vinesBroken++;
        return true;
      }
      return false;
    }
    
    // Handle tree sap obstacle - clears on match but fruit was slowed
    if (cell.isTreeSap || cell.obstacle === OBSTACLE_TYPES.TREE_SAP) {
      newBoard[row][col] = {
        ...cell,
        obstacle: OBSTACLE_TYPES.NONE,
        isTreeSap: false,
        obstacleData: null
      };
      return true; // Tree sap cleared, fruit can be removed
    }
    
    // Handle chocolate (blocker cell) - adjacent matches clear it
    if (cell.isChocolate || cell.obstacle === OBSTACLE_TYPES.CHOCOLATE) {
      chocolateCleared++;
      return true; // Chocolate cleared
    }
    
    // Handle FROZEN_FRUIT obstacle - needs 2 adjacent matches to thaw
    if (cell.obstacle === OBSTACLE_TYPES.FROZEN_FRUIT) {
      const frozenLayers = cell.obstacleData?.frozenLayers || 2;
      if (frozenLayers > 1) {
        // Still frozen, reduce layers but don't remove fruit
        newBoard[row][col] = {
          ...cell,
          obstacleData: { ...cell.obstacleData, frozenLayers: frozenLayers - 1 }
        };
        return false; // Can't be removed yet
      } else {
        // Fully thawed, fruit is now free
        newBoard[row][col] = {
          ...cell,
          obstacle: OBSTACLE_TYPES.NONE,
          obstacleData: null
        };
        return true; // Can now be removed
      }
    }
    
    // Handle BUBBLE obstacle - these float UP, handled in gravity phase
    if (cell.obstacle === OBSTACLE_TYPES.BUBBLE) {
      // Bubble pops when matched, fruit is freed
      newBoard[row][col] = {
        ...cell,
        obstacle: OBSTACLE_TYPES.NONE,
        obstacleData: null
      };
      return true; // Bubble popped, fruit can be removed
    }
    
    // Handle CURSE_TILE obstacle - spreads to adjacent tiles each turn if not cleared
    if (cell.obstacle === OBSTACLE_TYPES.CURSE_TILE) {
      // Curse is cleared when matched
      newBoard[row][col] = {
        ...cell,
        obstacle: OBSTACLE_TYPES.NONE,
        obstacleData: null
      };
      return true; // Curse cleared
    }
    
    return true; // Normal cell, can be removed
  };
  
  // Helper to check and damage adjacent bird nests
  const checkAdjacentBirdNests = (row, col) => {
    const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]];
    for (const [dr, dc] of directions) {
      const nr = row + dr;
      const nc = col + dc;
      if (nr >= 0 && nr < boardSize && nc >= 0 && nc < boardSize) {
        const cell = newBoard[nr][nc];
        if (cell && (cell.isBirdNest || cell.obstacle === OBSTACLE_TYPES.BIRD_NEST)) {
          const progress = (cell.obstacleData?.hatchProgress || 0) + 1;
          const target = cell.obstacleData?.hatchTarget || 3;
          
          if (progress >= target) {
            // Nest hatches! Replace with a random special fruit
            const fruitTypes = ['banana', 'apple', 'orange', 'strawberry', 'blueberry'];
            const randomFruit = fruitTypes[Math.floor(Math.random() * fruitTypes.length)];
            const specialTypes = [SPECIAL_TYPES.STRIPED_H, SPECIAL_TYPES.STRIPED_V, SPECIAL_TYPES.WRAPPED];
            const randomSpecial = specialTypes[Math.floor(Math.random() * specialTypes.length)];
            
            newBoard[nr][nc] = {
              type: randomFruit,
              special: randomSpecial,
              obstacle: OBSTACLE_TYPES.NONE,
              id: 'hatched-' + Math.random().toString(36).substr(2, 9)
            };
            nestHatched++;
          } else {
            // Increment progress
            newBoard[nr][nc] = {
              ...cell,
              obstacleData: { ...cell.obstacleData, hatchProgress: progress }
            };
          }
        }
      }
    }
  };
  
  // First, check if any special fruits are being matched and trigger their effects
  matches.forEach((pos, key) => {
    const cell = newBoard[pos.row][pos.col];
    if (!cell) return;
    
    if (cell.special === SPECIAL_TYPES.STRIPED_H) {
      // Clear entire row
      for (let c = 0; c < boardSize; c++) {
        if (processCellForRemoval(pos.row, c)) {
          cellsToRemove.add(`${pos.row},${c}`);
          if (newBoard[pos.row][c]?.type === targetFruit) targetFruitCount++;
        }
      }
      scoreMultiplier += 0.5;
    } else if (cell.special === SPECIAL_TYPES.STRIPED_V) {
      // Clear entire column
      for (let r = 0; r < boardSize; r++) {
        if (processCellForRemoval(r, pos.col)) {
          cellsToRemove.add(`${r},${pos.col}`);
          if (newBoard[r][pos.col]?.type === targetFruit) targetFruitCount++;
        }
      }
      scoreMultiplier += 0.5;
    } else if (cell.special === SPECIAL_TYPES.WRAPPED) {
      // Wrapped fruit explodes in 3x3 area
      for (let r = pos.row - 1; r <= pos.row + 1; r++) {
        for (let c = pos.col - 1; c <= pos.col + 1; c++) {
          if (r >= 0 && r < boardSize && c >= 0 && c < boardSize) {
            if (processCellForRemoval(r, c)) {
              cellsToRemove.add(`${r},${c}`);
              if (newBoard[r][c]?.type === targetFruit) targetFruitCount++;
            }
          }
        }
      }
      scoreMultiplier += 0.75;
    } else if (cell.special === SPECIAL_TYPES.DISCO && swappedCell) {
      // Clear all fruits of the swapped type
      const swappedType = newBoard[swappedCell.row][swappedCell.col]?.type;
      if (swappedType) {
        for (let r = 0; r < boardSize; r++) {
          for (let c = 0; c < boardSize; c++) {
            if (newBoard[r][c]?.type === swappedType) {
              if (processCellForRemoval(r, c)) {
                cellsToRemove.add(`${r},${c}`);
                if (swappedType === targetFruit) targetFruitCount++;
              }
            }
          }
        }
        scoreMultiplier += 1;
      }
    }
  });
  
  // Process matched cells - check for adjacent chocolate to clear
  matches.forEach((pos, key) => {
    // Check adjacent cells for chocolate
    const adjacentOffsets = [[-1, 0], [1, 0], [0, -1], [0, 1]];
    adjacentOffsets.forEach(([dr, dc]) => {
      const r = pos.row + dr;
      const c = pos.col + dc;
      if (r >= 0 && r < boardSize && c >= 0 && c < boardSize) {
        const adjCell = newBoard[r][c];
        if (adjCell?.isChocolate || adjCell?.obstacle === OBSTACLE_TYPES.CHOCOLATE) {
          if (processCellForRemoval(r, c)) {
            cellsToRemove.add(`${r},${c}`);
          }
        }
      }
    });
  });
  
  // Add all matched cells to removal set (if they can be removed after obstacle processing)
  matches.forEach((pos, key) => {
    const cell = newBoard[pos.row][pos.col];
    if (!cell) return;
    
    // Process the cell for removal (handles obstacles like ice, chains)
    const canRemove = processCellForRemoval(pos.row, pos.col);
    
    // Re-check the cell AFTER processing (obstacle may have been reduced/removed)
    const updatedCell = newBoard[pos.row][pos.col];
    
    // Skip if cell still has ice with remaining layers
    if (updatedCell.obstacle === OBSTACLE_TYPES.ICE && updatedCell.obstacleData?.layers > 0) {
      return; // Ice still has layers, don't remove yet
    }
    
    if (canRemove) {
      cellsToRemove.add(key);
      if (updatedCell?.type === targetFruit && updatedCell.special === SPECIAL_TYPES.NORMAL) {
        targetFruitCount++;
      }
    }
  });
  
  // Also process adjacent cells with obstacles (ice/chains can be damaged by adjacent matches)
  const processedAdjacentCells = new Set();
  matches.forEach((pos) => {
    const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]]; // up, down, left, right
    directions.forEach(([dr, dc]) => {
      const adjRow = pos.row + dr;
      const adjCol = pos.col + dc;
      const adjKey = `${adjRow},${adjCol}`;
      
      // Skip if out of bounds or already processed
      if (adjRow < 0 || adjRow >= boardSize || adjCol < 0 || adjCol >= boardSize) return;
      if (processedAdjacentCells.has(adjKey)) return;
      if (matches.has(adjKey)) return; // Already handled as a matched cell
      
      const adjCell = newBoard[adjRow][adjCol];
      if (!adjCell || adjCell.isGap) return;
      
      // Only process cells with ice or chain obstacles
      if (adjCell.obstacle === OBSTACLE_TYPES.ICE || adjCell.obstacle === OBSTACLE_TYPES.CHAIN) {
        processedAdjacentCells.add(adjKey);
        processCellForRemoval(adjRow, adjCol);
      }
    });
    
    // Check for adjacent bird nests
    checkAdjacentBirdNests(pos.row, pos.col);
  });
  
  // Process vine groups - all vines in a group must be matched together
  vineGroupsToCheck.forEach((positions, vineGroupId) => {
    // Find ALL vines in this group on the board
    const allVinesInGroup = new Set();
    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        const cell = newBoard[r][c];
        if (cell && (cell.isVine || cell.obstacle === OBSTACLE_TYPES.VINE)) {
          if (cell.obstacleData?.vineGroupId === vineGroupId) {
            allVinesInGroup.add(`${r},${c}`);
          }
        }
      }
    }
    
    // Check if ALL vines in the group are in the match
    let allMatched = true;
    allVinesInGroup.forEach(pos => {
      if (!positions.has(pos) && !cellsToRemove.has(pos)) {
        allMatched = false;
      }
    });
    
    // If all vines are matched, clear them all
    if (allMatched && allVinesInGroup.size > 0) {
      allVinesInGroup.forEach(posKey => {
        const [r, c] = posKey.split(',').map(Number);
        const cell = newBoard[r][c];
        if (cell) {
          // Clear vine obstacle and allow removal
          newBoard[r][c] = {
            ...cell,
            obstacle: OBSTACLE_TYPES.NONE,
            isVine: false,
            obstacleData: null
          };
          if (processCellForRemoval(r, c, true)) {
            cellsToRemove.add(posKey);
            if (cell.type === targetFruit) targetFruitCount++;
          }
        }
      });
    }
  });
  
  // Determine which special fruits to create based on match groups
  const specialFruitsToCreate = [];
  
  // Track cells that are part of T/L shapes (to avoid creating other specials there)
  const tlShapeCells = new Set();
  tlShapes.forEach(shape => {
    shape.cells.forEach(cell => {
      tlShapeCells.add(`${cell.row},${cell.col}`);
    });
    // Create wrapped fruit at intersection point
    specialFruitsToCreate.push({
      row: shape.intersectionRow,
      col: shape.intersectionCol,
      type: shape.type,
      special: SPECIAL_TYPES.WRAPPED
    });
  });
  
  matchGroups.forEach(group => {
    // Skip if this group is part of a T/L shape
    const groupCellsInTL = group.cells.some(cell => tlShapeCells.has(`${cell.row},${cell.col}`));
    if (groupCellsInTL) return;
    
    if (group.length === 4) {
      // 4 in a row = striped fruit
      const centerIndex = Math.floor(group.length / 2);
      const centerCell = group.cells[centerIndex];
      specialFruitsToCreate.push({
        row: centerCell.row,
        col: centerCell.col,
        type: group.type,
        special: group.direction === 'horizontal' ? SPECIAL_TYPES.STRIPED_H : SPECIAL_TYPES.STRIPED_V
      });
    } else if (group.length >= 5) {
      // 5 in a row = disco fruit
      const centerIndex = Math.floor(group.length / 2);
      const centerCell = group.cells[centerIndex];
      specialFruitsToCreate.push({
        row: centerCell.row,
        col: centerCell.col,
        type: group.type,
        special: SPECIAL_TYPES.DISCO
      });
    }
  });
  
  // Clear matched cells
  cellsToRemove.forEach(key => {
    const [row, col] = key.split(',').map(Number);
    // Don't remove if this cell should become a special fruit
    const isSpecialTarget = specialFruitsToCreate.some(sf => sf.row === row && sf.col === col);
    if (!isSpecialTarget) {
      newBoard[row][col] = null;
    }
  });
  
  // Create special fruits and count them by type
  let stripedCreated = 0;
  let wrappedCreated = 0;
  specialFruitsToCreate.forEach(sf => {
    newBoard[sf.row][sf.col] = createCell(sf.type, sf.special);
    if (sf.special === SPECIAL_TYPES.STRIPED_H || sf.special === SPECIAL_TYPES.STRIPED_V) {
      stripedCreated++;
    } else if (sf.special === SPECIAL_TYPES.WRAPPED) {
      wrappedCreated++;
    }
  });
  
  // Drop fruits down and fill empty cells (respecting gaps)
  // Track ingredients that reach the bottom
  let ingredientsDropped = 0;
  for (let col = 0; col < boardSize; col++) {
    ingredientsDropped += dropAndFillColumn(newBoard, col, fruitSet, targetFruit, boostTarget, levelId);
  }
  
  // CURSE_TILE spread logic - spreads to one random adjacent cell after each turn
  const spreadCurseTiles = () => {
    const cursedCells = [];
    // Find all cursed cells
    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        if (newBoard[r][c]?.obstacle === OBSTACLE_TYPES.CURSE_TILE) {
          cursedCells.push({ row: r, col: c });
        }
      }
    }
    
    // Each cursed cell has a chance to spread
    cursedCells.forEach(({ row, col }) => {
      // 30% chance to spread each turn
      if (Math.random() < 0.3) {
        const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]];
        const validTargets = [];
        
        for (const [dr, dc] of directions) {
          const nr = row + dr;
          const nc = col + dc;
          if (nr >= 0 && nr < boardSize && nc >= 0 && nc < boardSize) {
            const targetCell = newBoard[nr][nc];
            // Can only spread to normal cells without obstacles
            if (targetCell && !targetCell.isGap && targetCell.obstacle === OBSTACLE_TYPES.NONE) {
              validTargets.push({ row: nr, col: nc });
            }
          }
        }
        
        // Spread to one random adjacent cell
        if (validTargets.length > 0) {
          const target = validTargets[Math.floor(Math.random() * validTargets.length)];
          newBoard[target.row][target.col] = {
            ...newBoard[target.row][target.col],
            obstacle: OBSTACLE_TYPES.CURSE_TILE,
            obstacleData: { spreadFrom: `${row},${col}` }
          };
        }
      }
    });
  };
  
  // Spread curse tiles after each move
  spreadCurseTiles();
  
  // DEBUG: Log obstacle breaking progress
  if (iceBroken > 0 || chainsBroken > 0 || chocolateCleared > 0 || stripedCreated > 0 || wrappedCreated > 0 || ingredientsDropped > 0) {
  }
  
  return { 
    newBoard, 
    targetFruitCount, 
    scoreMultiplier, 
    cellsRemoved: cellsToRemove.size, 
    stripedCreated, 
    wrappedCreated,
    iceBroken,
    chainsBroken,
    chocolateCleared,
    ingredientsDropped
  };
};

// Legacy processMatches for backward compatibility
const processMatches = (board, matches, matchGroups, swappedCell = null, fruitSet = FRUIT_TYPES) => {
  return processMatchesWithTarget(board, matches, matchGroups, swappedCell, 'banana', fruitSet);
};

// Check if a swap would create a match
const wouldCreateMatch = (board, row1, col1, row2, col2) => {
  // Don't allow swapping with gaps
  if (board[row1][col1]?.isGap || board[row2][col2]?.isGap) {
    return false;
  }
  
  // Special case: if either cell is a disco fruit, always allow swap
  if (board[row1][col1]?.special === SPECIAL_TYPES.DISCO || 
      board[row2][col2]?.special === SPECIAL_TYPES.DISCO) {
    return true;
  }
  
  const newBoard = board.map(row => row.map(cell => cell ? { ...cell } : null));
  // Swap
  [newBoard[row1][col1], newBoard[row2][col2]] = [newBoard[row2][col2], newBoard[row1][col1]];
  const { matches } = findMatches(newBoard);
  return matches.size > 0;
};

// Check if any valid moves exist on the board
const hasValidMoves = (board) => {
  const size = board.length;
  
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      // Skip gap cells
      if (board[row][col]?.isGap) continue;
      
      // Check swap with right neighbor (if not a gap)
      if (col < size - 1 && !board[row][col + 1]?.isGap) {
        if (wouldCreateMatch(board, row, col, row, col + 1)) {
          return true;
        }
      }
      // Check swap with bottom neighbor (if not a gap)
      if (row < size - 1 && !board[row + 1][col]?.isGap) {
        if (wouldCreateMatch(board, row, col, row + 1, col)) {
          return true;
        }
      }
    }
  }
  return false;
};

// Shuffle the board when no moves are available (preserve gaps)
const shuffleBoard = (board) => {
  const size = board.length;
  const allCells = [];
  const gapPositions = [];
  
  // Collect all non-gap cells and track gap positions
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (board[row][col]?.isGap) {
        gapPositions.push({ row, col });
      } else if (board[row][col]) {
        allCells.push({ ...board[row][col] });
      }
    }
  }
  
  // Fisher-Yates shuffle
  for (let i = allCells.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [allCells[i], allCells[j]] = [allCells[j], allCells[i]];
  }
  
  // Create new board with shuffled cells (preserving gap positions)
  const newBoard = [];
  let cellIndex = 0;
  for (let row = 0; row < size; row++) {
    newBoard[row] = [];
    for (let col = 0; col < size; col++) {
      // Check if this is a gap position
      const isGapPos = gapPositions.some(g => g.row === row && g.col === col);
      if (isGapPos) {
        newBoard[row][col] = createGapCell();
      } else {
        const cell = allCells[cellIndex];
        newBoard[row][col] = {
          ...cell,
          row,
          col
        };
        cellIndex++;
      }
    }
  }
  
  return newBoard;
};

export function useGameState(levelConfig = null, playerLevel = 1, activeUpgrades = {}, calculateMasteryBonus = null, boardSeed = null) {
  // Default level config if none provided
  const config = levelConfig || {
    id: 1,
    targetFruit: 'banana',
    targetCount: 30,
    moves: 20,
    boardSize: 8,
  };
  
  const boardSize = config.boardSize || 8;
  const levelId = config.id || 1;
  const targetFruit = config.targetFruit || 'banana';
  const missionType = config.missionType || 'collect_fruit';
  
  // Calculate max sword uses from upgrades (default 1, 2 with double_sword upgrade)
  const calculatedMaxSwordUses = activeUpgrades.swordUses || 1;
  
  // Get fruit set based on level - gradually introduces more fruits
  const fruitSet = getFruitSetForLevel(levelId);

  const [board, setBoard] = useState(() => {
    const rng = boardSeed ? createSeededRng(boardSeed) : Math.random;
    return createBoard(boardSize, fruitSet, targetFruit, levelId, config, rng);
  });
  const [selectedCell, setSelectedCell] = useState(null);
  const [score, setScore] = useState(0);
  const [movesLeft, setMovesLeft] = useState(config.moves);
  const [fruitsCollected, setFruitsCollected] = useState(0);
  // Dual mission fruit tracking
  const [dualFruitsCollected, setDualFruitsCollected] = useState({ fruit1: 0, fruit2: 0 });
  const [gameStatus, setGameStatus] = useState('playing'); // 'playing', 'won', 'lost'
  
  // Helper to track dual fruit collection
  const trackDualFruits = useCallback((eliminatedCells, boardState) => {
    if (config.missionType !== 'dual_collect' || !config.dualMission) return;
    
    const fruit1Type = config.dualMission.objective1.fruit;
    const fruit2Type = config.dualMission.objective2.fruit;
    
    let fruit1Count = 0;
    let fruit2Count = 0;
    
    eliminatedCells.forEach(key => {
      const [r, c] = key.split('-').map(Number);
      const cell = boardState[r]?.[c];
      if (cell) {
        if (cell.type === fruit1Type) fruit1Count++;
        if (cell.type === fruit2Type) fruit2Count++;
      }
    });
    
    if (fruit1Count > 0 || fruit2Count > 0) {
      setDualFruitsCollected(prev => ({
        fruit1: prev.fruit1 + fruit1Count,
        fruit2: prev.fruit2 + fruit2Count
      }));
    }
  }, [config.missionType, config.dualMission]);
  
  // Track dual fruits from eliminatedByType object (more reliable)
  const trackDualFruitsFromEliminated = useCallback((eliminatedByType) => {
    if (config.missionType !== 'dual_collect' || !config.dualMission || !eliminatedByType) return;
    
    const fruit1Type = config.dualMission.objective1.fruit;
    const fruit2Type = config.dualMission.objective2.fruit;
    
    const fruit1Count = eliminatedByType[fruit1Type] || 0;
    const fruit2Count = eliminatedByType[fruit2Type] || 0;
    
    if (fruit1Count > 0 || fruit2Count > 0) {
      setDualFruitsCollected(prev => ({
        fruit1: prev.fruit1 + fruit1Count,
        fruit2: prev.fruit2 + fruit2Count
      }));
    }
  }, [config.missionType, config.dualMission]);
  
  const [highScore, setHighScore] = useState(() => {
    const saved = localStorage.getItem('fruitCrush_highScore');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [isAnimating, setIsAnimating] = useState(false);
  const [lastMatchInfo, setLastMatchInfo] = useState(null);
  const [explodingCells, setExplodingCells] = useState(new Set()); // Track cells being exploded
  const [clearEffect, setClearEffect] = useState(null); // Track row/column clear effects
  
  // Combo tracking for 2x points
  const [consecutive4Combos, setConsecutive4Combos] = useState(0); // Count of consecutive 4-fruit matches
  const [doublePointsActive, setDoublePointsActive] = useState(false); // Is 2x points active for next turn
  const [showDoublePointsAlert, setShowDoublePointsAlert] = useState(false); // Show the 2X alert
  
  // Insta-elimination ability (6-fruit combo)
  const [instaEliminationActive, setInstaEliminationActive] = useState(false); // Can player use insta-elimination
  const [showInstaEliminationPicker, setShowInstaEliminationPicker] = useState(false); // Show fruit picker modal
  
  // Swapping animation state
  const [swappingCells, setSwappingCells] = useState(null); // { from: {row, col}, to: {row, col}, direction }
  
  // Challenge tracking state
  const [lastEliminatedFruits, setLastEliminatedFruits] = useState(null);
  const [lastDiscoCombo, setLastDiscoCombo] = useState(false);
  
  // Shuffle state
  const [showShuffleAlert, setShowShuffleAlert] = useState(false);
  
  // Sword ability state - multiple tiers based on player level
  // Level 3: Bronze Sword (3x3)
  // Level 5: Silver Sword (4x4)
  // Level 10: Golden Sword (5x5)
  // Level 15: Diamond Sword (7x7)
  const [swordUsesRemaining, setSwordUsesRemaining] = useState(calculatedMaxSwordUses); // How many sword uses left this level
  const [swordAbilityUsed, setSwordAbilityUsed] = useState(false); // Has ALL sword uses been exhausted
  const [swordMode, setSwordMode] = useState(false); // Is sword mode active (waiting for click)
  const [swordSlashAnimation, setSwordSlashAnimation] = useState(null); // { centerRow, centerCol, tier } for animation
  const [maxSwordUses, setMaxSwordUses] = useState(calculatedMaxSwordUses); // Max sword uses (1 default, 2 with upgrade)

  // God Sword ability state - legendary item that clears entire board
  const [godSwordMode, setGodSwordMode] = useState(false); // Is god sword mode active
  const [godSwordUsesRemaining, setGodSwordUsesRemaining] = useState(0); // Uses left for this level session
  const [godSwordAnimation, setGodSwordAnimation] = useState(false); // Animation state

  // === MILESTONE ITEM ABILITIES ===
  // Crystal Ball - Preview next 3 rows of falling fruits
  const [crystalBallActive, setCrystalBallActive] = useState(false);
  const [crystalBallPreview, setCrystalBallPreview] = useState([]); // Array of next 3 rows

  // Fruit Magnet - Pull matching fruits together
  const [fruitMagnetMode, setFruitMagnetMode] = useState(false);

  // Dragon Scale - +50% boss damage (tracked in boss fight component)
  const [dragonScaleActive, setDragonScaleActive] = useState(false);

  // Moon Stone - Highlights best moves for 30 seconds
  const [moonStoneActive, setMoonStoneActive] = useState(false);
  const [moonStoneTimeout, setMoonStoneTimeout] = useState(null);

  // Thunder Strike - Clears random column + row
  const [thunderStrikeAnimation, setThunderStrikeAnimation] = useState(null);

  // === NEW WORLD ITEM ABILITIES ===
  // Ocean Pearl - Bubble shield blocking obstacle spawn
  const [oceanPearlActive, setOceanPearlActive] = useState(false);

  // Trident Shard - Clear 3 random rows
  const [tridentShardAnimation, setTridentShardAnimation] = useState(null);

  // Frost Crystal - Freeze and shatter 5 random tiles
  const [frostCrystalAnimation, setFrostCrystalAnimation] = useState(null);

  // Aurora Essence - Rainbow cascade effect
  const [auroraEssenceActive, setAuroraEssenceActive] = useState(false);

  // Pharaoh Scarab - Destroy all curse tiles
  const [scarabAnimation, setScarabAnimation] = useState(null);

  // Golden Key - Unlock all locked tiles
  const [goldenKeyAnimation, setGoldenKeyAnimation] = useState(null);

  // Special combo animation state
  const [specialComboAnimation, setSpecialComboAnimation] = useState(null); // { type, centerRow, centerCol, phase }

  // Consumable states
  const [bombMode, setBombMode] = useState(false); // Is bomb mode active (waiting for click)
  const [colorBombMode, setColorBombMode] = useState(false); // Is color bomb mode active (waiting for click)
  const [hintCell, setHintCell] = useState(null); // { row, col } for hint highlight
  const [doubleXPActive, setDoubleXPActive] = useState(false); // Is 2X XP consumable active
  const [doubleXPTurnsLeft, setDoubleXPTurnsLeft] = useState(0); // Turns remaining for 2X XP
  const [emergencyShieldUsedToday, setEmergencyShieldUsedToday] = useState(() => {
    // Check if emergency shield was already used today
    const lastUsed = localStorage.getItem('fruitCrush_emergencyShieldUsed');
    if (lastUsed) {
      const lastUsedDate = new Date(lastUsed).toDateString();
      const today = new Date().toDateString();
      return lastUsedDate === today;
    }
    return false;
  });
  
  // Challenge tracking for special fruits created
  const [lastStripedCreated, setLastStripedCreated] = useState(0);
  const [lastWrappedCreated, setLastWrappedCreated] = useState(0);
  const [lastSpecialComboTriggered, setLastSpecialComboTriggered] = useState(false);
  
  // Mission tracking for levels 51-100
  const [missionProgress, setMissionProgress] = useState({
    stripedCreated: 0,
    wrappedCreated: 0,
    discoCreated: 0,
    iceBroken: 0,
    chainsBroken: 0,
    chocolateCleared: 0,
    ingredientsDropped: 0,
    specialCombos: 0,
  });
  
  // Ingredient tracking
  const [ingredientsOnBoard, setIngredientsOnBoard] = useState([]);
  
  // === ENHANCED ANIMATION STATES ===
  // Match size effect tracking
  const [lastMatchSize, setLastMatchSize] = useState(0); // Track the last match size for effects
  const [showFruityCrush, setShowFruityCrush] = useState(false); // Show "Fruity Crush!" text for 5+ matches
  const [screenShake, setScreenShake] = useState(false); // Trigger screen shake
  
  // === COMBO NAME SYSTEM ===
  // Dynamic combo names based on match size and cascade
  const [comboName, setComboName] = useState(null); // { name, emoji, color }
  const [showComboName, setShowComboName] = useState(false);
  
  // Combo names configuration
  const COMBO_NAMES = {
    // Match size combos (3, 4, 5, 6+)
    3: { name: "Nice!", emoji: "👍", color: "from-green-400 to-green-600" },
    4: { name: "SWEET!", emoji: "🍬", color: "from-blue-400 to-blue-600" },
    5: { name: "FRUIT FRENZY!", emoji: "🍇🍓🍊", color: "from-purple-400 to-pink-600" },
    6: { name: "MEGA CRUSH!", emoji: "💥🔥", color: "from-orange-400 to-red-600" },
    7: { name: "SPECTACULAR!", emoji: "🌟✨💫", color: "from-yellow-400 to-orange-500" },
    // Cascade combos
    cascade2: { name: "DOUBLE!", emoji: "2️⃣", color: "from-cyan-400 to-blue-500" },
    cascade3: { name: "TRIPLE THREAT!", emoji: "3️⃣🔥", color: "from-pink-400 to-red-500" },
    cascade4: { name: "QUAD DAMAGE!", emoji: "4️⃣💪", color: "from-amber-400 to-orange-600" },
    cascade5: { name: "UNSTOPPABLE!", emoji: "🚀💥🔥", color: "from-red-500 to-yellow-500" },
    // Special combos
    striped: { name: "STRIPED BURST!", emoji: "⚡", color: "from-yellow-300 to-yellow-600" },
    wrapped: { name: "BOOM!", emoji: "💣💥", color: "from-orange-500 to-red-600" },
    disco: { name: "DISCO FEVER!", emoji: "🪩✨🕺", color: "from-purple-500 to-pink-500" },
    special_combo: { name: "SUPER COMBO!", emoji: "🎆🎇", color: "from-red-500 to-purple-600" },
  };
  
  // Score fly-up animations
  const [scorePopups, setScorePopups] = useState([]); // Array of {id, value, x, y}
  
  // Combo multiplier display
  const [cascadeCount, setCascadeCount] = useState(0); // Current cascade count for multiplier display
  const [showComboMultiplier, setShowComboMultiplier] = useState(false);
  
  // Victory/Lose animations
  const [showVictoryAnimation, setShowVictoryAnimation] = useState(false);
  const [showLoseAnimation, setShowLoseAnimation] = useState(false);
  
  // Idle state tracking
  const [isIdle, setIsIdle] = useState(false);
  const [idleTimer, setIdleTimer] = useState(null);
  
  // Level intro animation
  const [showLevelIntro, setShowLevelIntro] = useState(true);
  
  // Urgency state (low moves warning)
  const [showUrgency, setShowUrgency] = useState(false);
  
  // Special fruit creation animation
  const [newSpecialFruits, setNewSpecialFruits] = useState([]); // Array of {row, col, type}

  // ===== WORLD MECHANIC STATES =====
  // Volcanic Core - Lava Rise
  const [lavaLevel, setLavaLevel] = useState(0); // Number of rows covered by lava from bottom
  const [movesSinceLavaRise, setMovesSinceLavaRise] = useState(0);
  
  // Moonlight Garden - Day/Night Cycle
  const [isDayTime, setIsDayTime] = useState(true); // true = day, false = night
  const [movesSinceCycleChange, setMovesSinceCycleChange] = useState(0);
  const [dayNightTransition, setDayNightTransition] = useState(false); // Animation state
  
  // Cosmic Nebula - Gravity Shift
  const [gravityDirection, setGravityDirection] = useState('down'); // 'down', 'up', 'left', 'right'
  const [gravityShiftPending, setGravityShiftPending] = useState(false);
  
  // Storm Kingdom - Lightning Strikes
  const [lightningCells, setLightningCells] = useState([]); // Cells about to be struck
  const [showLightning, setShowLightning] = useState(false);
  
  // Casino Royale - Multipliers
  const [activeMultiplier, setActiveMultiplier] = useState(1); // Random multiplier (1x, 2x, 3x, 5x)
  const [showJackpot, setShowJackpot] = useState(false);
  
  // Alchemist's Lab - Potion Mixing
  const [potionColors, setPotionColors] = useState([]); // Collected potion colors
  const [potionBonus, setPotionBonus] = useState(0);
  
  // Circus Carnival - Spinning Wheels
  const [spinningCells, setSpinningCells] = useState([]); // Cells that will spin/rotate
  const [showSpinEffect, setShowSpinEffect] = useState(false);
  
  // Pirate Shipwreck - Rising Water
  const [waterLevel, setWaterLevel] = useState(0);
  const [movesSinceWaterRise, setMovesSinceWaterRise] = useState(0);
  
  // Cherry Blossom Temple - Harmony Bonus
  const [harmonyStreak, setHarmonyStreak] = useState(0);
  const [harmonyBonus, setHarmonyBonus] = useState(0);
  
  // Mushroom Cavern - Spore Spread
  const [sporeCount, setSporeCount] = useState(0);
  const [sporeCells, setSporeCells] = useState([]);
  
  // Get world mechanic from level config
  const worldMechanic = config.worldMechanic || null;
  const lavaRiseRate = config.lavaRiseRate || 6;
  const cycleLength = config.cycleLength || 3;

  // Reset when level config changes
  useEffect(() => {
    let newBoard = createBoard(boardSize, fruitSet, targetFruit, levelId, config);
    
    // Apply Lucky Start upgrade (15% chance to start with a special fruit)
    const luckyStartChance = activeUpgrades.luckyStart || 0;
    if (luckyStartChance > 0 && Math.random() < luckyStartChance) {
      // Find a random non-obstacle, non-gap cell to convert to special
      const validCells = [];
      for (let r = 0; r < boardSize; r++) {
        for (let c = 0; c < boardSize; c++) {
          const cell = newBoard[r][c];
          if (cell && !cell.isGap && !cell.obstacle && !cell.isIngredient && !cell.special) {
            validCells.push({ row: r, col: c });
          }
        }
      }
      if (validCells.length > 0) {
        const randomCell = validCells[Math.floor(Math.random() * validCells.length)];
        const specialTypes = [SPECIAL_TYPES.STRIPED_H, SPECIAL_TYPES.STRIPED_V, SPECIAL_TYPES.WRAPPED];
        const randomSpecial = specialTypes[Math.floor(Math.random() * specialTypes.length)];
        newBoard[randomCell.row][randomCell.col] = {
          ...newBoard[randomCell.row][randomCell.col],
          special: randomSpecial
        };
      }
    }
    
    setBoard(newBoard);
    setSelectedCell(null);
    setScore(0);
    // Apply startMoves upgrade (+2 moves at level start)
    const bonusMoves = activeUpgrades.startMoves || 0;
    setMovesLeft(config.moves + bonusMoves);
    setFruitsCollected(0);
    setDualFruitsCollected({ fruit1: 0, fruit2: 0 });
    setGameStatus('playing');
    setIsAnimating(false);
    setLastMatchInfo(null);
    setExplodingCells(new Set());
    setClearEffect(null);
    setConsecutive4Combos(0);
    setDoublePointsActive(false);
    setShowDoublePointsAlert(false);
    setInstaEliminationActive(false);
    setShowInstaEliminationPicker(false);
    setSwappingCells(null);
    setLastEliminatedFruits(null);
    setLastDiscoCombo(false);
    setShowShuffleAlert(false);
    setSwordAbilityUsed(false);
    setSwordMode(false);
    setDoubleXPActive(false);
    setDoubleXPTurnsLeft(0);
    // Reset mission progress for levels 51+
    setMissionProgress({
      stripedCreated: 0,
      wrappedCreated: 0,
      discoCreated: 0,
      iceBroken: 0,
      chainsBroken: 0,
      chocolateCleared: 0,
      ingredientsDropped: 0,
      specialCombos: 0,
    });
  }, [config.targetFruit, config.targetCount, config.moves, boardSize, fruitSet, levelId, activeUpgrades.startMoves]);

  // High score stored directly, no ref needed
  const updateHighScoreIfNeeded = useCallback((newScore) => {
    const currentHighScore = parseInt(localStorage.getItem('fruitCrush_highScore') || '0', 10);
    if (newScore > currentHighScore) {
      setHighScore(newScore);
      localStorage.setItem('fruitCrush_highScore', newScore.toString());
    }
  }, []);

  // Derive game status from state based on mission type
  const computedGameStatus = useMemo(() => {
    if (gameStatus !== 'playing') return gameStatus;
    
    // Check win condition based on mission type
    let missionComplete = false;
    switch (missionType) {
      case 'break_ice':
        missionComplete = missionProgress.iceBroken >= config.targetCount;
        break;
      case 'break_chains':
        missionComplete = missionProgress.chainsBroken >= config.targetCount;
        break;
      case 'clear_chocolate':
        missionComplete = missionProgress.chocolateCleared >= config.targetCount;
        break;
      case 'drop_ingredient':
        missionComplete = missionProgress.ingredientsDropped >= config.targetCount;
        break;
      case 'create_striped':
        missionComplete = missionProgress.stripedCreated >= config.targetCount;
        break;
      case 'create_wrapped':
        missionComplete = missionProgress.wrappedCreated >= config.targetCount;
        break;
      case 'create_disco':
        missionComplete = missionProgress.discoCreated >= config.targetCount;
        break;
      case 'special_combo':
      case 'combo_mission':
        missionComplete = missionProgress.specialCombos >= config.targetCount;
        break;
      case 'reach_score':
        missionComplete = score >= config.targetCount;
        break;
      case 'boss_fight':
        // Boss fight win is handled externally - never auto-win based on fruits
        missionComplete = false;
        break;
      case 'dual_collect':
        // Both objectives must be completed
        if (config.dualMission) {
          const obj1Complete = dualFruitsCollected.fruit1 >= config.dualMission.objective1.count;
          const obj2Complete = dualFruitsCollected.fruit2 >= config.dualMission.objective2.count;
          missionComplete = obj1Complete && obj2Complete;
        }
        break;
      case 'collect_fruit':
      default:
        missionComplete = fruitsCollected >= config.targetCount;
        break;
    }
    
    if (missionComplete) return 'won';
    if (movesLeft <= 0 && !missionComplete) return 'lost';
    return 'playing';
  }, [fruitsCollected, dualFruitsCollected, movesLeft, gameStatus, config.targetCount, config.dualMission, missionType, missionProgress, score]);

  // Auto-update gameStatus when win/lose conditions are met
  useEffect(() => {
    // Check win condition based on mission type
    let missionComplete = false;
    let currentProgress = 0;
    
    switch (missionType) {
      case 'break_ice':
        currentProgress = missionProgress.iceBroken;
        missionComplete = currentProgress >= config.targetCount;
        break;
      case 'break_chains':
        currentProgress = missionProgress.chainsBroken;
        missionComplete = currentProgress >= config.targetCount;
        break;
      case 'clear_chocolate':
        currentProgress = missionProgress.chocolateCleared;
        missionComplete = currentProgress >= config.targetCount;
        break;
      case 'drop_ingredient':
        currentProgress = missionProgress.ingredientsDropped;
        missionComplete = currentProgress >= config.targetCount;
        break;
      case 'create_striped':
        currentProgress = missionProgress.stripedCreated;
        missionComplete = currentProgress >= config.targetCount;
        break;
      case 'create_wrapped':
        currentProgress = missionProgress.wrappedCreated;
        missionComplete = currentProgress >= config.targetCount;
        break;
      case 'create_disco':
        currentProgress = missionProgress.discoCreated;
        missionComplete = currentProgress >= config.targetCount;
        break;
      case 'special_combo':
      case 'combo_mission':
        currentProgress = missionProgress.specialCombos;
        missionComplete = currentProgress >= config.targetCount;
        break;
      case 'boss_fight':
        // Boss fight win is handled externally by bossFight.isDefeated
        // Don't auto-win here - just track progress for display
        currentProgress = 0; // Will be updated by boss fight component
        missionComplete = false; // Boss defeat is checked in GamePage
        break;
      case 'reach_score':
        currentProgress = score;
        missionComplete = currentProgress >= config.targetCount;
        break;
      case 'collect_fruit':
      default:
        currentProgress = fruitsCollected;
        missionComplete = currentProgress >= config.targetCount;
        break;
    }
    
    
    if (gameStatus === 'playing') {
      if (missionComplete) {
        setGameStatus('won');
      } else if (movesLeft <= 0 && !missionComplete) {
        // Check for Emergency Shield upgrade (gives 1 extra move once per day)
        const hasEmergencyShield = activeUpgrades.emergencyShield || false;
        if (hasEmergencyShield && !emergencyShieldUsedToday) {
          setMovesLeft(1);
          setEmergencyShieldUsedToday(true);
          localStorage.setItem('fruitCrush_emergencyShieldUsed', new Date().toISOString());
        } else {
          setGameStatus('lost');
        }
      }
    }
  }, [fruitsCollected, movesLeft, config.targetCount, gameStatus, missionType, missionProgress, score, activeUpgrades.emergencyShield, emergencyShieldUsedToday]);

  // Initialize sword uses based on upgrades when config changes
  useEffect(() => {
    setSwordUsesRemaining(calculatedMaxSwordUses);
    setMaxSwordUses(calculatedMaxSwordUses);
  }, [calculatedMaxSwordUses]);

  // Determine if target fruit should be boosted based on level
  const boostTarget = levelId >= 11;

  // Process cascading matches with explosion animation
  const processCascade = useCallback(async (currentBoard, initialSwappedCell = null, targetFruitParam = 'banana', currentFruitSet = FRUIT_TYPES) => {
    let boardToProcess = currentBoard.map(row => row.map(cell => cell ? { ...cell } : null));
    const currentBoardSize = boardToProcess.length;
    let totalTargetFruits = 0;
    let totalScore = 0;
    let cascade = 0;
    let swappedCell = initialSwappedCell;
    let had4Match = false; // Track if this turn had a 4-fruit match
    let had6Match = false; // Track if this turn had a 6-fruit match
    let hadDiscoCombo = false; // Track if disco combo was used
    let totalStripedCreated = 0; // Track striped fruits created
    let totalWrappedCreated = 0; // Track wrapped fruits created
    
    // Track obstacle breaks for missions
    let totalIceBroken = 0;
    let totalChainsBroken = 0;
    let totalChocolateCleared = 0;
    let totalIngredientsDropped = 0; // Track ingredients dropped to bottom
    
    // Track eliminated fruits by type for challenges
    const eliminatedByType = {};
    
    // Track max match size for "Fruity Crush!" effect
    let maxMatchSize = 0;

    while (true) {
      const { matches, matchGroups, tlShapes } = findMatches(boardToProcess);
      if (matches.size === 0) break;

      cascade++;
      
      // Check for 4-fruit and 6-fruit matches and disco
      matchGroups.forEach(group => {
        // Track max match size
        if (group.length > maxMatchSize) {
          maxMatchSize = group.length;
        }
        
        if (group.length === 4) {
          had4Match = true;
        }
        if (group.length >= 5) {
          hadDiscoCombo = true;
        }
        if (group.length >= 6) {
          had6Match = true;
        }
      });
      
      // Count eliminated fruits by type
      matches.forEach((pos) => {
        const cell = boardToProcess[pos.row][pos.col];
        if (cell && cell.type) {
          eliminatedByType[cell.type] = (eliminatedByType[cell.type] || 0) + 1;
        }
      });
      
      // Step 1: Show explosion animation on matched cells
      const matchedPositions = new Set();
      matches.forEach((pos) => {
        matchedPositions.add(`${pos.row}-${pos.col}`);
      });
      
      // Check for striped fruit effects and wrapped fruit effects
      let stripedEffect = null;
      matches.forEach((pos) => {
        const cell = boardToProcess[pos.row][pos.col];
        if (cell?.special === SPECIAL_TYPES.STRIPED_H) {
          stripedEffect = { type: 'row', index: pos.row };
          // Add entire row to matched positions
          for (let c = 0; c < currentBoardSize; c++) {
            matchedPositions.add(`${pos.row}-${c}`);
          }
        } else if (cell?.special === SPECIAL_TYPES.STRIPED_V) {
          stripedEffect = { type: 'column', index: pos.col };
          // Add entire column to matched positions
          for (let r = 0; r < currentBoardSize; r++) {
            matchedPositions.add(`${r}-${pos.col}`);
          }
        } else if (cell?.special === SPECIAL_TYPES.WRAPPED) {
          // Wrapped fruit explodes in 3x3 area
          for (let r = pos.row - 1; r <= pos.row + 1; r++) {
            for (let c = pos.col - 1; c <= pos.col + 1; c++) {
              if (r >= 0 && r < currentBoardSize && c >= 0 && c < currentBoardSize) {
                matchedPositions.add(`${r}-${c}`);
              }
            }
          }
        }
      });
      
      // Set exploding cells for animation
      setExplodingCells(matchedPositions);
      if (stripedEffect) {
        setClearEffect(stripedEffect);
      }
      
      // Wait for explosion animation (400ms)
      await new Promise(resolve => setTimeout(resolve, 400));
      
      // Clear explosion state
      setExplodingCells(new Set());
      setClearEffect(null);
      
      // Step 2: Process the matches (remove cells and drop)
      const { newBoard, targetFruitCount, scoreMultiplier, cellsRemoved, stripedCreated, wrappedCreated, iceBroken, chainsBroken, chocolateCleared, ingredientsDropped } = processMatchesWithTarget(
        boardToProcess, 
        matches, 
        matchGroups,
        swappedCell,
        targetFruitParam,
        currentFruitSet,
        boostTarget,
        tlShapes,
        levelId
      );
      totalTargetFruits += targetFruitCount;
      
      // Base score calculation
      let cascadeScore = Math.floor(cellsRemoved * 10 * cascade * scoreMultiplier);
      
      // Apply combo bonus for levels with comboBonus flag (2x score for cascades)
      if (levelConfig?.comboBonus && cascade > 1) {
        cascadeScore = Math.floor(cascadeScore * 2);
      }
      
      // Apply cascade bonus for 4+ chain reactions (from Cascade Master upgrade)
      const cascadeBonus = activeUpgrades?.cascadeBonus || 0;
      if (cascade >= 4 && cascadeBonus > 0) {
        cascadeScore = Math.floor(cascadeScore * (1 + cascadeBonus));
      }
      
      // Apply fruit mastery bonus if callback is provided
      let masteryBonus = 0;
      if (calculateMasteryBonus && Object.keys(eliminatedByType).length > 0) {
        // Get matched fruits from this cascade iteration
        const matchedFruits = [];
        let avgRow = 0, avgCol = 0, matchCount = 0;
        matches.forEach((pos) => {
          const cell = boardToProcess[pos.row]?.[pos.col];
          if (cell && cell.type) {
            matchedFruits.push(cell.type);
            avgRow += pos.row;
            avgCol += pos.col;
            matchCount++;
          }
        });
        if (matchedFruits.length > 0) {
          masteryBonus = calculateMasteryBonus(matchedFruits);
          if (masteryBonus > 0) {
            cascadeScore += masteryBonus;
            // Show mastery bonus popup at average position of matched fruits
            if (matchCount > 0) {
              avgRow = Math.floor(avgRow / matchCount);
              avgCol = Math.floor(avgCol / matchCount);
              setScorePopups(prev => [...prev, {
                id: `mastery-${Date.now()}-${cascade}`,
                value: masteryBonus,
                x: avgCol,
                y: avgRow,
                type: 'mastery'
              }]);
            }
          }
        }
      }
      
      totalScore += cascadeScore;
      totalStripedCreated += stripedCreated || 0;
      totalWrappedCreated += wrappedCreated || 0;
      totalIceBroken += iceBroken || 0;
      totalChainsBroken += chainsBroken || 0;
      totalChocolateCleared += chocolateCleared || 0;
      totalIngredientsDropped += ingredientsDropped || 0;
      boardToProcess = newBoard;
      swappedCell = null; // Only use swapped cell for first iteration

      // Step 3: Update board and wait for drop animation
      setBoard([...boardToProcess.map(row => [...row])]);
      
      setLastMatchInfo({ cascade, targetFruits: targetFruitCount, score: cellsRemoved * 10 * cascade });
      
      // Wait for drop animation (300ms)
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    return { 
      finalBoard: boardToProcess, 
      totalTargetFruits, 
      totalScore, 
      had4Match, 
      had6Match, 
      hadDiscoCombo,
      eliminatedByType,
      totalStripedCreated,
      totalWrappedCreated,
      totalIceBroken,
      totalChainsBroken,
      totalChocolateCleared,
      totalIngredientsDropped,
      maxMatchSize,
      cascadeCount: cascade
    };
  }, [calculateMasteryBonus, setScorePopups]);

  const handleCellClick = useCallback(async (row, col) => {
    if (computedGameStatus !== 'playing' || isAnimating) return;

    const clickedCell = { row, col };
    const clickedCellData = board[row][col];
    
    // Ingredients cannot be selected or swapped - they are solid!
    if (clickedCellData?.isIngredient) {
      return; // Ignore clicks on ingredients
    }

    if (!selectedCell) {
      // First selection
      setSelectedCell(clickedCell);
    } else if (selectedCell.row === row && selectedCell.col === col) {
      // Deselect
      setSelectedCell(null);
    } else if (areAdjacent(selectedCell, clickedCell)) {
      // Check if either cell is a disco fruit
      const selectedCellData = board[selectedCell.row][selectedCell.col];
      const isDiscoSwap = selectedCellData?.special === SPECIAL_TYPES.DISCO || 
                          clickedCellData?.special === SPECIAL_TYPES.DISCO;

      // Check for special fruit combos
      const isStripedCombo = (selectedCellData?.special === SPECIAL_TYPES.STRIPED_H || selectedCellData?.special === SPECIAL_TYPES.STRIPED_V) &&
                             (clickedCellData?.special === SPECIAL_TYPES.STRIPED_H || clickedCellData?.special === SPECIAL_TYPES.STRIPED_V);
      
      const isStripedWrappedCombo = ((selectedCellData?.special === SPECIAL_TYPES.STRIPED_H || selectedCellData?.special === SPECIAL_TYPES.STRIPED_V) &&
                                     clickedCellData?.special === SPECIAL_TYPES.WRAPPED) ||
                                    ((clickedCellData?.special === SPECIAL_TYPES.STRIPED_H || clickedCellData?.special === SPECIAL_TYPES.STRIPED_V) &&
                                     selectedCellData?.special === SPECIAL_TYPES.WRAPPED);
      
      // NEW: Wrapped + Wrapped combo = 4x4 explosion
      const isWrappedWrappedCombo = selectedCellData?.special === SPECIAL_TYPES.WRAPPED &&
                                    clickedCellData?.special === SPECIAL_TYPES.WRAPPED;
      
      // NEW: Disco + Striped combo = All fruits of that type become striped
      const isDiscoStripedCombo = (selectedCellData?.special === SPECIAL_TYPES.DISCO &&
                                   (clickedCellData?.special === SPECIAL_TYPES.STRIPED_H || clickedCellData?.special === SPECIAL_TYPES.STRIPED_V)) ||
                                  ((selectedCellData?.special === SPECIAL_TYPES.STRIPED_H || selectedCellData?.special === SPECIAL_TYPES.STRIPED_V) &&
                                   clickedCellData?.special === SPECIAL_TYPES.DISCO);
      
      const isSpecialCombo = isStripedCombo || isStripedWrappedCombo || isWrappedWrappedCombo || isDiscoStripedCombo;

      // Try to swap
      if (isDiscoSwap || isSpecialCombo || wouldCreateMatch(board, selectedCell.row, selectedCell.col, row, col)) {
        setIsAnimating(true);
        
        try {
        // Perform swap
        let newBoard = board.map(r => r.map(cell => cell ? { ...cell } : null));
        [newBoard[selectedCell.row][selectedCell.col], newBoard[row][col]] = 
        [newBoard[row][col], newBoard[selectedCell.row][selectedCell.col]];
        
        setBoard(newBoard);
        setSelectedCell(null);
        setMovesLeft(prev => prev - 1);
        
        // Decrement 2X XP turns if active
        if (doubleXPActive && doubleXPTurnsLeft > 0) {
          setDoubleXPTurnsLeft(prev => {
            const newTurns = prev - 1;
            if (newTurns <= 0) {
              setDoubleXPActive(false);
            }
            return newTurns;
          });
        }

        // Handle special fruit combos (Striped + Striped, Striped + Wrapped) for click-swap
        if (isSpecialCombo) {
          await new Promise(resolve => setTimeout(resolve, 150));
          
          const fromRow = selectedCell.row;
          const fromCol = selectedCell.col;
          const toRow = row;
          const toCol = col;
          
          let cellsToExplode = new Set();
          let comboPoints = 0;
          let targetFruitsCollected = 0;
          
          if (isStripedCombo) {
            // Striped + Striped = Clear entire row AND column (cross pattern)
            setSpecialComboAnimation({ type: 'cross', row: fromRow, col: fromCol, phase: 'start' });
            setClearEffect({ type: 'cross', row: fromRow, col: fromCol });
            setLastSpecialComboTriggered(prev => !prev); // Toggle to trigger effect
            
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // Clear both rows (but protect ingredients!)
            for (let c = 0; c < boardSize; c++) {
              if (!newBoard[fromRow][c]?.isGap && !newBoard[fromRow][c]?.isIngredient && newBoard[fromRow][c]) {
                cellsToExplode.add(`${fromRow}-${c}`);
                if (newBoard[fromRow][c]?.type === config.targetFruit) targetFruitsCollected++;
                comboPoints += 15;
              }
              if (!newBoard[toRow][c]?.isGap && !newBoard[toRow][c]?.isIngredient && newBoard[toRow][c]) {
                cellsToExplode.add(`${toRow}-${c}`);
                if (newBoard[toRow][c]?.type === config.targetFruit) targetFruitsCollected++;
                comboPoints += 15;
              }
            }
            // Clear both columns (but protect ingredients!)
            for (let r = 0; r < boardSize; r++) {
              if (!newBoard[r][fromCol]?.isGap && !newBoard[r][fromCol]?.isIngredient && newBoard[r][fromCol]) {
                cellsToExplode.add(`${r}-${fromCol}`);
                if (newBoard[r][fromCol]?.type === config.targetFruit) targetFruitsCollected++;
                comboPoints += 15;
              }
              if (!newBoard[r][toCol]?.isGap && !newBoard[r][toCol]?.isIngredient && newBoard[r][toCol]) {
                cellsToExplode.add(`${r}-${toCol}`);
                if (newBoard[r][toCol]?.type === config.targetFruit) targetFruitsCollected++;
                comboPoints += 15;
              }
            }
            
          } else if (isStripedWrappedCombo) {
            // Striped + Wrapped = 3x3 expansion + clear 3 rows horizontally and 3 columns vertically
            const wrappedPos = selectedCellData?.special === SPECIAL_TYPES.WRAPPED 
              ? { row: toRow, col: toCol }
              : { row: fromRow, col: fromCol };
            
            setSpecialComboAnimation({ type: 'mega', row: wrappedPos.row, col: wrappedPos.col, phase: 'expand' });
            setLastSpecialComboTriggered(prev => !prev); // Toggle to trigger effect
            
            await new Promise(resolve => setTimeout(resolve, 400));
            
            setSpecialComboAnimation({ type: 'mega', row: wrappedPos.row, col: wrappedPos.col, phase: 'sweep' });
            setClearEffect({ type: 'mega', row: wrappedPos.row, col: wrappedPos.col });
            
            // Clear 3 rows
            for (let rOffset = -1; rOffset <= 1; rOffset++) {
              const r = wrappedPos.row + rOffset;
              if (r >= 0 && r < boardSize) {
                for (let c = 0; c < boardSize; c++) {
                  if (!newBoard[r][c]?.isGap && !newBoard[r][c]?.isIngredient && newBoard[r][c]) {
                    cellsToExplode.add(`${r}-${c}`);
                    if (newBoard[r][c]?.type === config.targetFruit) targetFruitsCollected++;
                    comboPoints += 20;
                  }
                }
              }
            }
            
            // Clear 3 columns
            for (let cOffset = -1; cOffset <= 1; cOffset++) {
              const c = wrappedPos.col + cOffset;
              if (c >= 0 && c < boardSize) {
                for (let r = 0; r < boardSize; r++) {
                  if (!newBoard[r][c]?.isGap && !newBoard[r][c]?.isIngredient && newBoard[r][c]) {
                    cellsToExplode.add(`${r}-${c}`);
                    if (newBoard[r][c]?.type === config.targetFruit) targetFruitsCollected++;
                    comboPoints += 20;
                  }
                }
              }
            }
          } else if (isWrappedWrappedCombo) {
            // NEW: Wrapped + Wrapped = 4x4 explosion centered between the two wrapped fruits
            const centerRow = Math.floor((fromRow + toRow) / 2);
            const centerCol = Math.floor((fromCol + toCol) / 2);
            
            setSpecialComboAnimation({ type: 'mega_explosion', row: centerRow, col: centerCol, phase: 'start' });
            setClearEffect({ type: 'mega_explosion', row: centerRow, col: centerCol });
            setLastSpecialComboTriggered(prev => !prev);
            
            await new Promise(resolve => setTimeout(resolve, 200));
            
            // First explosion wave (inner 2x2)
            setSpecialComboAnimation({ type: 'mega_explosion', row: centerRow, col: centerCol, phase: 'wave1' });
            await new Promise(resolve => setTimeout(resolve, 150));
            
            // Second explosion wave (4x4 area)
            setSpecialComboAnimation({ type: 'mega_explosion', row: centerRow, col: centerCol, phase: 'wave2' });
            
            // Clear 4x4 area around both wrapped fruits
            for (let rOffset = -2; rOffset <= 2; rOffset++) {
              for (let cOffset = -2; cOffset <= 2; cOffset++) {
                // Use the position of one of the wrapped fruits as center
                const r = fromRow + rOffset;
                const c = fromCol + cOffset;
                if (r >= 0 && r < boardSize && c >= 0 && c < boardSize) {
                  if (!newBoard[r][c]?.isGap && !newBoard[r][c]?.isIngredient && newBoard[r][c]) {
                    cellsToExplode.add(`${r}-${c}`);
                    if (newBoard[r][c]?.type === config.targetFruit) targetFruitsCollected++;
                    comboPoints += 25;
                  }
                }
              }
            }
            // Also include area around second wrapped fruit
            for (let rOffset = -2; rOffset <= 2; rOffset++) {
              for (let cOffset = -2; cOffset <= 2; cOffset++) {
                const r = toRow + rOffset;
                const c = toCol + cOffset;
                if (r >= 0 && r < boardSize && c >= 0 && c < boardSize) {
                  if (!newBoard[r][c]?.isGap && !newBoard[r][c]?.isIngredient && newBoard[r][c]) {
                    cellsToExplode.add(`${r}-${c}`);
                    if (newBoard[r][c]?.type === config.targetFruit) targetFruitsCollected++;
                    comboPoints += 25;
                  }
                }
              }
            }
          } else if (isDiscoStripedCombo) {
            // NEW: Disco + Striped = All fruits of the striped fruit's type become striped and activate
            let discoPos, stripedPos, stripedCell;
            if (selectedCellData?.special === SPECIAL_TYPES.DISCO) {
              discoPos = { row: toRow, col: toCol }; // Disco moved here after swap
              stripedPos = { row: fromRow, col: fromCol }; // Striped moved here
              stripedCell = clickedCellData; // Original striped cell data
            } else {
              discoPos = { row: fromRow, col: fromCol }; // Disco moved here
              stripedPos = { row: toRow, col: toCol }; // Striped moved here
              stripedCell = selectedCellData; // Original striped cell data
            }
            
            const targetFruitType = stripedCell.type;
            const isHorizontal = stripedCell.special === SPECIAL_TYPES.STRIPED_H;
            
            // Phase 1: Rainbow wave animation
            setSpecialComboAnimation({ type: 'disco_striped', phase: 'rainbow', targetType: targetFruitType });
            setClearEffect({ type: 'disco_striped', targetType: targetFruitType });
            setLastSpecialComboTriggered(prev => !prev);
            
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // Find all fruits of the target type and mark them as striped
            const stripedPositions = [];
            for (let r = 0; r < boardSize; r++) {
              for (let c = 0; c < boardSize; c++) {
                if (newBoard[r][c]?.type === targetFruitType && !newBoard[r][c]?.isGap) {
                  stripedPositions.push({ row: r, col: c });
                }
              }
            }
            
            // Phase 2: Transform all target fruits to striped (alternating H/V for visual variety)
            setSpecialComboAnimation({ type: 'disco_striped', phase: 'transform', positions: stripedPositions });
            
            await new Promise(resolve => setTimeout(resolve, 400));
            
            // Phase 3: Activate all striped fruits sequentially with staggered timing
            setSpecialComboAnimation({ type: 'disco_striped', phase: 'activate' });
            
            // Clear all rows and columns where the striped fruits were
            for (const pos of stripedPositions) {
              // Clear row
              for (let c = 0; c < boardSize; c++) {
                if (!newBoard[pos.row][c]?.isGap && !newBoard[pos.row][c]?.isIngredient && newBoard[pos.row][c]) {
                  cellsToExplode.add(`${pos.row}-${c}`);
                  if (newBoard[pos.row][c]?.type === config.targetFruit) targetFruitsCollected++;
                  comboPoints += 15;
                }
              }
              // Clear column
              for (let r = 0; r < boardSize; r++) {
                if (!newBoard[r][pos.col]?.isGap && !newBoard[r][pos.col]?.isIngredient && newBoard[r][pos.col]) {
                  cellsToExplode.add(`${r}-${pos.col}`);
                  if (newBoard[r][pos.col]?.type === config.targetFruit) targetFruitsCollected++;
                  comboPoints += 15;
                }
              }
            }
            
            // Also clear the disco fruit position
            cellsToExplode.add(`${discoPos.row}-${discoPos.col}`);
            comboPoints += 50; // Bonus for disco combo
          }
          
          setExplodingCells(cellsToExplode);
          trackDualFruits(cellsToExplode, newBoard);
          await new Promise(resolve => setTimeout(resolve, 300));
          
          cellsToExplode.forEach(key => {
            const [r, c] = key.split('-').map(Number);
            if (newBoard[r] && newBoard[r][c] && !newBoard[r][c]?.isGap) {
              newBoard[r][c] = null;
            }
          });
          
          for (let c = 0; c < boardSize; c++) {
            dropAndFillColumn(newBoard, c, fruitSet, config.targetFruit, boostTarget, levelId);
          }
          
          setBoard([...newBoard.map(r => [...r])]);
          setFruitsCollected(prev => prev + targetFruitsCollected);
          setScore(prev => {
            const newScore = prev + comboPoints;
            updateHighScoreIfNeeded(newScore);
            return newScore;
          });
          
          setExplodingCells(new Set());
          setClearEffect(null);
          setSpecialComboAnimation(null);
          
          await new Promise(resolve => setTimeout(resolve, 300));
          
          const { finalBoard, totalTargetFruits, totalScore: cascadeScore, eliminatedByType, totalStripedCreated, totalWrappedCreated, totalIceBroken, totalChainsBroken, totalChocolateCleared, totalIngredientsDropped } = 
            await processCascade(newBoard, null, config.targetFruit, fruitSet);
          
          if (eliminatedByType && Object.keys(eliminatedByType).length > 0) {
            setLastEliminatedFruits(eliminatedByType);
            trackDualFruitsFromEliminated(eliminatedByType);
          }
          
          // Update mission progress for this cascade
          if (totalIceBroken > 0 || totalChainsBroken > 0 || totalChocolateCleared > 0 || totalStripedCreated > 0 || totalWrappedCreated > 0 || totalIngredientsDropped > 0) {
            setMissionProgress(prev => ({
              ...prev,
              iceBroken: prev.iceBroken + (totalIceBroken || 0),
              chainsBroken: prev.chainsBroken + (totalChainsBroken || 0),
              chocolateCleared: prev.chocolateCleared + (totalChocolateCleared || 0),
              stripedCreated: prev.stripedCreated + (totalStripedCreated || 0),
              wrappedCreated: prev.wrappedCreated + (totalWrappedCreated || 0),
              ingredientsDropped: prev.ingredientsDropped + (totalIngredientsDropped || 0),
            }));
          }
          
          let boardToSet = finalBoard;
          if (!hasValidMoves(boardToSet)) {
            setShowShuffleAlert(true);
            setTimeout(() => setShowShuffleAlert(false), 2000);
            boardToSet = shuffleBoard(boardToSet);
            while (!hasValidMoves(boardToSet)) {
              boardToSet = shuffleBoard(boardToSet);
            }
          }
          
          setBoard(boardToSet);
          return;
        }

        // Handle disco fruit swap - eliminate all of the target fruit type
        if (isDiscoSwap) {
          await new Promise(resolve => setTimeout(resolve, 150));
          
          // Determine which is disco and which is the target
          let discoPos, targetPos;
          if (selectedCellData?.special === SPECIAL_TYPES.DISCO) {
            discoPos = { row: row, col: col }; // Disco moved to clicked position
            targetPos = { row: selectedCell.row, col: selectedCell.col }; // Target is where disco was
          } else {
            discoPos = { row: selectedCell.row, col: selectedCell.col }; // Disco moved to selected position  
            targetPos = { row: row, col: col }; // Target is where clicked
          }
          
          // Get the fruit type that we're swapping the disco with
          const swappedFruitType = newBoard[targetPos.row][targetPos.col]?.type;
          
          if (swappedFruitType) {
            let targetFruitsFromDisco = 0;
            let cellsCleared = 0;
            const currentBoardSize = newBoard.length;
            
            // Clear all fruits of the swapped type AND the disco fruit
            for (let r = 0; r < currentBoardSize; r++) {
              for (let c = 0; c < currentBoardSize; c++) {
                if (newBoard[r][c]?.type === swappedFruitType || 
                    (r === discoPos.row && c === discoPos.col)) {
                  if (newBoard[r][c]?.type === config.targetFruit) {
                    targetFruitsFromDisco++;
                  }
                  newBoard[r][c] = null;
                  cellsCleared++;
                }
              }
            }
            
            // Drop fruits and fill empty cells (respecting gaps)
            for (let c = 0; c < currentBoardSize; c++) {
              dropAndFillColumn(newBoard, c, fruitSet, config.targetFruit, boostTarget, levelId);
            }
            
            setBoard([...newBoard.map(r => [...r])]);
            setFruitsCollected(prev => prev + targetFruitsFromDisco);
            setScore(prev => {
              const newScore = prev + cellsCleared * 20;
              updateHighScoreIfNeeded(newScore);
              return newScore;
            });
            
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }

        // Process any regular matches and cascades
        await new Promise(resolve => setTimeout(resolve, 150));
        
        // Check if double points is active for this turn
        const pointsMultiplier = doublePointsActive ? 2 : 1;
        if (doublePointsActive) {
          setDoublePointsActive(false); // Use up the double points
        }
        
        const { finalBoard, totalTargetFruits, totalScore, had4Match, had6Match, hadDiscoCombo, eliminatedByType, totalStripedCreated, totalWrappedCreated, totalIceBroken, totalChainsBroken, totalChocolateCleared, totalIngredientsDropped, maxMatchSize, cascadeCount: thisCascade } = await processCascade(
          newBoard, 
          clickedCell,
          config.targetFruit,
          fruitSet
        );
        
        // === TRIGGER ENHANCED ANIMATIONS ===
        // Update match size for effects
        if (maxMatchSize > 0) {
          setLastMatchSize(maxMatchSize);
        }
        
        // === COMBO NAME SYSTEM ===
        // Determine which combo name to show based on match size and cascade
        let selectedCombo = null;
        
        // Priority: Special combo > Cascade > Match size
        if (hadDiscoCombo) {
          selectedCombo = COMBO_NAMES.disco;
        } else if (totalStripedCreated > 0 && totalWrappedCreated > 0) {
          selectedCombo = COMBO_NAMES.special_combo;
        } else if (totalWrappedCreated > 0) {
          selectedCombo = COMBO_NAMES.wrapped;
        } else if (totalStripedCreated > 0) {
          selectedCombo = COMBO_NAMES.striped;
        } else if (thisCascade >= 5) {
          selectedCombo = COMBO_NAMES.cascade5;
        } else if (thisCascade === 4) {
          selectedCombo = COMBO_NAMES.cascade4;
        } else if (thisCascade === 3) {
          selectedCombo = COMBO_NAMES.cascade3;
        } else if (thisCascade === 2) {
          selectedCombo = COMBO_NAMES.cascade2;
        } else if (maxMatchSize >= 7) {
          selectedCombo = COMBO_NAMES[7];
        } else if (maxMatchSize >= 6) {
          selectedCombo = COMBO_NAMES[6];
        } else if (maxMatchSize >= 5) {
          selectedCombo = COMBO_NAMES[5];
        } else if (maxMatchSize >= 4) {
          selectedCombo = COMBO_NAMES[4];
        }
        
        // Show combo name if one was selected
        if (selectedCombo) {
          setComboName(selectedCombo);
          setShowComboName(true);
          setTimeout(() => {
            setShowComboName(false);
            setComboName(null);
          }, 1500);
        }
        
        // 5+ match: Trigger "Fruity Crush!" text and screen shake
        if (maxMatchSize >= 5) {
          setShowFruityCrush(true);
          setScreenShake(true);
          setTimeout(() => {
            setShowFruityCrush(false);
            setScreenShake(false);
          }, 1500);
        }
        
        // Update cascade count for combo multiplier display
        if (thisCascade > 1) {
          setCascadeCount(thisCascade);
          setShowComboMultiplier(true);
          setTimeout(() => setShowComboMultiplier(false), 1000);
        }
        
        // Track eliminated fruits for challenges
        if (eliminatedByType && Object.keys(eliminatedByType).length > 0) {
          setLastEliminatedFruits(eliminatedByType);
          // Track dual mission fruits
          trackDualFruitsFromEliminated(eliminatedByType);
        }
        
        // Track special fruits created for challenges
        if (totalStripedCreated > 0) {
          setLastStripedCreated(prev => prev + totalStripedCreated);
        }
        if (totalWrappedCreated > 0) {
          setLastWrappedCreated(prev => prev + totalWrappedCreated);
        }
        
        // Update mission progress for obstacle levels
        if (totalIceBroken > 0 || totalChainsBroken > 0 || totalChocolateCleared > 0 || totalStripedCreated > 0 || totalWrappedCreated > 0 || totalIngredientsDropped > 0) {
          setMissionProgress(prev => {
            const newProgress = {
              ...prev,
              iceBroken: prev.iceBroken + (totalIceBroken || 0),
              chainsBroken: prev.chainsBroken + (totalChainsBroken || 0),
              chocolateCleared: prev.chocolateCleared + (totalChocolateCleared || 0),
              stripedCreated: prev.stripedCreated + (totalStripedCreated || 0),
              wrappedCreated: prev.wrappedCreated + (totalWrappedCreated || 0),
              ingredientsDropped: prev.ingredientsDropped + (totalIngredientsDropped || 0),
            };
            return newProgress;
          });
        }
        
        // Track disco combos for challenges
        if (hadDiscoCombo) {
          setLastDiscoCombo(true);
          setTimeout(() => setLastDiscoCombo(false), 100);
          setMissionProgress(prev => ({
            ...prev,
            discoCreated: prev.discoCreated + 1,
          }));
        }
        
        // Apply points multiplier
        const finalScore = totalScore * pointsMultiplier;
        
        // Check if board has valid moves, shuffle if not
        let boardToSet = finalBoard;
        if (!hasValidMoves(finalBoard)) {
          boardToSet = shuffleBoard(finalBoard);
          setShowShuffleAlert(true);
          setTimeout(() => setShowShuffleAlert(false), 2000);
        }
        
        setBoard(boardToSet);
        setFruitsCollected(prev => prev + totalTargetFruits);
        setScore(prev => {
          const newScore = prev + finalScore;
          updateHighScoreIfNeeded(newScore);
          return newScore;
        });
        
        // Handle combo tracking
        if (had6Match) {
          // 6-fruit match: Activate insta-elimination
          setInstaEliminationActive(true);
          setShowInstaEliminationPicker(true);
          // Reset 4-combo counter
          setConsecutive4Combos(0);
        } else if (had4Match) {
          // 4-fruit match: Increment counter
          const newCount = consecutive4Combos + 1;
          setConsecutive4Combos(newCount);
          
          if (newCount >= 2) {
            // Two consecutive 4-matches: Activate double points for next turn
            setDoublePointsActive(true);
            setShowDoublePointsAlert(true);
            setConsecutive4Combos(0); // Reset counter
            
            // Auto-hide alert after 2 seconds
            setTimeout(() => setShowDoublePointsAlert(false), 2500);
          }
        } else {
          // No 4+ match: Reset counter
          setConsecutive4Combos(0);
        }
        
        setLastMatchInfo(null);
        } catch (error) {
          console.error('[handleCellClick] Error during swap:', error);
        } finally {
          // Always reset animation state to prevent freezes
          setIsAnimating(false);
        }
      } else {
        // Invalid swap
        setSelectedCell(null);
      }
    } else {
      // Select new cell
      setSelectedCell(clickedCell);
    }
  }, [selectedCell, board, computedGameStatus, isAnimating, processCascade, updateHighScoreIfNeeded, config.targetFruit, doublePointsActive, consecutive4Combos]);

  // ===== WORLD MECHANICS PROCESSING =====
  const processWorldMechanics = useCallback(() => {
    if (!worldMechanic) return;
    
    // === VOLCANIC CORE: Lava Rise ===
    if (worldMechanic === 'lava_rise') {
      setMovesSinceLavaRise(prev => {
        const newCount = prev + 1;
        if (newCount >= lavaRiseRate) {
          // Lava rises one row!
          setLavaLevel(prevLava => {
            const newLavaLevel = Math.min(prevLava + 1, boardSize - 2);
            
            // Convert bottom row(s) to lava (destroy fruits)
            setBoard(prevBoard => {
              const newBoard = prevBoard.map(r => [...r]);
              const lavaRow = boardSize - 1 - prevLava;
              
              if (lavaRow >= 0) {
                for (let col = 0; col < boardSize; col++) {
                  if (newBoard[lavaRow][col] && !newBoard[lavaRow][col].isGap) {
                    // Mark cell as lava (destroyed)
                    newBoard[lavaRow][col] = {
                      ...newBoard[lavaRow][col],
                      obstacle: OBSTACLE_TYPES.LAVA_FLOW,
                      isLava: true,
                    };
                  }
                }
              }
              
              return newBoard;
            });
            
            // Show warning
            setComboName({ name: "🌋 LAVA RISES! 🌋", emoji: "🔥", color: "from-red-600 to-orange-500" });
            setShowComboName(true);
            setTimeout(() => setShowComboName(false), 1500);
            
            return newLavaLevel;
          });
          return 0; // Reset counter
        }
        return newCount;
      });
    }
    
    // === MOONLIGHT GARDEN: Day/Night Cycle ===
    if (worldMechanic === 'day_night') {
      setMovesSinceCycleChange(prev => {
        const newCount = prev + 1;
        if (newCount >= cycleLength) {
          // Toggle day/night!
          setIsDayTime(prevDay => !prevDay);
          setDayNightTransition(true);
          setTimeout(() => setDayNightTransition(false), 1000);
          
          // Show transition message
          setComboName(prevDay => ({ 
            name: prevDay ? "🌙 NIGHT FALLS! 🌙" : "☀️ DAWN BREAKS! ☀️", 
            emoji: prevDay ? "🌑" : "🌞", 
            color: prevDay ? "from-indigo-600 to-purple-700" : "from-yellow-400 to-orange-500" 
          }));
          setShowComboName(true);
          setTimeout(() => setShowComboName(false), 1500);
          
          return 0;
        }
        return newCount;
      });
    }
    
    // === COSMIC NEBULA: Gravity Shift ===
    if (worldMechanic === 'gravity_shift') {
      // Every 5 moves, gravity shifts to a random direction
      const shouldShift = Math.random() < 0.2; // 20% chance per move
      if (shouldShift) {
        const directions = ['down', 'up', 'left', 'right'];
        const currentIndex = directions.indexOf(gravityDirection);
        const newDirections = directions.filter((_, i) => i !== currentIndex);
        const newDirection = newDirections[Math.floor(Math.random() * newDirections.length)];
        
        setGravityDirection(newDirection);
        setGravityShiftPending(true);
        
        // Apply the gravity shift to the current board
        setBoard(prevBoard => {
          const newBoard = prevBoard.map(r => [...r]);
          const size = newBoard.length;
          
          if (newDirection === 'up') {
            // Gravity UP - all fruits shift up
            for (let col = 0; col < size; col++) {
              let emptyRow = 0;
              for (let row = 0; row < size; row++) {
                if (newBoard[row][col] !== null && !newBoard[row][col]?.isGap) {
                  if (row !== emptyRow) {
                    newBoard[emptyRow][col] = newBoard[row][col];
                    newBoard[row][col] = null;
                  }
                  emptyRow++;
                }
              }
              // Fill empty cells (new fruits come from bottom in reverse)
              for (let row = 0; row < size; row++) {
                if (newBoard[row][col] === null) {
                  const randomType = fruitSet[Math.floor(Math.random() * fruitSet.length)];
                  newBoard[row][col] = createCell(randomType);
                }
              }
            }
          } else if (newDirection === 'left') {
            // Gravity LEFT - all fruits shift left
            for (let row = 0; row < size; row++) {
              let emptyCol = 0;
              for (let col = 0; col < size; col++) {
                if (newBoard[row][col] !== null && !newBoard[row][col]?.isGap) {
                  if (col !== emptyCol) {
                    newBoard[row][emptyCol] = newBoard[row][col];
                    newBoard[row][col] = null;
                  }
                  emptyCol++;
                }
              }
              // Fill empty cells from left
              for (let col = 0; col < size; col++) {
                if (newBoard[row][col] === null) {
                  const randomType = fruitSet[Math.floor(Math.random() * fruitSet.length)];
                  newBoard[row][col] = createCell(randomType);
                }
              }
            }
          } else if (newDirection === 'right') {
            // Gravity RIGHT - all fruits shift right
            for (let row = 0; row < size; row++) {
              let emptyCol = size - 1;
              for (let col = size - 1; col >= 0; col--) {
                if (newBoard[row][col] !== null && !newBoard[row][col]?.isGap) {
                  if (col !== emptyCol) {
                    newBoard[row][emptyCol] = newBoard[row][col];
                    newBoard[row][col] = null;
                  }
                  emptyCol--;
                }
              }
              // Fill empty cells from right
              for (let col = size - 1; col >= 0; col--) {
                if (newBoard[row][col] === null) {
                  const randomType = fruitSet[Math.floor(Math.random() * fruitSet.length)];
                  newBoard[row][col] = createCell(randomType);
                }
              }
            }
          }
          // 'down' is default, no change needed
          
          return newBoard;
        });
        
        const directionEmojis = { down: '⬇️', up: '⬆️', left: '⬅️', right: '➡️' };
        setComboName({ 
          name: `GRAVITY SHIFT ${directionEmojis[newDirection]}`, 
          emoji: "🌌", 
          color: "from-purple-600 to-indigo-700" 
        });
        setShowComboName(true);
        setTimeout(() => {
          setShowComboName(false);
          setGravityShiftPending(false);
        }, 1500);
      }
    }
    
    // === STORM KINGDOM: Lightning Strikes ===
    if (worldMechanic === 'lightning_strikes') {
      const shouldStrike = Math.random() < 0.25; // 25% chance per move
      if (shouldStrike) {
        // Random lightning strike on 1-3 cells
        const strikeCount = Math.floor(Math.random() * 3) + 1;
        const strikeCells = [];
        
        for (let i = 0; i < strikeCount; i++) {
          const row = Math.floor(Math.random() * boardSize);
          const col = Math.floor(Math.random() * boardSize);
          strikeCells.push({ row, col });
        }
        
        setLightningCells(strikeCells);
        setShowLightning(true);
        
        setComboName({ name: "⚡ LIGHTNING! ⚡", emoji: "🌩️", color: "from-yellow-400 to-blue-500" });
        setShowComboName(true);
        
        // Clear struck cells after animation
        setTimeout(() => {
          setBoard(prevBoard => {
            const newBoard = prevBoard.map(r => [...r]);
            strikeCells.forEach(({ row, col }) => {
              if (newBoard[row]?.[col] && !newBoard[row][col].isGap) {
                // Lightning clears the cell - create new random fruit
                const randomFruit = fruitSet[Math.floor(Math.random() * fruitSet.length)];
                newBoard[row][col] = createCell(randomFruit);
              }
            });
            return newBoard;
          });
          
          setShowLightning(false);
          setLightningCells([]);
          setShowComboName(false);
        }, 800);
      }
    }
    
    // === CASINO ROYALE: Random Multipliers ===
    if (worldMechanic === 'random_multipliers') {
      const shouldSpin = Math.random() < 0.15; // 15% chance per move
      if (shouldSpin) {
        const multipliers = [1, 1, 2, 2, 2, 3, 3, 5];
        const newMultiplier = multipliers[Math.floor(Math.random() * multipliers.length)];
        setActiveMultiplier(newMultiplier);
        
        if (newMultiplier >= 3) {
          setShowJackpot(true);
          setTimeout(() => setShowJackpot(false), 2000);
        }
        
        if (newMultiplier > 1) {
          setComboName({ 
            name: `🎰 ${newMultiplier}X MULTIPLIER! 🎰`, 
            emoji: "💰", 
            color: newMultiplier >= 5 ? "from-yellow-400 to-amber-500" : "from-green-400 to-emerald-500" 
          });
          setShowComboName(true);
          setTimeout(() => setShowComboName(false), 1500);
        }
      }
    }
    
    // === ALCHEMIST'S LAB: Potion Mixing ===
    if (worldMechanic === 'potion_mixing') {
      // Collect potion colors based on matched fruits
      const shouldMix = Math.random() < 0.2; // 20% chance per move
      if (shouldMix) {
        const potionTypes = ['🔴', '🔵', '🟢', '🟡', '🟣'];
        const newPotion = potionTypes[Math.floor(Math.random() * potionTypes.length)];
        
        setPotionColors(prev => {
          const updated = [...prev, newPotion];
          
          // Check for color combinations (3 of same = bonus)
          const colorCounts = updated.reduce((acc, color) => {
            acc[color] = (acc[color] || 0) + 1;
            return acc;
          }, {});
          
          const hasTriple = Object.values(colorCounts).some(count => count >= 3);
          
          if (hasTriple) {
            // Give bonus and clear potions
            setPotionBonus(prev => prev + 500);
            setComboName({ 
              name: "⚗️ POTION BREWED! ⚗️", 
              emoji: "🧪", 
              color: "from-green-500 to-emerald-400" 
            });
            setShowComboName(true);
            setTimeout(() => setShowComboName(false), 1500);
            return []; // Clear potions after brewing
          }
          
          // Keep only last 5 potions
          return updated.slice(-5);
        });
      }
    }
    
    // === CIRCUS CARNIVAL: Spinning Wheels ===
    if (worldMechanic === 'spinning_wheels') {
      const shouldSpin = Math.random() < 0.15; // 15% chance per move
      if (shouldSpin) {
        // Select a random 3x3 section to rotate
        const centerRow = 1 + Math.floor(Math.random() * (boardSize - 2));
        const centerCol = 1 + Math.floor(Math.random() * (boardSize - 2));
        
        setShowSpinEffect(true);
        setSpinningCells([{ row: centerRow, col: centerCol }]);
        
        // Rotate the 3x3 section clockwise
        setBoard(prevBoard => {
          const newBoard = prevBoard.map(r => [...r]);
          const tempCells = [];
          
          // Collect 3x3 cells (excluding gaps)
          for (let r = centerRow - 1; r <= centerRow + 1; r++) {
            for (let c = centerCol - 1; c <= centerCol + 1; c++) {
              if (r >= 0 && r < boardSize && c >= 0 && c < boardSize) {
                if (newBoard[r][c] && !newBoard[r][c].isGap) {
                  tempCells.push({ row: r, col: c, cell: { ...newBoard[r][c] } });
                }
              }
            }
          }
          
          // Rotate clockwise mapping: (r,c) -> (c, 2-r) relative to center
          tempCells.forEach(({ row, col, cell }) => {
            const relR = row - (centerRow - 1);
            const relC = col - (centerCol - 1);
            const newRelR = relC;
            const newRelC = 2 - relR;
            const newRow = (centerRow - 1) + newRelR;
            const newCol = (centerCol - 1) + newRelC;
            
            if (newRow >= 0 && newRow < boardSize && newCol >= 0 && newCol < boardSize) {
              if (!newBoard[newRow][newCol]?.isGap) {
                newBoard[newRow][newCol] = cell;
              }
            }
          });
          
          return newBoard;
        });
        
        setComboName({ 
          name: "🎪 WHEEL SPIN! 🎪", 
          emoji: "🎡", 
          color: "from-pink-500 to-purple-500" 
        });
        setShowComboName(true);
        
        setTimeout(() => {
          setShowSpinEffect(false);
          setSpinningCells([]);
          setShowComboName(false);
        }, 1200);
      }
    }
    
    // === PIRATE SHIPWRECK: Rising Water ===
    if (worldMechanic === 'rising_water') {
      const waterRiseRate = config.waterRiseRate || 6;
      
      setMovesSinceWaterRise(prev => {
        const newCount = prev + 1;
        if (newCount >= waterRiseRate) {
          // Water rises!
          setWaterLevel(prevWater => {
            const newWaterLevel = Math.min(prevWater + 1, boardSize - 3);
            
            // Mark bottom row(s) as underwater (blocks matches)
            setBoard(prevBoard => {
              const newBoard = prevBoard.map(r => [...r]);
              const waterRow = boardSize - 1 - prevWater;
              
              if (waterRow >= 0) {
                for (let col = 0; col < boardSize; col++) {
                  if (newBoard[waterRow][col] && !newBoard[waterRow][col].isGap) {
                    newBoard[waterRow][col] = {
                      ...newBoard[waterRow][col],
                      obstacle: OBSTACLE_TYPES.WATER_LEVEL,
                      isUnderwater: true,
                    };
                  }
                }
              }
              
              return newBoard;
            });
            
            setComboName({ name: "🌊 WATER RISES! 🌊", emoji: "💧", color: "from-blue-600 to-cyan-500" });
            setShowComboName(true);
            setTimeout(() => setShowComboName(false), 1500);
            
            return newWaterLevel;
          });
          return 0; // Reset counter
        }
        return newCount;
      });
    }
    
    // === CHERRY BLOSSOM TEMPLE: Harmony Bonus ===
    if (worldMechanic === 'harmony_bonus') {
      // Track balanced matches (equal fruit types = harmony)
      // Award bonus for consecutive "balanced" moves
      const isBalanced = Math.random() < 0.3; // Simplified: 30% chance move is "balanced"
      
      if (isBalanced) {
        setHarmonyStreak(prev => {
          const newStreak = prev + 1;
          
          if (newStreak >= 3) {
            // Award harmony bonus
            const bonus = newStreak * 200;
            setHarmonyBonus(prev => prev + bonus);
            setScore(prev => prev + bonus);
            
            setComboName({ 
              name: `🌸 HARMONY x${newStreak}! 🌸`, 
              emoji: "⛩️", 
              color: "from-pink-400 to-rose-500" 
            });
            setShowComboName(true);
            setTimeout(() => setShowComboName(false), 1500);
          }
          
          return newStreak;
        });
      } else {
        // Break streak on non-balanced move
        setHarmonyStreak(0);
      }
    }
    
    // === MUSHROOM CAVERN: Spore Spread ===
    if (worldMechanic === 'spore_spread') {
      const shouldSpread = Math.random() < 0.2; // 20% chance per move
      
      if (shouldSpread) {
        setBoard(prevBoard => {
          const newBoard = prevBoard.map(r => [...r]);
          const newSporeCells = [];
          
          // Find existing spore cells or pick random ones
          for (let r = 0; r < boardSize; r++) {
            for (let c = 0; c < boardSize; c++) {
              if (newBoard[r][c]?.obstacle === OBSTACLE_TYPES.SPORE_MUSHROOM) {
                // Spread to one adjacent cell
                const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]];
                const validTargets = [];
                
                for (const [dr, dc] of directions) {
                  const nr = r + dr;
                  const nc = c + dc;
                  if (nr >= 0 && nr < boardSize && nc >= 0 && nc < boardSize) {
                    const targetCell = newBoard[nr][nc];
                    if (targetCell && !targetCell.isGap && targetCell.obstacle === OBSTACLE_TYPES.NONE) {
                      validTargets.push({ row: nr, col: nc });
                    }
                  }
                }
                
                if (validTargets.length > 0 && Math.random() < 0.4) {
                  const target = validTargets[Math.floor(Math.random() * validTargets.length)];
                  newBoard[target.row][target.col] = {
                    ...newBoard[target.row][target.col],
                    obstacle: OBSTACLE_TYPES.SPORE_MUSHROOM,
                    isSpore: true,
                  };
                  newSporeCells.push(target);
                }
              }
            }
          }
          
          // If no spores exist yet, spawn one randomly
          if (newSporeCells.length === 0 && Math.random() < 0.3) {
            const randRow = Math.floor(Math.random() * boardSize);
            const randCol = Math.floor(Math.random() * boardSize);
            if (newBoard[randRow]?.[randCol] && !newBoard[randRow][randCol].isGap) {
              newBoard[randRow][randCol] = {
                ...newBoard[randRow][randCol],
                obstacle: OBSTACLE_TYPES.SPORE_MUSHROOM,
                isSpore: true,
              };
              newSporeCells.push({ row: randRow, col: randCol });
            }
          }
          
          if (newSporeCells.length > 0) {
            setSporeCells(newSporeCells);
            setSporeCount(prev => prev + newSporeCells.length);
            
            setComboName({ 
              name: "🍄 SPORES SPREAD! 🍄", 
              emoji: "🍄", 
              color: "from-purple-600 to-fuchsia-500" 
            });
            setShowComboName(true);
            setTimeout(() => setShowComboName(false), 1200);
          }
          
          return newBoard;
        });
      }
    }
    
  }, [worldMechanic, lavaRiseRate, cycleLength, boardSize, gravityDirection, fruitSet, targetFruit, config]);

  // Handle swipe gesture for fruit swapping
  const handleSwipe = useCallback(async (fromRow, fromCol, toRow, toCol, direction) => {
    if (computedGameStatus !== 'playing' || isAnimating) {
      return;
    }
    
    // Check bounds
    if (toRow < 0 || toRow >= boardSize || toCol < 0 || toCol >= boardSize) return;
    
    const fromCell = board[fromRow][fromCol];
    const toCell = board[toRow][toCol];
    
    if (!fromCell || !toCell) return;
    
    // Ingredients cannot be swiped - they are solid!
    if (fromCell?.isIngredient || toCell?.isIngredient) {
      return; // Ignore swipes involving ingredients
    }
    
    const isDiscoSwap = fromCell?.special === SPECIAL_TYPES.DISCO || 
                        toCell?.special === SPECIAL_TYPES.DISCO;
    
    // Check for special fruit combos
    const isStripedCombo = (fromCell?.special === SPECIAL_TYPES.STRIPED_H || fromCell?.special === SPECIAL_TYPES.STRIPED_V) &&
                           (toCell?.special === SPECIAL_TYPES.STRIPED_H || toCell?.special === SPECIAL_TYPES.STRIPED_V);
    
    const isStripedWrappedCombo = ((fromCell?.special === SPECIAL_TYPES.STRIPED_H || fromCell?.special === SPECIAL_TYPES.STRIPED_V) &&
                                   toCell?.special === SPECIAL_TYPES.WRAPPED) ||
                                  ((toCell?.special === SPECIAL_TYPES.STRIPED_H || toCell?.special === SPECIAL_TYPES.STRIPED_V) &&
                                   fromCell?.special === SPECIAL_TYPES.WRAPPED);
    
    // NEW: Wrapped + Wrapped combo
    const isWrappedWrappedCombo = fromCell?.special === SPECIAL_TYPES.WRAPPED &&
                                  toCell?.special === SPECIAL_TYPES.WRAPPED;
    
    // NEW: Disco + Striped combo
    const isDiscoStripedCombo = (fromCell?.special === SPECIAL_TYPES.DISCO &&
                                 (toCell?.special === SPECIAL_TYPES.STRIPED_H || toCell?.special === SPECIAL_TYPES.STRIPED_V)) ||
                                ((fromCell?.special === SPECIAL_TYPES.STRIPED_H || fromCell?.special === SPECIAL_TYPES.STRIPED_V) &&
                                 toCell?.special === SPECIAL_TYPES.DISCO);
    
    const isSpecialCombo = isStripedCombo || isStripedWrappedCombo || isWrappedWrappedCombo || isDiscoStripedCombo;
    
    // Check if swap would create a match (or is a special combo)
    if (isDiscoSwap || isSpecialCombo || wouldCreateMatch(board, fromRow, fromCol, toRow, toCol)) {
      setIsAnimating(true);
      setSelectedCell(null);
      
      // Safety timeout to ensure isAnimating gets reset even if something goes wrong
      const safetyTimeout = setTimeout(() => {
        setIsAnimating(false);
      }, 10000); // 10 seconds max
      
      try {
        // Start swap animation
        setSwappingCells({
          from: { row: fromRow, col: fromCol },
          to: { row: toRow, col: toCol },
          direction
        });
      
      // Wait for swap animation
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Perform actual swap
      let newBoard = board.map(r => r.map(cell => cell ? { ...cell } : null));
      [newBoard[fromRow][fromCol], newBoard[toRow][toCol]] = 
      [newBoard[toRow][toCol], newBoard[fromRow][fromCol]];
      
      setBoard(newBoard);
      setSwappingCells(null);
      setMovesLeft(prev => prev - 1);
      
      // Process world mechanics after move
      processWorldMechanics();
      
      // Decrement 2X XP turns if active
      if (doubleXPActive && doubleXPTurnsLeft > 0) {
        setDoubleXPTurnsLeft(prev => {
          const newTurns = prev - 1;
          if (newTurns <= 0) {
            setDoubleXPActive(false);
          }
          return newTurns;
        });
      }
      
      // Handle special fruit combos (Striped + Striped, Striped + Wrapped)
      if (isSpecialCombo) {
        await new Promise(resolve => setTimeout(resolve, 150));
        
        // Calculate center point for the combo
        const centerRow = Math.floor((fromRow + toRow) / 2);
        const centerCol = Math.floor((fromCol + toCol) / 2);
        
        let cellsToExplode = new Set();
        let comboPoints = 0;
        let targetFruitsCollected = 0;
        
        if (isStripedCombo) {
          // Striped + Striped = Clear entire row AND column (cross pattern)
          setSpecialComboAnimation({ type: 'cross', row: fromRow, col: fromCol, phase: 'start' });
          setClearEffect({ type: 'cross', row: fromRow, col: fromCol });
          setLastSpecialComboTriggered(prev => !prev); // Toggle to trigger effect
          
          await new Promise(resolve => setTimeout(resolve, 300));
          
          // Clear the row of fromCell
          for (let c = 0; c < boardSize; c++) {
            if (!newBoard[fromRow][c]?.isGap && !newBoard[fromRow][c]?.isIngredient && newBoard[fromRow][c]) {
              cellsToExplode.add(`${fromRow}-${c}`);
              if (newBoard[fromRow][c]?.type === config.targetFruit) targetFruitsCollected++;
              comboPoints += 15;
            }
          }
          // Clear the column of fromCell
          for (let r = 0; r < boardSize; r++) {
            if (!newBoard[r][fromCol]?.isGap && !newBoard[r][fromCol]?.isIngredient && newBoard[r][fromCol]) {
              cellsToExplode.add(`${r}-${fromCol}`);
              if (newBoard[r][fromCol]?.type === config.targetFruit) targetFruitsCollected++;
              comboPoints += 15;
            }
          }
          // Also clear row and column of toCell
          for (let c = 0; c < boardSize; c++) {
            if (!newBoard[toRow][c]?.isGap && !newBoard[toRow][c]?.isIngredient && newBoard[toRow][c]) {
              cellsToExplode.add(`${toRow}-${c}`);
              if (newBoard[toRow][c]?.type === config.targetFruit) targetFruitsCollected++;
              comboPoints += 15;
            }
          }
          for (let r = 0; r < boardSize; r++) {
            if (!newBoard[r][toCol]?.isGap && !newBoard[r][toCol]?.isIngredient && newBoard[r][toCol]) {
              cellsToExplode.add(`${r}-${toCol}`);
              if (newBoard[r][toCol]?.type === config.targetFruit) targetFruitsCollected++;
              comboPoints += 15;
            }
          }
          
        } else if (isStripedWrappedCombo) {
          // Striped + Wrapped = 3x3 expansion + clear 3 rows horizontally and 3 columns vertically
          const wrappedPos = fromCell?.special === SPECIAL_TYPES.WRAPPED 
            ? { row: toRow, col: toCol }  // After swap, wrapped is at toCell position
            : { row: fromRow, col: fromCol };
          
          setSpecialComboAnimation({ type: 'mega', row: wrappedPos.row, col: wrappedPos.col, phase: 'expand' });
          setLastSpecialComboTriggered(prev => !prev); // Toggle to trigger effect
          
          // First phase: 3x3 expansion animation
          await new Promise(resolve => setTimeout(resolve, 400));
          
          setSpecialComboAnimation({ type: 'mega', row: wrappedPos.row, col: wrappedPos.col, phase: 'sweep' });
          setClearEffect({ type: 'mega', row: wrappedPos.row, col: wrappedPos.col });
          
          // Clear 3 rows (row-1, row, row+1) horizontally
          for (let rOffset = -1; rOffset <= 1; rOffset++) {
            const r = wrappedPos.row + rOffset;
            if (r >= 0 && r < boardSize) {
              for (let c = 0; c < boardSize; c++) {
                if (!newBoard[r][c]?.isGap && !newBoard[r][c]?.isIngredient && newBoard[r][c]) {
                  cellsToExplode.add(`${r}-${c}`);
                  if (newBoard[r][c]?.type === config.targetFruit) targetFruitsCollected++;
                  comboPoints += 20;
                }
              }
            }
          }
          
          // Clear 3 columns (col-1, col, col+1) vertically
          for (let cOffset = -1; cOffset <= 1; cOffset++) {
            const c = wrappedPos.col + cOffset;
            if (c >= 0 && c < boardSize) {
              for (let r = 0; r < boardSize; r++) {
                if (!newBoard[r][c]?.isGap && !newBoard[r][c]?.isIngredient && newBoard[r][c]) {
                  cellsToExplode.add(`${r}-${c}`);
                  if (newBoard[r][c]?.type === config.targetFruit) targetFruitsCollected++;
                  comboPoints += 20;
                }
              }
            }
          }
        } else if (isWrappedWrappedCombo) {
          // NEW: Wrapped + Wrapped = 4x4 explosion centered between the two wrapped fruits
          const centerRowWW = Math.floor((fromRow + toRow) / 2);
          const centerColWW = Math.floor((fromCol + toCol) / 2);
          
          setSpecialComboAnimation({ type: 'mega_explosion', row: centerRowWW, col: centerColWW, phase: 'start' });
          setClearEffect({ type: 'mega_explosion', row: centerRowWW, col: centerColWW });
          setLastSpecialComboTriggered(prev => !prev);
          
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // First explosion wave
          setSpecialComboAnimation({ type: 'mega_explosion', row: centerRowWW, col: centerColWW, phase: 'wave1' });
          await new Promise(resolve => setTimeout(resolve, 150));
          
          // Second explosion wave
          setSpecialComboAnimation({ type: 'mega_explosion', row: centerRowWW, col: centerColWW, phase: 'wave2' });
          
          // Clear 4x4 area around both wrapped fruits
          for (let rOffset = -2; rOffset <= 2; rOffset++) {
            for (let cOffset = -2; cOffset <= 2; cOffset++) {
              const r = fromRow + rOffset;
              const c = fromCol + cOffset;
              if (r >= 0 && r < boardSize && c >= 0 && c < boardSize) {
                if (!newBoard[r][c]?.isGap && !newBoard[r][c]?.isIngredient && newBoard[r][c]) {
                  cellsToExplode.add(`${r}-${c}`);
                  if (newBoard[r][c]?.type === config.targetFruit) targetFruitsCollected++;
                  comboPoints += 25;
                }
              }
            }
          }
          // Also include area around second wrapped fruit
          for (let rOffset = -2; rOffset <= 2; rOffset++) {
            for (let cOffset = -2; cOffset <= 2; cOffset++) {
              const r = toRow + rOffset;
              const c = toCol + cOffset;
              if (r >= 0 && r < boardSize && c >= 0 && c < boardSize) {
                if (!newBoard[r][c]?.isGap && !newBoard[r][c]?.isIngredient && newBoard[r][c]) {
                  cellsToExplode.add(`${r}-${c}`);
                  if (newBoard[r][c]?.type === config.targetFruit) targetFruitsCollected++;
                  comboPoints += 25;
                }
              }
            }
          }
        } else if (isDiscoStripedCombo) {
          // NEW: Disco + Striped = All fruits of the striped fruit's type become striped and activate
          let discoPos, stripedPos, stripedCell;
          if (fromCell?.special === SPECIAL_TYPES.DISCO) {
            discoPos = { row: toRow, col: toCol }; // Disco moved here after swap
            stripedPos = { row: fromRow, col: fromCol }; // Striped moved here
            stripedCell = toCell; // Original striped cell data
          } else {
            discoPos = { row: fromRow, col: fromCol }; // Disco moved here
            stripedPos = { row: toRow, col: toCol }; // Striped moved here
            stripedCell = fromCell; // Original striped cell data
          }
          
          const targetFruitType = stripedCell.type;
          
          // Phase 1: Rainbow wave animation
          setSpecialComboAnimation({ type: 'disco_striped', phase: 'rainbow', targetType: targetFruitType });
          setClearEffect({ type: 'disco_striped', targetType: targetFruitType });
          setLastSpecialComboTriggered(prev => !prev);
          
          await new Promise(resolve => setTimeout(resolve, 300));
          
          // Find all fruits of the target type
          const stripedPositions = [];
          for (let r = 0; r < boardSize; r++) {
            for (let c = 0; c < boardSize; c++) {
              if (newBoard[r][c]?.type === targetFruitType && !newBoard[r][c]?.isGap) {
                stripedPositions.push({ row: r, col: c });
              }
            }
          }
          
          // Phase 2: Transform animation
          setSpecialComboAnimation({ type: 'disco_striped', phase: 'transform', positions: stripedPositions });
          
          await new Promise(resolve => setTimeout(resolve, 400));
          
          // Phase 3: Activate all striped fruits
          setSpecialComboAnimation({ type: 'disco_striped', phase: 'activate' });
          
          // Clear all rows and columns where the striped fruits were
          for (const pos of stripedPositions) {
            // Clear row
            for (let c = 0; c < boardSize; c++) {
              if (!newBoard[pos.row][c]?.isGap && !newBoard[pos.row][c]?.isIngredient && newBoard[pos.row][c]) {
                cellsToExplode.add(`${pos.row}-${c}`);
                if (newBoard[pos.row][c]?.type === config.targetFruit) targetFruitsCollected++;
                comboPoints += 15;
              }
            }
            // Clear column
            for (let r = 0; r < boardSize; r++) {
              if (!newBoard[r][pos.col]?.isGap && !newBoard[r][pos.col]?.isIngredient && newBoard[r][pos.col]) {
                cellsToExplode.add(`${r}-${pos.col}`);
                if (newBoard[r][pos.col]?.type === config.targetFruit) targetFruitsCollected++;
                comboPoints += 15;
              }
            }
          }
          
          // Also clear the disco fruit position
          cellsToExplode.add(`${discoPos.row}-${discoPos.col}`);
          comboPoints += 50; // Bonus for disco combo
        }
        
        // Show exploding cells
        setExplodingCells(cellsToExplode);
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Remove exploded cells
        cellsToExplode.forEach(key => {
          const [r, c] = key.split('-').map(Number);
          if (newBoard[r] && newBoard[r][c] && !newBoard[r][c]?.isGap) {
            newBoard[r][c] = null;
          }
        });
        
        // Drop and fill columns
        for (let c = 0; c < boardSize; c++) {
          dropAndFillColumn(newBoard, c, fruitSet, config.targetFruit, boostTarget, levelId);
        }
        
        setBoard([...newBoard.map(r => [...r])]);
        setFruitsCollected(prev => prev + targetFruitsCollected);
        setScore(prev => {
          const newScore = prev + comboPoints;
          updateHighScoreIfNeeded(newScore);
          return newScore;
        });
        
        setExplodingCells(new Set());
        setClearEffect(null);
        setSpecialComboAnimation(null);
        
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Process cascade
        const { finalBoard, totalTargetFruits, totalScore: cascadeScore, eliminatedByType, totalStripedCreated, totalWrappedCreated, totalIceBroken, totalChainsBroken, totalChocolateCleared, totalIngredientsDropped } = 
          await processCascade(newBoard, null, config.targetFruit, fruitSet);
        
        if (eliminatedByType && Object.keys(eliminatedByType).length > 0) {
          setLastEliminatedFruits(eliminatedByType);
          trackDualFruitsFromEliminated(eliminatedByType);
        }
        
        // Update mission progress for this cascade
        if (totalIceBroken > 0 || totalChainsBroken > 0 || totalChocolateCleared > 0 || totalStripedCreated > 0 || totalWrappedCreated > 0 || totalIngredientsDropped > 0) {
          setMissionProgress(prev => ({
            ...prev,
            iceBroken: prev.iceBroken + (totalIceBroken || 0),
            chainsBroken: prev.chainsBroken + (totalChainsBroken || 0),
            chocolateCleared: prev.chocolateCleared + (totalChocolateCleared || 0),
            stripedCreated: prev.stripedCreated + (totalStripedCreated || 0),
            wrappedCreated: prev.wrappedCreated + (totalWrappedCreated || 0),
            ingredientsDropped: prev.ingredientsDropped + (totalIngredientsDropped || 0),
          }));
        }
        
        // Check for valid moves
        let boardToSet = finalBoard;
        if (!hasValidMoves(boardToSet)) {
          setShowShuffleAlert(true);
          setTimeout(() => setShowShuffleAlert(false), 2000);
          boardToSet = shuffleBoard(boardToSet);
          while (!hasValidMoves(boardToSet)) {
            boardToSet = shuffleBoard(boardToSet);
          }
        }
        
        setBoard(boardToSet);
        return;
      }
      
      // Handle disco fruit swap
      if (isDiscoSwap) {
        await new Promise(resolve => setTimeout(resolve, 150));
        
        let discoPos, targetPos;
        if (fromCell?.special === SPECIAL_TYPES.DISCO) {
          discoPos = { row: toRow, col: toCol };
          targetPos = { row: fromRow, col: fromCol };
        } else {
          discoPos = { row: fromRow, col: fromCol };
          targetPos = { row: toRow, col: toCol };
        }
        
        const swappedFruitType = newBoard[targetPos.row][targetPos.col]?.type;
        
        if (swappedFruitType) {
          let targetFruitsFromDisco = 0;
          let cellsCleared = 0;
          const currentBoardSize = newBoard.length;
          
          for (let r = 0; r < currentBoardSize; r++) {
            for (let c = 0; c < currentBoardSize; c++) {
              // Don't clear gaps
              if (newBoard[r][c]?.isGap) continue;
              if (newBoard[r][c]?.type === swappedFruitType || 
                  (r === discoPos.row && c === discoPos.col)) {
                if (newBoard[r][c]?.type === config.targetFruit) {
                  targetFruitsFromDisco++;
                }
                newBoard[r][c] = null;
                cellsCleared++;
              }
            }
          }
          
          // Drop and fill (respecting gaps)
          for (let c = 0; c < currentBoardSize; c++) {
            dropAndFillColumn(newBoard, c, fruitSet, config.targetFruit, boostTarget, levelId);
          }
          
          setBoard([...newBoard.map(r => [...r])]);
          setFruitsCollected(prev => prev + targetFruitsFromDisco);
          setScore(prev => {
            const newScore = prev + cellsCleared * 20;
            updateHighScoreIfNeeded(newScore);
            return newScore;
          });
          
          // Track disco combo for challenges
          setLastDiscoCombo(true);
          setTimeout(() => setLastDiscoCombo(false), 100);
          
          await new Promise(resolve => setTimeout(resolve, 300));
          
          const { finalBoard, totalTargetFruits, totalScore: cascadeScore, eliminatedByType: discoEliminated, totalStripedCreated, totalWrappedCreated, totalIceBroken, totalChainsBroken, totalChocolateCleared, totalIngredientsDropped } = 
            await processCascade(newBoard, null, config.targetFruit, fruitSet);
          
          // Track eliminated fruits for challenges
          if (discoEliminated && Object.keys(discoEliminated).length > 0) {
            setLastEliminatedFruits(discoEliminated);
          }
          
          // Update mission progress for this cascade
          if (totalIceBroken > 0 || totalChainsBroken > 0 || totalChocolateCleared > 0 || totalStripedCreated > 0 || totalWrappedCreated > 0 || totalIngredientsDropped > 0) {
            setMissionProgress(prev => ({
              ...prev,
              iceBroken: prev.iceBroken + (totalIceBroken || 0),
              chainsBroken: prev.chainsBroken + (totalChainsBroken || 0),
              chocolateCleared: prev.chocolateCleared + (totalChocolateCleared || 0),
              stripedCreated: prev.stripedCreated + (totalStripedCreated || 0),
              wrappedCreated: prev.wrappedCreated + (totalWrappedCreated || 0),
              ingredientsDropped: prev.ingredientsDropped + (totalIngredientsDropped || 0),
            }));
          }
          
          // Check if board has valid moves, shuffle if not
          let boardToSet = finalBoard;
          if (!hasValidMoves(finalBoard)) {
            boardToSet = shuffleBoard(finalBoard);
            setShowShuffleAlert(true);
            setTimeout(() => setShowShuffleAlert(false), 2000);
          }
          
          setBoard([...boardToSet.map(r => [...r])]);
          setFruitsCollected(prev => prev + totalTargetFruits);
          setScore(prev => {
            const newScore = prev + cascadeScore;
            updateHighScoreIfNeeded(newScore);
            return newScore;
          });
        }
        
        return;
      }
      
      // Regular swap - process cascades
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const swappedCell = { row: toRow, col: toCol };
      const { finalBoard, totalTargetFruits, totalScore: cascadeScore, had4Match, had6Match, hadDiscoCombo, eliminatedByType, totalStripedCreated, totalWrappedCreated, totalIceBroken, totalChainsBroken, totalChocolateCleared, totalIngredientsDropped } = 
        await processCascade(newBoard, swappedCell, config.targetFruit, fruitSet);
      
      // Track eliminated fruits for challenges
      if (eliminatedByType && Object.keys(eliminatedByType).length > 0) {
        setLastEliminatedFruits(eliminatedByType);
        trackDualFruitsFromEliminated(eliminatedByType);
      }
      
      // Update mission progress for this cascade
      if (totalIceBroken > 0 || totalChainsBroken > 0 || totalChocolateCleared > 0 || totalStripedCreated > 0 || totalWrappedCreated > 0 || totalIngredientsDropped > 0) {
        setMissionProgress(prev => ({
          ...prev,
          iceBroken: prev.iceBroken + (totalIceBroken || 0),
          chainsBroken: prev.chainsBroken + (totalChainsBroken || 0),
          chocolateCleared: prev.chocolateCleared + (totalChocolateCleared || 0),
          stripedCreated: prev.stripedCreated + (totalStripedCreated || 0),
          wrappedCreated: prev.wrappedCreated + (totalWrappedCreated || 0),
          ingredientsDropped: prev.ingredientsDropped + (totalIngredientsDropped || 0),
        }));
      }
      
      // Track disco combos for challenges
      if (hadDiscoCombo) {
        setLastDiscoCombo(true);
        setTimeout(() => setLastDiscoCombo(false), 100);
      }
      
      // Check if board has valid moves, shuffle if not
      let boardToSet = finalBoard;
      if (!hasValidMoves(finalBoard)) {
        boardToSet = shuffleBoard(finalBoard);
        setShowShuffleAlert(true);
        setTimeout(() => setShowShuffleAlert(false), 2000);
      }
      
      setBoard([...boardToSet.map(r => [...r])]);
      setFruitsCollected(prev => prev + totalTargetFruits);
      
      let scoreToAdd = cascadeScore;
      if (doublePointsActive) {
        scoreToAdd *= 2;
        setDoublePointsActive(false);
      }
      
      setScore(prev => {
        const newScore = prev + scoreToAdd;
        updateHighScoreIfNeeded(newScore);
        return newScore;
      });
      
      // Handle combo tracking
      if (had6Match) {
        setInstaEliminationActive(true);
        setShowInstaEliminationPicker(true);
        setConsecutive4Combos(0);
      } else if (had4Match) {
        const newCount = consecutive4Combos + 1;
        setConsecutive4Combos(newCount);
        
        if (newCount >= 2) {
          setDoublePointsActive(true);
          setShowDoublePointsAlert(true);
          setConsecutive4Combos(0);
          setTimeout(() => setShowDoublePointsAlert(false), 2500);
        }
      } else {
        setConsecutive4Combos(0);
      }
      
      setLastMatchInfo(null);
      } catch (error) {
        console.error('[handleSwipe] Error during swap:', error);
      } finally {
        // Always reset animation state to prevent freezes
        clearTimeout(safetyTimeout);
        setIsAnimating(false);
      }
    } else {
      // Invalid swap - show bounce back animation
      setSwappingCells({
        from: { row: fromRow, col: fromCol },
        to: { row: toRow, col: toCol },
        direction
      });
      
      await new Promise(resolve => setTimeout(resolve, 150));
      setSwappingCells(null);
    }
  }, [board, boardSize, computedGameStatus, isAnimating, processCascade, updateHighScoreIfNeeded, config.targetFruit, doublePointsActive, consecutive4Combos]);

  // Insta-elimination function - eliminate all of a chosen fruit type
  const useInstaElimination = useCallback(async (fruitType) => {
    if (!instaEliminationActive) return;
    
    setIsAnimating(true);
    setShowInstaEliminationPicker(false);
    
    let newBoard = board.map(r => r.map(cell => cell ? { ...cell } : null));
    const currentBoardSize = newBoard.length;
    let targetFruitsEliminated = 0;
    let cellsCleared = 0;
    
    // Find all cells of the chosen type (skip gaps)
    const cellsToEliminate = new Set();
    for (let r = 0; r < currentBoardSize; r++) {
      for (let c = 0; c < currentBoardSize; c++) {
        if (newBoard[r][c]?.isGap) continue;
        if (newBoard[r][c]?.type === fruitType) {
          cellsToEliminate.add(`${r}-${c}`);
          if (fruitType === config.targetFruit) {
            targetFruitsEliminated++;
          }
          cellsCleared++;
        }
      }
    }
    
    // Show explosion animation
    setExplodingCells(cellsToEliminate);
    await new Promise(resolve => setTimeout(resolve, 400));
    setExplodingCells(new Set());
    
    // Remove the fruits (skip gaps)
    for (let r = 0; r < currentBoardSize; r++) {
      for (let c = 0; c < currentBoardSize; c++) {
        if (newBoard[r][c]?.isGap) continue;
        if (newBoard[r][c]?.type === fruitType) {
          newBoard[r][c] = null;
        }
      }
    }
    
    // Drop fruits and fill empty cells (respecting gaps)
    for (let c = 0; c < currentBoardSize; c++) {
      dropAndFillColumn(newBoard, c, fruitSet, config.targetFruit, boostTarget, levelId);
    }
    
    setBoard([...newBoard.map(r => [...r])]);
    setFruitsCollected(prev => prev + targetFruitsEliminated);
    setScore(prev => {
      const newScore = prev + cellsCleared * 15;
      updateHighScoreIfNeeded(newScore);
      return newScore;
    });
    
    // Use up the ability (doesn't cost a move)
    setInstaEliminationActive(false);
    
    // Wait for drop animation
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Process any cascades that result
    const { finalBoard, totalTargetFruits, totalScore, totalStripedCreated, totalWrappedCreated, totalIceBroken, totalChainsBroken, totalChocolateCleared, totalIngredientsDropped } = await processCascade(
      newBoard,
      null,
      config.targetFruit,
      fruitSet
    );
    
    // Update mission progress for this cascade
    if (totalIceBroken > 0 || totalChainsBroken > 0 || totalChocolateCleared > 0 || totalStripedCreated > 0 || totalWrappedCreated > 0 || totalIngredientsDropped > 0) {
      setMissionProgress(prev => ({
        ...prev,
        iceBroken: prev.iceBroken + (totalIceBroken || 0),
        chainsBroken: prev.chainsBroken + (totalChainsBroken || 0),
        chocolateCleared: prev.chocolateCleared + (totalChocolateCleared || 0),
        stripedCreated: prev.stripedCreated + (totalStripedCreated || 0),
        wrappedCreated: prev.wrappedCreated + (totalWrappedCreated || 0),
        ingredientsDropped: prev.ingredientsDropped + (totalIngredientsDropped || 0),
      }));
    }
    
    // Check if board has valid moves, shuffle if not
    let boardToSet = finalBoard;
    if (!hasValidMoves(finalBoard)) {
      boardToSet = shuffleBoard(finalBoard);
      setShowShuffleAlert(true);
      setTimeout(() => setShowShuffleAlert(false), 2000);
    }
    
    setBoard(boardToSet);
    setFruitsCollected(prev => prev + totalTargetFruits);
    setScore(prev => {
      const newScore = prev + totalScore;
      updateHighScoreIfNeeded(newScore);
      return newScore;
    });
    
    setIsAnimating(false);
  }, [board, instaEliminationActive, config.targetFruit, fruitSet, processCascade, updateHighScoreIfNeeded]);

  // Cancel insta-elimination (if player doesn't want to use it)
  const cancelInstaElimination = useCallback(() => {
    setShowInstaEliminationPicker(false);
    // Keep the ability active for later use
  }, []);

  // Re-open insta-elimination picker (when player taps the indicator)
  const openInstaEliminationPicker = useCallback(() => {
    if (instaEliminationActive) {
      setShowInstaEliminationPicker(true);
    }
  }, [instaEliminationActive]);

  // Activate sword mode (player clicks sword button to enter sword mode)
  const activateSwordMode = useCallback(() => {
    if (!swordAbilityUsed && gameStatus === 'playing' && !isAnimating) {
      setSwordMode(true);
    }
  }, [swordAbilityUsed, gameStatus, isAnimating]);

  // Cancel sword mode
  const cancelSwordMode = useCallback(() => {
    setSwordMode(false);
  }, []);

  // Get sword tier info based on player level
  const swordTier = useMemo(() => {
    // Check for sword upgrade bonus (+1 size)
    const swordBonus = activeUpgrades.swordBonus || 0;
    
    let baseTier;
    if (playerLevel >= 150) {
      baseTier = { name: 'Master', size: 8, color: 'purple', emoji: '👑', radius: 4 };
    } else if (playerLevel >= 100) {
      baseTier = { name: 'Diamond', size: 7, color: 'cyan', emoji: '💎', radius: 3 };
    } else if (playerLevel >= 50) {
      baseTier = { name: 'Golden', size: 5, color: 'yellow', emoji: '🥇', radius: 2 };
    } else if (playerLevel >= 20) {
      baseTier = { name: 'Silver', size: 4, color: 'gray', emoji: '🥈', radius: 1.5 };
    } else if (playerLevel >= 3) {
      baseTier = { name: 'Bronze', size: 3, color: 'amber', emoji: '🥉', radius: 1 };
    } else {
      return null; // No sword available
    }
    
    // Apply sword bonus if upgrade is purchased
    if (swordBonus > 0) {
      baseTier.size += swordBonus;
      baseTier.radius += swordBonus / 2;
    }
    
    return baseTier;
  }, [playerLevel, activeUpgrades.swordBonus]);

  // Use sword ability - cut NxN area around clicked cell based on sword tier
  const useSwordAbility = useCallback(async (centerRow, centerCol) => {
    try {
      // Check if sword uses are exhausted
      if (swordUsesRemaining <= 0 || !swordMode || isAnimating || gameStatus !== 'playing' || !swordTier) return;
      
      // Bounds check
      if (centerRow < 0 || centerRow >= boardSize || centerCol < 0 || centerCol >= boardSize) return;
      
      // Don't allow clicking on gaps
      if (!board[centerRow] || !board[centerRow][centerCol] || board[centerRow][centerCol]?.isGap) return;
      
      setIsAnimating(true);
      setSwordMode(false);
      
      // Decrement sword uses
      const newUsesRemaining = swordUsesRemaining - 1;
      setSwordUsesRemaining(newUsesRemaining);
      // Only mark as fully used if no uses remain
      if (newUsesRemaining <= 0) {
        setSwordAbilityUsed(true);
      }
      
      // Trigger sword slash animation
      setSwordSlashAnimation({ centerRow, centerCol, tier: swordTier.name.toLowerCase() });
      
      // Wait for slash animation to play
      await new Promise(resolve => setTimeout(resolve, 400));
      
      const newBoard = board.map(row => row.map(cell => cell ? { ...cell } : null));
      const cellsToRemove = new Set();
      let targetFruitsEliminated = 0;
      const eliminatedByType = {};
      
      // Calculate the range based on sword size
      // For odd sizes (3, 5, 7): radius is (size-1)/2
      // For even sizes (4): we use ceil and floor for asymmetric range
      const swordSize = swordTier.size;
      const halfSize = Math.floor(swordSize / 2);
      const startOffset = -halfSize;
      const endOffset = swordSize % 2 === 0 ? halfSize - 1 : halfSize;
    
    // Track ingredients destroyed by sword
    let ingredientsDestroyedBySword = 0;
    
    // Mark NxN area for removal (skip gaps)
    for (let r = centerRow + startOffset; r <= centerRow + endOffset + (swordSize % 2 === 0 ? 1 : 0); r++) {
      for (let c = centerCol + startOffset; c <= centerCol + endOffset + (swordSize % 2 === 0 ? 1 : 0); c++) {
        if (r >= 0 && r < boardSize && c >= 0 && c < boardSize && newBoard[r][c] && !newBoard[r][c]?.isGap) {
          const cell = newBoard[r][c];
          cellsToRemove.add(`${r},${c}`);
          
          // Track eliminated fruits
          if (cell.type === config.targetFruit && !cell.isIngredient) {
            targetFruitsEliminated++;
          }
          
          // Track ingredients destroyed by sword - they should count as collected
          if (cell.isIngredient) {
            ingredientsDestroyedBySword++;
          }
          
          eliminatedByType[cell.type] = (eliminatedByType[cell.type] || 0) + 1;
        }
      }
    }
    
    // Set exploding cells for animation
    setExplodingCells(cellsToRemove);
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Clear sword animation
    setSwordSlashAnimation(null);
    
    // Remove cells (skip gaps)
    cellsToRemove.forEach(key => {
      const [r, c] = key.split(',').map(Number);
      if (!newBoard[r][c]?.isGap) {
        newBoard[r][c] = null;
      }
    });
    
    // Track for challenges
    if (Object.keys(eliminatedByType).length > 0) {
      setLastEliminatedFruits(eliminatedByType);
      trackDualFruitsFromEliminated(eliminatedByType);
    }
    
    setExplodingCells(new Set());
    
    // Drop fruits and fill empty cells (respecting gaps)
    const targetFruitForDrop = config?.targetFruit || targetFruit;
    for (let c = 0; c < boardSize; c++) {
      dropAndFillColumn(newBoard, c, fruitSet, targetFruitForDrop, boostTarget, levelId);
    }
    
    setBoard([...newBoard.map(r => [...r])]);
    setFruitsCollected(prev => prev + targetFruitsEliminated);
    setScore(prev => {
      const newScore = prev + cellsToRemove.size * 20; // 20 points per cell
      updateHighScoreIfNeeded(newScore);
      return newScore;
    });
    
    // Wait for drop animation
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Process any cascades
    const { finalBoard, totalTargetFruits, totalScore, totalStripedCreated, totalWrappedCreated, totalIceBroken, totalChainsBroken, totalChocolateCleared, totalIngredientsDropped } = await processCascade(
      newBoard,
      null,
      config.targetFruit,
      fruitSet
    );
    
    // Update mission progress for this cascade + ingredients destroyed by sword
    const totalIngredientsCollected = (totalIngredientsDropped || 0) + ingredientsDestroyedBySword;
    if (totalIceBroken > 0 || totalChainsBroken > 0 || totalChocolateCleared > 0 || totalStripedCreated > 0 || totalWrappedCreated > 0 || totalIngredientsCollected > 0) {
      setMissionProgress(prev => ({
        ...prev,
        iceBroken: prev.iceBroken + (totalIceBroken || 0),
        chainsBroken: prev.chainsBroken + (totalChainsBroken || 0),
        chocolateCleared: prev.chocolateCleared + (totalChocolateCleared || 0),
        stripedCreated: prev.stripedCreated + (totalStripedCreated || 0),
        wrappedCreated: prev.wrappedCreated + (totalWrappedCreated || 0),
        ingredientsDropped: prev.ingredientsDropped + totalIngredientsCollected,
      }));
    }
    
    // Check if board has valid moves
    let boardToSet = finalBoard;
    if (!hasValidMoves(finalBoard)) {
      boardToSet = shuffleBoard(finalBoard);
      setShowShuffleAlert(true);
      setTimeout(() => setShowShuffleAlert(false), 2000);
    }
    
    setBoard(boardToSet);
    setFruitsCollected(prev => prev + totalTargetFruits);
    setScore(prev => {
      const newScore = prev + totalScore;
      updateHighScoreIfNeeded(newScore);
      return newScore;
    });
    
    setIsAnimating(false);
    } catch (error) {
      console.error('Sword ability error:', error);
      setIsAnimating(false);
      setSwordMode(false);
      setSwordSlashAnimation(null);
      setExplodingCells(new Set());
    }
  }, [board, swordUsesRemaining, swordMode, isAnimating, gameStatus, boardSize, config, targetFruit, fruitSet, processCascade, updateHighScoreIfNeeded, swordTier, boostTarget, levelId]);

  // ============== GOD SWORD ABILITY ==============
  // Legendary item that clears the ENTIRE board in one use
  
  // Activate God Sword mode
  const activateGodSwordMode = useCallback(() => {
    if (godSwordUsesRemaining > 0 && gameStatus === 'playing' && !isAnimating) {
      setGodSwordMode(true);
    }
  }, [godSwordUsesRemaining, gameStatus, isAnimating]);

  // Cancel God Sword mode
  const cancelGodSwordMode = useCallback(() => {
    setGodSwordMode(false);
  }, []);

  // Use God Sword ability - clears ENTIRE board
  const activateGodSword = useCallback(async () => {
    try {
      if (godSwordUsesRemaining <= 0 || !godSwordMode || isAnimating || gameStatus !== 'playing') return;
      
      setIsAnimating(true);
      setGodSwordMode(false);
      
      // Decrement uses
      const newUsesRemaining = godSwordUsesRemaining - 1;
      setGodSwordUsesRemaining(newUsesRemaining);
      
      // Trigger epic animation
      setGodSwordAnimation(true);
      
      // Show epic combo name
      setComboName({ name: "⚔️ GOD SWORD! ⚔️", emoji: "💀", color: "from-yellow-400 via-amber-500 to-red-500" });
      setShowComboName(true);
      
      // Wait for animation
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const newBoard = board.map(row => row.map(cell => cell ? { ...cell } : null));
      const cellsToRemove = new Set();
      let totalTargetFruits = 0;
      let totalIngredients = 0;
      const eliminatedByType = {};
      
      // Mark ALL cells for removal (except gaps)
      for (let r = 0; r < boardSize; r++) {
        for (let c = 0; c < boardSize; c++) {
          if (newBoard[r][c] && !newBoard[r][c]?.isGap) {
            const cell = newBoard[r][c];
            cellsToRemove.add(`${r},${c}`);
            
            // Track eliminated fruits
            if (cell.type === config.targetFruit && !cell.isIngredient) {
              totalTargetFruits++;
            }
            if (cell.isIngredient) {
              totalIngredients++;
            }
            eliminatedByType[cell.type] = (eliminatedByType[cell.type] || 0) + 1;
          }
        }
      }
      
      // Set exploding cells for animation
      setExplodingCells(cellsToRemove);
      setScreenShake(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      setScreenShake(false);
      
      // Clear animation
      setGodSwordAnimation(false);
      
      // Remove all cells (except gaps)
      cellsToRemove.forEach(key => {
        const [r, c] = key.split(',').map(Number);
        if (!newBoard[r][c]?.isGap) {
          newBoard[r][c] = null;
        }
      });
      
      // Track for challenges
      if (Object.keys(eliminatedByType).length > 0) {
        setLastEliminatedFruits(eliminatedByType);
        trackDualFruitsFromEliminated(eliminatedByType);
      }
      
      setExplodingCells(new Set());
      
      // Drop and fill entire board
      const targetFruitForDrop = config?.targetFruit || targetFruit;
      for (let c = 0; c < boardSize; c++) {
        dropAndFillColumn(newBoard, c, fruitSet, targetFruitForDrop, boostTarget, levelId);
      }
      
      setBoard([...newBoard.map(r => [...r])]);
      setFruitsCollected(prev => prev + totalTargetFruits);
      setScore(prev => {
        const newScore = prev + cellsToRemove.size * 50; // 50 points per cell (more than regular sword)
        updateHighScoreIfNeeded(newScore);
        return newScore;
      });
      
      // Update mission progress for ingredients
      if (totalIngredients > 0) {
        setMissionProgress(prev => ({
          ...prev,
          ingredientsDropped: prev.ingredientsDropped + totalIngredients
        }));
      }
      
      // Wait for drop animation
      await new Promise(resolve => setTimeout(resolve, 400));
      
      // Process cascades
      const cascadeResult = await processCascade(
        newBoard,
        null,
        config.targetFruit,
        fruitSet
      );
      
      // Hide combo name
      setTimeout(() => setShowComboName(false), 1500);
      
      setIsAnimating(false);
    } catch (error) {
      console.error('God Sword ability error:', error);
      setIsAnimating(false);
      setGodSwordMode(false);
      setGodSwordAnimation(false);
      setExplodingCells(new Set());
    }
  }, [board, godSwordUsesRemaining, godSwordMode, isAnimating, gameStatus, boardSize, config, targetFruit, fruitSet, processCascade, updateHighScoreIfNeeded, boostTarget, levelId]);

  // Initialize God Sword uses from equipped items (called from GamePage)
  const initializeGodSwordUses = useCallback((usesRemaining) => {
    setGodSwordUsesRemaining(usesRemaining);
  }, []);

  // === MILESTONE ITEM ABILITY FUNCTIONS ===

  // Crystal Ball - Preview next fruits that will fall
  const activateCrystalBall = useCallback(() => {
    if (gameStatus !== 'playing') return false;
    
    // Generate preview of next 3 rows of fruits
    const preview = [];
    for (let i = 0; i < 3; i++) {
      const row = [];
      for (let j = 0; j < boardSize; j++) {
        const randomFruit = fruitSet[Math.floor(Math.random() * fruitSet.length)];
        row.push({ type: randomFruit, emoji: FRUITS[randomFruit]?.emoji });
      }
      preview.push(row);
    }
    
    setCrystalBallPreview(preview);
    setCrystalBallActive(true);
    
    // Auto-deactivate after 10 seconds
    setTimeout(() => {
      setCrystalBallActive(false);
      setCrystalBallPreview([]);
    }, 10000);
    
    return true;
  }, [gameStatus, boardSize, fruitSet]);

  // Moon Stone - Highlight best moves for 30 seconds
  const activateMoonStone = useCallback(() => {
    if (gameStatus !== 'playing') return false;
    
    setMoonStoneActive(true);
    setComboName({ name: "🌙 MOON STONE! 🌙", emoji: "✨", color: "from-indigo-400 to-purple-500" });
    setShowComboName(true);
    setTimeout(() => setShowComboName(false), 1500);
    
    // Clear any existing timeout
    if (moonStoneTimeout) {
      clearTimeout(moonStoneTimeout);
    }
    
    // Auto-deactivate after 30 seconds
    const timeout = setTimeout(() => {
      setMoonStoneActive(false);
      setHintCell(null);
    }, 30000);
    setMoonStoneTimeout(timeout);
    
    return true;
  }, [gameStatus, moonStoneTimeout]);

  // Effect to continuously show best hint while Moon Stone is active
  useEffect(() => {
    if (!moonStoneActive || gameStatus !== 'playing' || isAnimating) return;
    
    // Find the best move and highlight it
    const findAndShowBestMove = () => {
      let bestMove = null;
      let bestScore = 0;
      
      for (let row = 0; row < boardSize; row++) {
        for (let col = 0; col < boardSize; col++) {
          if (board[row]?.[col]?.isGap) continue;
          
          // Check right swap
          if (col < boardSize - 1 && !board[row][col + 1]?.isGap) {
            const testBoard = board.map(r => r.map(c => c ? { ...c } : null));
            [testBoard[row][col], testBoard[row][col + 1]] = [testBoard[row][col + 1], testBoard[row][col]];
            const { matches } = findMatches(testBoard, config.targetFruit, boardSize);
            let score = matches.size;
            matches.forEach(key => {
              const [r, c] = key.split('-').map(Number);
              if (testBoard[r]?.[c]?.type === config.targetFruit) score += 2;
            });
            if (score > bestScore) {
              bestScore = score;
              bestMove = { row, col };
            }
          }
          
          // Check down swap
          if (row < boardSize - 1 && !board[row + 1]?.[col]?.isGap) {
            const testBoard = board.map(r => r.map(c => c ? { ...c } : null));
            [testBoard[row][col], testBoard[row + 1][col]] = [testBoard[row + 1][col], testBoard[row][col]];
            const { matches } = findMatches(testBoard, config.targetFruit, boardSize);
            let score = matches.size;
            matches.forEach(key => {
              const [r, c] = key.split('-').map(Number);
              if (testBoard[r]?.[c]?.type === config.targetFruit) score += 2;
            });
            if (score > bestScore) {
              bestScore = score;
              bestMove = { row, col };
            }
          }
        }
      }
      
      if (bestMove) {
        setHintCell(bestMove);
      }
    };
    
    // Initial call
    findAndShowBestMove();
    
    // Update every 500ms while moonstone is active
    const interval = setInterval(findAndShowBestMove, 500);
    
    return () => clearInterval(interval);
  }, [moonStoneActive, gameStatus, isAnimating, board, boardSize, config.targetFruit]);

  // Thunder Strike - Clear random column + row instantly
  const activateThunderStrike = useCallback(async () => {
    if (gameStatus !== 'playing' || isAnimating) return false;
    
    setIsAnimating(true);
    
    // Pick random row and column
    const randomRow = Math.floor(Math.random() * boardSize);
    const randomCol = Math.floor(Math.random() * boardSize);
    
    // Show animation
    setThunderStrikeAnimation({ row: randomRow, col: randomCol });
    setComboName({ name: "⚡ THUNDER STRIKE! ⚡", emoji: "🌩️", color: "from-yellow-300 via-yellow-500 to-amber-600" });
    setShowComboName(true);
    
    await new Promise(resolve => setTimeout(resolve, 600));
    
    const newBoard = board.map(row => row.map(cell => cell ? { ...cell } : null));
    const cellsToRemove = new Set();
    let targetFruitsEliminated = 0;
    
    // Clear entire row
    for (let c = 0; c < boardSize; c++) {
      if (newBoard[randomRow][c] && !newBoard[randomRow][c]?.isGap) {
        cellsToRemove.add(`${randomRow},${c}`);
        if (newBoard[randomRow][c].type === config.targetFruit) {
          targetFruitsEliminated++;
        }
      }
    }
    
    // Clear entire column
    for (let r = 0; r < boardSize; r++) {
      if (newBoard[r][randomCol] && !newBoard[r][randomCol]?.isGap) {
        cellsToRemove.add(`${r},${randomCol}`);
        if (newBoard[r][randomCol].type === config.targetFruit) {
          targetFruitsEliminated++;
        }
      }
    }
    
    setExplodingCells(cellsToRemove);
    setScreenShake(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    setScreenShake(false);
    setThunderStrikeAnimation(null);
    
    // Remove cells
    cellsToRemove.forEach(key => {
      const [r, c] = key.split(',').map(Number);
      if (!newBoard[r][c]?.isGap) {
        newBoard[r][c] = null;
      }
    });
    
    setExplodingCells(new Set());
    
    // Drop and fill
    for (let c = 0; c < boardSize; c++) {
      dropAndFillColumn(newBoard, c, fruitSet, config.targetFruit, boostTarget, levelId);
    }
    
    setBoard([...newBoard.map(r => [...r])]);
    setFruitsCollected(prev => prev + targetFruitsEliminated);
    setScore(prev => prev + cellsToRemove.size * 25);
    
    await new Promise(resolve => setTimeout(resolve, 300));
    
    setTimeout(() => setShowComboName(false), 1500);
    setIsAnimating(false);
    
    return true;
  }, [gameStatus, isAnimating, board, boardSize, config, fruitSet, boostTarget, levelId]);

  // Trident Shard - Clear 3 random rows with water blast
  const activateTridentShard = useCallback(async () => {
    if (gameStatus !== 'playing' || isAnimating) return false;
    
    setIsAnimating(true);
    
    // Pick 3 random unique rows
    const rows = [];
    while (rows.length < 3) {
      const r = Math.floor(Math.random() * boardSize);
      if (!rows.includes(r)) rows.push(r);
    }
    
    setTridentShardAnimation({ rows });
    setComboName({ name: "🔱 TRIDENT SHARD! 🔱", emoji: "🌊", color: "from-cyan-400 via-blue-500 to-indigo-600" });
    setShowComboName(true);
    
    await new Promise(resolve => setTimeout(resolve, 600));
    
    const newBoard = board.map(row => row.map(cell => cell ? { ...cell } : null));
    const cellsToRemove = new Set();
    let targetFruitsEliminated = 0;
    
    // Clear the 3 rows
    rows.forEach(rowIdx => {
      for (let c = 0; c < boardSize; c++) {
        if (newBoard[rowIdx][c] && !newBoard[rowIdx][c]?.isGap) {
          cellsToRemove.add(`${rowIdx},${c}`);
          if (newBoard[rowIdx][c].type === config.targetFruit) {
            targetFruitsEliminated++;
          }
        }
      }
    });
    
    setExplodingCells(cellsToRemove);
    setScreenShake(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    setScreenShake(false);
    setTridentShardAnimation(null);
    
    cellsToRemove.forEach(key => {
      const [r, c] = key.split(',').map(Number);
      if (!newBoard[r][c]?.isGap) {
        newBoard[r][c] = null;
      }
    });
    
    setExplodingCells(new Set());
    
    for (let c = 0; c < boardSize; c++) {
      dropAndFillColumn(newBoard, c, fruitSet, config.targetFruit, boostTarget, levelId);
    }
    
    setBoard([...newBoard.map(r => [...r])]);
    setFruitsCollected(prev => prev + targetFruitsEliminated);
    setScore(prev => prev + cellsToRemove.size * 20);
    
    await new Promise(resolve => setTimeout(resolve, 300));
    
    setTimeout(() => setShowComboName(false), 1500);
    setIsAnimating(false);
    
    return true;
  }, [gameStatus, isAnimating, board, boardSize, config, fruitSet, boostTarget, levelId]);

  // Frost Crystal - Freeze and shatter 5 random tiles
  const activateFrostCrystal = useCallback(async () => {
    if (gameStatus !== 'playing' || isAnimating) return false;
    
    setIsAnimating(true);
    
    // Pick 5 random tiles
    const tiles = [];
    const attempts = 0;
    while (tiles.length < 5 && attempts < 50) {
      const r = Math.floor(Math.random() * boardSize);
      const c = Math.floor(Math.random() * boardSize);
      const key = `${r},${c}`;
      if (!tiles.includes(key) && board[r]?.[c] && !board[r][c]?.isGap) {
        tiles.push(key);
      }
    }
    
    setFrostCrystalAnimation({ tiles });
    setComboName({ name: "❄️ FROST CRYSTAL! ❄️", emoji: "💎", color: "from-cyan-200 via-blue-400 to-indigo-500" });
    setShowComboName(true);
    
    await new Promise(resolve => setTimeout(resolve, 600));
    
    const newBoard = board.map(row => row.map(cell => cell ? { ...cell } : null));
    const cellsToRemove = new Set(tiles);
    let targetFruitsEliminated = 0;
    
    tiles.forEach(key => {
      const [r, c] = key.split(',').map(Number);
      if (newBoard[r]?.[c]?.type === config.targetFruit) {
        targetFruitsEliminated++;
      }
    });
    
    setExplodingCells(cellsToRemove);
    await new Promise(resolve => setTimeout(resolve, 400));
    setFrostCrystalAnimation(null);
    
    cellsToRemove.forEach(key => {
      const [r, c] = key.split(',').map(Number);
      if (newBoard[r]?.[c] && !newBoard[r][c]?.isGap) {
        newBoard[r][c] = null;
      }
    });
    
    setExplodingCells(new Set());
    
    for (let c = 0; c < boardSize; c++) {
      dropAndFillColumn(newBoard, c, fruitSet, config.targetFruit, boostTarget, levelId);
    }
    
    setBoard([...newBoard.map(r => [...r])]);
    setFruitsCollected(prev => prev + targetFruitsEliminated);
    setScore(prev => prev + tiles.length * 30);
    
    await new Promise(resolve => setTimeout(resolve, 300));
    
    setTimeout(() => setShowComboName(false), 1500);
    setIsAnimating(false);
    
    return true;
  }, [gameStatus, isAnimating, board, boardSize, config, fruitSet, boostTarget, levelId]);

  // Dragon Scale - Activate 50% boss damage bonus
  const activateDragonScale = useCallback(() => {
    if (gameStatus !== 'playing') return false;
    setDragonScaleActive(true);
    return true;
  }, [gameStatus]);

  // Ocean Pearl - Activate bubble shield
  const activateOceanPearl = useCallback(() => {
    if (gameStatus !== 'playing') return false;
    setOceanPearlActive(true);
    setComboName({ name: "🌊 OCEAN PEARL! 🌊", emoji: "🫧", color: "from-cyan-400 to-blue-500" });
    setShowComboName(true);
    setTimeout(() => setShowComboName(false), 1500);
    // Auto-deactivate after blocking one obstacle or level end
    return true;
  }, [gameStatus]);
  
  // Deactivate Ocean Pearl (called when it blocks an effect)
  const deactivateOceanPearl = useCallback(() => {
    setOceanPearlActive(false);
    setComboName({ name: "🫧 EFFECT BLOCKED! 🫧", emoji: "✨", color: "from-cyan-300 to-teal-400" });
    setShowComboName(true);
    setTimeout(() => setShowComboName(false), 1500);
  }, []);

  // Aurora Essence - Rainbow cascade clearing effect
  const activateAuroraEssence = useCallback(async () => {
    if (gameStatus !== 'playing' || isAnimating) return false;
    
    setIsAnimating(true);
    setAuroraEssenceActive(true);
    setComboName({ name: "🌌 AURORA ESSENCE! 🌌", emoji: "🌈", color: "from-green-400 via-purple-500 to-pink-500" });
    setShowComboName(true);
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Clear all fruits of a random color (most common on board)
    const colorCounts = {};
    board.forEach(row => {
      row.forEach(cell => {
        if (cell && !cell.isGap && cell.type) {
          colorCounts[cell.type] = (colorCounts[cell.type] || 0) + 1;
        }
      });
    });
    
    // Find most common fruit
    let maxColor = null;
    let maxCount = 0;
    Object.entries(colorCounts).forEach(([color, count]) => {
      if (count > maxCount) {
        maxColor = color;
        maxCount = count;
      }
    });
    
    if (!maxColor) {
      setIsAnimating(false);
      setAuroraEssenceActive(false);
      return false;
    }
    
    const newBoard = board.map(row => row.map(cell => cell ? { ...cell } : null));
    const cellsToRemove = new Set();
    let targetFruitsEliminated = 0;
    
    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        if (newBoard[r][c]?.type === maxColor && !newBoard[r][c]?.isGap) {
          cellsToRemove.add(`${r},${c}`);
          if (maxColor === config.targetFruit) {
            targetFruitsEliminated++;
          }
        }
      }
    }
    
    setExplodingCells(cellsToRemove);
    setScreenShake(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    setScreenShake(false);
    
    cellsToRemove.forEach(key => {
      const [r, c] = key.split(',').map(Number);
      newBoard[r][c] = null;
    });
    
    setExplodingCells(new Set());
    setAuroraEssenceActive(false);
    
    for (let c = 0; c < boardSize; c++) {
      dropAndFillColumn(newBoard, c, fruitSet, config.targetFruit, boostTarget, levelId);
    }
    
    setBoard([...newBoard.map(r => [...r])]);
    setFruitsCollected(prev => prev + targetFruitsEliminated);
    setScore(prev => prev + cellsToRemove.size * 25);
    
    await new Promise(resolve => setTimeout(resolve, 300));
    
    setTimeout(() => setShowComboName(false), 1500);
    setIsAnimating(false);
    
    return true;
  }, [gameStatus, isAnimating, board, boardSize, config, fruitSet, boostTarget, levelId]);

  const resetGame = useCallback(() => {
    setBoard(createBoard(boardSize, fruitSet, targetFruit, levelId, config));
    setSelectedCell(null);
    setScore(0);
    // Apply startMoves upgrade (+2 moves at level start)
    const bonusMoves = activeUpgrades.startMoves || 0;
    setMovesLeft(config.moves + bonusMoves);
    setFruitsCollected(0);
    setDualFruitsCollected({ fruit1: 0, fruit2: 0 });
    setGameStatus('playing');
    setIsAnimating(false);
    setLastMatchInfo(null);
    setExplodingCells(new Set());
    setClearEffect(null);
    setConsecutive4Combos(0);
    setDoublePointsActive(false);
    setShowDoublePointsAlert(false);
    setInstaEliminationActive(false);
    setShowInstaEliminationPicker(false);
    setSwappingCells(null);
    // Reset sword uses based on upgrade
    setSwordUsesRemaining(calculatedMaxSwordUses);
    setMaxSwordUses(calculatedMaxSwordUses);
    setSwordAbilityUsed(false);
    setSwordMode(false);
    // Reset God Sword mode (uses are NOT reset here - they persist across levels)
    setGodSwordMode(false);
    setGodSwordAnimation(false);
    setBombMode(false);
    setColorBombMode(false);
    setHintCell(null);
  }, [config, boardSize, fruitSet, targetFruit, levelId, calculatedMaxSwordUses]);

  // === CONSUMABLE FUNCTIONS ===

  // Add bonus moves from consumable
  const addBonusMoves = useCallback((amount) => {
    if (gameStatus !== 'playing') return false;
    setMovesLeft(prev => prev + amount);
    return true;
  }, [gameStatus]);

  // Trigger manual shuffle from consumable
  const triggerShuffle = useCallback(() => {
    if (gameStatus !== 'playing' || isAnimating) return false;
    const newBoard = shuffleBoard(board);
    setBoard(newBoard);
    return true;
  }, [gameStatus, isAnimating, board]);

  // ==================== MILESTONE ITEM ABILITIES ====================
  
  // Fruit Magnet - Pull matching fruits together to create guaranteed matches
  const activateFruitMagnet = useCallback(async () => {
    if (gameStatus !== 'playing' || isAnimating) return false;
    
    setIsAnimating(true);
    setComboName({ name: "🧲 FRUIT MAGNET! 🧲", emoji: "🧲", color: "from-red-500 to-orange-500" });
    setShowComboName(true);
    
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const newBoard = board.map(row => row.map(cell => cell ? { ...cell } : null));
    const targetType = config.targetFruit; // Focus on target fruit
    
    // Find all target fruits and normal cells
    const targetFruits = [];
    const normalCells = [];
    
    for (let row = 0; row < newBoard.length; row++) {
      for (let col = 0; col < newBoard[row].length; col++) {
        const cell = newBoard[row][col];
        if (cell && !cell.isGap && !cell.isIngredient && cell.type) {
          if (cell.type === targetType) {
            targetFruits.push({ row, col, cell: { ...cell } });
          } else if (cell.obstacle === OBSTACLE_TYPES.NONE) {
            normalCells.push({ row, col, cell: { ...cell } });
          }
        }
      }
    }
    
    // Create a horizontal match by arranging 3-5 target fruits in a row
    if (targetFruits.length >= 3 && normalCells.length >= 3) {
      // Find a valid row for the match
      for (let tryRow = 0; tryRow < newBoard.length; tryRow++) {
        let validStartCol = -1;
        // Find 3 consecutive non-gap cells in this row
        for (let col = 0; col <= newBoard[0].length - 3; col++) {
          let valid = true;
          for (let i = 0; i < 3; i++) {
            const cell = newBoard[tryRow][col + i];
            if (!cell || cell.isGap || cell.isIngredient || cell.obstacle !== OBSTACLE_TYPES.NONE) {
              valid = false;
              break;
            }
          }
          if (valid) {
            validStartCol = col;
            break;
          }
        }
        
        if (validStartCol >= 0) {
          // Swap target fruits into the match row
          const fruitsToPlace = Math.min(3, targetFruits.length);
          for (let i = 0; i < fruitsToPlace; i++) {
            const targetPos = targetFruits[i];
            const destRow = tryRow;
            const destCol = validStartCol + i;
            
            // Skip if already in position
            if (targetPos.row === destRow && targetPos.col === destCol) continue;
            
            // Swap positions
            const destCell = newBoard[destRow][destCol];
            newBoard[destRow][destCol] = { ...targetPos.cell, id: 'magnet-' + Math.random().toString(36).substr(2, 9) };
            newBoard[targetPos.row][targetPos.col] = destCell;
          }
          break;
        }
      }
    }
    
    setBoard(newBoard);
    
    await new Promise(resolve => setTimeout(resolve, 500));
    setTimeout(() => setShowComboName(false), 1500);
    setIsAnimating(false);
    return true;
  }, [gameStatus, isAnimating, board, config.targetFruit]);

  // Tsunami Wave - Clear bottom 2 rows with visual effect and proper gravity
  const activateTsunamiWave = useCallback(async () => {
    if (gameStatus !== 'playing' || isAnimating) return false;
    
    setIsAnimating(true);
    setComboName({ name: "🌊 TSUNAMI WAVE! 🌊", emoji: "🌊", color: "from-blue-500 to-cyan-500" });
    setShowComboName(true);
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newBoard = board.map(row => row.map(cell => cell ? { ...cell } : null));
    let pointsEarned = 0;
    let targetFruitsCleared = 0;
    const cellsToExplode = new Set();
    
    // Mark bottom 2 rows for explosion animation
    const rowsToClear = [newBoard.length - 1, newBoard.length - 2];
    rowsToClear.forEach(rowIdx => {
      if (newBoard[rowIdx]) {
        newBoard[rowIdx].forEach((cell, colIdx) => {
          if (cell && !cell.isGap) {
            cellsToExplode.add(`${rowIdx},${colIdx}`);
            if (cell.type === config.targetFruit) {
              targetFruitsCleared++;
            }
          }
        });
      }
    });
    
    // Show explosion animation
    setExplodingCells(cellsToExplode);
    await new Promise(resolve => setTimeout(resolve, 400));
    
    // Clear the cells
    rowsToClear.forEach(rowIdx => {
      if (newBoard[rowIdx]) {
        newBoard[rowIdx] = newBoard[rowIdx].map(cell => {
          if (cell && !cell.isGap) {
            pointsEarned += 50;
            return null;
          }
          return cell;
        });
      }
    });
    
    setExplodingCells(new Set());
    
    // Apply gravity and fill new fruits
    const boostTarget = levelId >= 11;
    for (let col = 0; col < boardSize; col++) {
      dropAndFillColumn(newBoard, col, fruitSet, config.targetFruit, boostTarget, levelId);
    }
    
    setBoard([...newBoard.map(r => [...r])]);
    setScore(prev => prev + pointsEarned);
    setFruitsCollected(prev => prev + targetFruitsCleared);
    
    setTimeout(() => setShowComboName(false), 1500);
    setIsAnimating(false);
    
    return true;
  }, [gameStatus, isAnimating, board, config.targetFruit, boardSize, fruitSet, levelId]);

  // Guardian Shield - Block next damage/negative effect
  const [guardianShieldActive, setGuardianShieldActive] = useState(false);
  
  const activateGuardianShield = useCallback(() => {
    if (gameStatus !== 'playing') return false;
    
    setGuardianShieldActive(true);
    setComboName({ name: "🛡️ SHIELD ACTIVE! 🛡️", emoji: "🛡️", color: "from-yellow-500 to-amber-500" });
    setShowComboName(true);
    setTimeout(() => setShowComboName(false), 1500);
    return true;
  }, [gameStatus]);
  
  // Deactivate Guardian Shield (called when shield blocks an attack)
  const deactivateGuardianShield = useCallback(() => {
    setGuardianShieldActive(false);
    setComboName({ name: "🛡️ SHIELD BLOCKED! 🛡️", emoji: "✨", color: "from-green-400 to-emerald-500" });
    setShowComboName(true);
    setTimeout(() => setShowComboName(false), 1500);
  }, []);

  // Eye of Ra - Reveal and highlight the BEST possible match
  const activateEyeOfRa = useCallback(() => {
    if (gameStatus !== 'playing') return false;
    
    setComboName({ name: "👁️ EYE OF RA! 👁️", emoji: "👁️", color: "from-yellow-400 to-orange-500" });
    setShowComboName(true);
    
    // Find the best match on the board
    const findBestMove = () => {
      let bestMove = null;
      let bestScore = 0;
      
      for (let row = 0; row < board.length; row++) {
        for (let col = 0; col < board[row].length; col++) {
          const cell = board[row][col];
          if (!cell || cell.isGap) continue;
          
          // Check swap right
          if (col < board[row].length - 1) {
            const rightCell = board[row][col + 1];
            if (rightCell && !rightCell.isGap) {
              // Simulate swap
              const testBoard = board.map(r => r.map(c => c ? { ...c } : null));
              [testBoard[row][col], testBoard[row][col + 1]] = [testBoard[row][col + 1], testBoard[row][col]];
              const { matches } = findMatches(testBoard);
              const matchScore = matches.size + (cell.type === config.targetFruit ? 5 : 0);
              if (matchScore > bestScore) {
                bestScore = matchScore;
                bestMove = { row, col };
              }
            }
          }
          
          // Check swap down
          if (row < board.length - 1) {
            const downCell = board[row + 1][col];
            if (downCell && !downCell.isGap) {
              const testBoard = board.map(r => r.map(c => c ? { ...c } : null));
              [testBoard[row][col], testBoard[row + 1][col]] = [testBoard[row + 1][col], testBoard[row][col]];
              const { matches } = findMatches(testBoard);
              const matchScore = matches.size + (cell.type === config.targetFruit ? 5 : 0);
              if (matchScore > bestScore) {
                bestScore = matchScore;
                bestMove = { row, col };
              }
            }
          }
        }
      }
      
      return bestMove;
    };
    
    // Find and show the best move
    const bestMove = findBestMove();
    if (bestMove) {
      setHintCell(bestMove);
      // Keep it visible longer and flash
      let flashCount = 0;
      const flashInterval = setInterval(() => {
        flashCount++;
        if (flashCount % 2 === 0) {
          setHintCell(bestMove);
        } else {
          setHintCell(null);
        }
        if (flashCount >= 10) {
          clearInterval(flashInterval);
          setHintCell(null);
        }
      }, 400);
    }
    
    setTimeout(() => setShowComboName(false), 2000);
    return true;
  }, [gameStatus, board, config.targetFruit]);

  // Yeti Fur - Immune to freeze effects for 5 turns
  const [yetiFurTurns, setYetiFurTurns] = useState(0);
  
  const activateYetiFur = useCallback(() => {
    if (gameStatus !== 'playing') return false;
    
    setYetiFurTurns(5);
    setComboName({ name: "🦣 YETI FUR ACTIVE! 🦣", emoji: "🦣", color: "from-blue-300 to-white" });
    setShowComboName(true);
    setTimeout(() => setShowComboName(false), 1500);
    return true;
  }, [gameStatus]);

  // Snow Globe - Freeze all obstacles for 3 turns
  const [snowGlobeTurns, setSnowGlobeTurns] = useState(0);
  
  const activateSnowGlobe = useCallback(() => {
    if (gameStatus !== 'playing') return false;
    
    setSnowGlobeTurns(3);
    setComboName({ name: "🔮 OBSTACLES FROZEN! 🔮", emoji: "❄️", color: "from-cyan-400 to-blue-500" });
    setShowComboName(true);
    setTimeout(() => setShowComboName(false), 1500);
    return true;
  }, [gameStatus]);

  // Ice Pick - Break any frozen tile instantly (enters targeting mode)
  const [icePickMode, setIcePickMode] = useState(false);
  
  const activateIcePick = useCallback(() => {
    if (gameStatus !== 'playing') return false;
    
    setIcePickMode(true);
    setComboName({ name: "⛏️ SELECT FROZEN TILE! ⛏️", emoji: "⛏️", color: "from-gray-400 to-blue-400" });
    setShowComboName(true);
    setTimeout(() => setShowComboName(false), 2000);
    return true;
  }, [gameStatus]);

  // Pharaoh's Scarab - Remove all curse tiles
  const activatePharaohScarab = useCallback(async () => {
    if (gameStatus !== 'playing' || isAnimating) return false;
    
    setIsAnimating(true);
    setComboName({ name: "🪲 CURSES REMOVED! 🪲", emoji: "🪲", color: "from-yellow-600 to-amber-700" });
    setShowComboName(true);
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newBoard = board.map(row => row.map(cell => {
      if (cell && cell.obstacle === 'CURSE_TILE') {
        return { ...cell, obstacle: null };
      }
      return cell ? { ...cell } : null;
    }));
    
    setBoard(newBoard);
    setTimeout(() => setShowComboName(false), 1500);
    setIsAnimating(false);
    return true;
  }, [gameStatus, isAnimating, board]);

  // Ankh of Life - Continue level after losing (sets a flag)
  const [ankhOfLifeActive, setAnkhOfLifeActive] = useState(false);
  
  const activateAnkhOfLife = useCallback(() => {
    if (gameStatus !== 'playing') return false;
    
    setAnkhOfLifeActive(true);
    setComboName({ name: "☥ SECOND CHANCE READY! ☥", emoji: "☥", color: "from-yellow-400 to-green-500" });
    setShowComboName(true);
    setTimeout(() => setShowComboName(false), 1500);
    return true;
  }, [gameStatus]);

  // ===== VIP ITEM ABILITIES =====
  
  // Add extra moves (Ruby Ring)
  const addMoves = useCallback((count) => {
    if (gameStatus !== 'playing') return false;
    setMovesLeft(prev => prev + count);
    setComboName({ name: `💍 +${count} MOVES! 💍`, emoji: "💍", color: "from-red-500 to-rose-600" });
    setShowComboName(true);
    setTimeout(() => setShowComboName(false), 1500);
    return true;
  }, [gameStatus]);
  
  // Trigger board shuffle (Sapphire Orb)
  const triggerVIPShuffle = useCallback(() => {
    if (gameStatus !== 'playing' || isAnimating) return false;
    
    setComboName({ name: "🔮 OPTIMAL SHUFFLE! 🔮", emoji: "🔮", color: "from-blue-500 to-indigo-600" });
    setShowComboName(true);
    
    // Use existing shuffle logic
    const newBoard = shuffleBoard(board);
    setBoard(newBoard);
    
    setTimeout(() => setShowComboName(false), 1500);
    return true;
  }, [gameStatus, isAnimating, board]);
  
  // Spawn random special fruits (Opal Orb)
  const spawnRandomSpecials = useCallback((count) => {
    if (gameStatus !== 'playing' || isAnimating) return false;
    
    setIsAnimating(true);
    setComboName({ name: `🌙 ${count} SPECIALS! 🌙`, emoji: "🌙", color: "from-pink-400 to-purple-500" });
    setShowComboName(true);
    
    const specialTypes = [SPECIAL_TYPES.STRIPED_H, SPECIAL_TYPES.STRIPED_V, SPECIAL_TYPES.WRAPPED];
    const newBoard = board.map(row => row.map(cell => cell ? { ...cell } : null));
    
    // Find valid cells (non-gap, non-special)
    const validCells = [];
    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        const cell = newBoard[r][c];
        if (cell && !cell.isGap && cell.special === SPECIAL_TYPES.NORMAL) {
          validCells.push({ r, c });
        }
      }
    }
    
    // Shuffle and pick random cells
    const shuffled = validCells.sort(() => Math.random() - 0.5);
    const toConvert = shuffled.slice(0, Math.min(count, shuffled.length));
    
    toConvert.forEach(({ r, c }) => {
      const randomSpecial = specialTypes[Math.floor(Math.random() * specialTypes.length)];
      newBoard[r][c] = { ...newBoard[r][c], special: randomSpecial };
    });
    
    setBoard(newBoard);
    setTimeout(() => {
      setShowComboName(false);
      setIsAnimating(false);
    }, 1000);
    return true;
  }, [gameStatus, isAnimating, board, boardSize]);
  
  // Clear all obstacles of a specific type (Pearl Pendant)
  const clearObstacleType = useCallback(async (obstacleType) => {
    if (gameStatus !== 'playing' || isAnimating) return false;
    
    setIsAnimating(true);
    setComboName({ name: `📿 OBSTACLES CLEARED! 📿`, emoji: "📿", color: "from-slate-300 to-gray-400" });
    setShowComboName(true);
    
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const newBoard = board.map(row => row.map(cell => {
      if (cell && cell.obstacle === obstacleType) {
        return { ...cell, obstacle: null, iceLevel: 0 };
      }
      return cell ? { ...cell } : null;
    }));
    
    setBoard(newBoard);
    setTimeout(() => {
      setShowComboName(false);
      setIsAnimating(false);
    }, 1000);
    return true;
  }, [gameStatus, isAnimating, board]);
  
  // Cross blast - clear row and column (Royal Scepter)
  const executeCrossBlast = useCallback(async (centerRow, centerCol) => {
    if (isAnimating || gameStatus !== 'playing') return false;
    
    setIsAnimating(true);
    setComboName({ name: "👑 ROYAL CROSS BLAST! 👑", emoji: "👑", color: "from-yellow-500 to-amber-600" });
    setShowComboName(true);
    
    const newBoard = board.map(row => row.map(cell => cell ? { ...cell } : null));
    const cellsToRemove = new Set();
    let pointsEarned = 0;
    
    // Clear entire row
    for (let c = 0; c < boardSize; c++) {
      if (newBoard[centerRow] && newBoard[centerRow][c] && !newBoard[centerRow][c]?.isGap) {
        cellsToRemove.add(`${centerRow},${c}`);
        pointsEarned += 10;
      }
    }
    
    // Clear entire column
    for (let r = 0; r < boardSize; r++) {
      if (newBoard[r] && newBoard[r][centerCol] && !newBoard[r][centerCol]?.isGap) {
        cellsToRemove.add(`${r},${centerCol}`);
        pointsEarned += 10;
      }
    }
    
    // Visual effect
    setClearEffect({ type: 'cross', row: centerRow, col: centerCol });
    setExplodingCells(cellsToRemove);
    await new Promise(resolve => setTimeout(resolve, 400));
    
    // Remove cells
    cellsToRemove.forEach(key => {
      const [r, c] = key.split(',').map(Number);
      if (newBoard[r] && newBoard[r][c] && !newBoard[r][c]?.isGap) {
        newBoard[r][c] = null;
      }
    });
    
    setScore(prev => prev + pointsEarned);
    setExplodingCells(new Set());
    setClearEffect(null);
    
    // Apply gravity and refill using dropAndFillColumn
    await new Promise(resolve => setTimeout(resolve, 100));
    const boostTarget = levelId >= 11;
    for (let c = 0; c < boardSize; c++) {
      dropAndFillColumn(newBoard, c, fruitSet, config.targetFruit, boostTarget, levelId);
    }
    setBoard(newBoard);
    
    setTimeout(() => {
      setShowComboName(false);
      setIsAnimating(false);
    }, 500);
    return true;
  }, [gameStatus, isAnimating, board, boardSize, fruitSet, config, levelId]);

  // Activate bomb mode (3x3 destruction)
  const activateBombMode = useCallback(() => {
    if (gameStatus !== 'playing' || isAnimating) return false;
    setBombMode(true);
    setColorBombMode(false);
    setSwordMode(false);
    return true;
  }, [gameStatus, isAnimating]);

  // Cancel bomb mode
  const cancelBombMode = useCallback(() => {
    setBombMode(false);
  }, []);

  // Execute bomb at position - renamed from useBomb to avoid React hooks rule
  const executeBomb = useCallback(async (centerRow, centerCol) => {
    if (!bombMode || isAnimating || gameStatus !== 'playing') return false;
    
    // Validate row and col are valid numbers
    if (typeof centerRow !== 'number' || typeof centerCol !== 'number') return false;
    if (centerRow < 0 || centerRow >= boardSize || centerCol < 0 || centerCol >= boardSize) return false;
    if (!board[centerRow] || !board[centerRow][centerCol]) return false;
    if (board[centerRow][centerCol]?.isGap) return false;

    setIsAnimating(true);
    setBombMode(false);

    const newBoard = board.map(row => row.map(cell => cell ? { ...cell } : null));
    const cellsToRemove = new Set();
    let targetFruitsEliminated = 0;
    const eliminatedByType = {};

    // 3x3 area
    for (let r = centerRow - 1; r <= centerRow + 1; r++) {
      for (let c = centerCol - 1; c <= centerCol + 1; c++) {
        if (r >= 0 && r < boardSize && c >= 0 && c < boardSize && newBoard[r][c] && !newBoard[r][c]?.isGap) {
          const cell = newBoard[r][c];
          cellsToRemove.add(`${r},${c}`);
          if (cell.type === config.targetFruit) {
            targetFruitsEliminated++;
          }
          eliminatedByType[cell.type] = (eliminatedByType[cell.type] || 0) + 1;
        }
      }
    }

    setExplodingCells(cellsToRemove);
    await new Promise(resolve => setTimeout(resolve, 300));

    cellsToRemove.forEach(key => {
      const [r, c] = key.split(',').map(Number);
      if (!newBoard[r][c]?.isGap) {
        newBoard[r][c] = null;
      }
    });

    // Update stats
    setFruitsCollected(prev => prev + targetFruitsEliminated);
    setScore(prev => prev + cellsToRemove.size * 15);
    setLastEliminatedFruits(eliminatedByType);
    trackDualFruitsFromEliminated(eliminatedByType);

    setExplodingCells(new Set());
    await new Promise(resolve => setTimeout(resolve, 100));

    // Drop fruits and refill using the correct function
    const boostTarget = levelId >= 11;
    for (let c = 0; c < boardSize; c++) {
      dropAndFillColumn(newBoard, c, fruitSet, config.targetFruit, boostTarget, levelId);
    }
    setBoard([...newBoard.map(r => [...r])]);
    
    // Wait for drop animation
    await new Promise(resolve => setTimeout(resolve, 300));

    setIsAnimating(false);
    return true;
  }, [bombMode, isAnimating, gameStatus, board, boardSize, config.targetFruit, fruitSet, levelId]);

  // Activate color bomb mode
  const activateColorBombMode = useCallback(() => {
    if (gameStatus !== 'playing' || isAnimating) return false;
    setColorBombMode(true);
    setBombMode(false);
    setSwordMode(false);
    return true;
  }, [gameStatus, isAnimating]);

  // Cancel color bomb mode
  const cancelColorBombMode = useCallback(() => {
    setColorBombMode(false);
  }, []);

  // Execute color bomb on a fruit type - renamed from useColorBomb
  const executeColorBomb = useCallback(async (row, col) => {
    if (!colorBombMode || isAnimating || gameStatus !== 'playing') return false;
    
    // Validate row and col are valid numbers
    if (typeof row !== 'number' || typeof col !== 'number') return false;
    if (row < 0 || row >= boardSize || col < 0 || col >= boardSize) return false;
    if (!board[row] || !board[row][col]) return false;
    if (board[row][col]?.isGap) return false;

    const targetType = board[row][col].type;
    setIsAnimating(true);
    setColorBombMode(false);

    const newBoard = board.map(row => row.map(cell => cell ? { ...cell } : null));
    const cellsToRemove = new Set();
    let targetFruitsEliminated = 0;
    const eliminatedByType = {};

    // Find all fruits of this type
    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        if (newBoard[r][c] && !newBoard[r][c].isGap && newBoard[r][c].type === targetType) {
          cellsToRemove.add(`${r},${c}`);
          if (newBoard[r][c].type === config.targetFruit) {
            targetFruitsEliminated++;
          }
          eliminatedByType[newBoard[r][c].type] = (eliminatedByType[newBoard[r][c].type] || 0) + 1;
        }
      }
    }

    setExplodingCells(cellsToRemove);
    await new Promise(resolve => setTimeout(resolve, 400));

    cellsToRemove.forEach(key => {
      const [r, c] = key.split(',').map(Number);
      newBoard[r][c] = null;
    });

    setFruitsCollected(prev => prev + targetFruitsEliminated);
    setScore(prev => prev + cellsToRemove.size * 20);
    setLastEliminatedFruits(eliminatedByType);
    trackDualFruitsFromEliminated(eliminatedByType);

    setExplodingCells(new Set());
    await new Promise(resolve => setTimeout(resolve, 100));

    // Drop fruits and refill using the correct function
    const boostTarget = levelId >= 11;
    for (let c = 0; c < boardSize; c++) {
      dropAndFillColumn(newBoard, c, fruitSet, config.targetFruit, boostTarget, levelId);
    }
    setBoard([...newBoard.map(r => [...r])]);
    
    // Wait for drop animation
    await new Promise(resolve => setTimeout(resolve, 300));

    setIsAnimating(false);
    return true;
  }, [colorBombMode, isAnimating, gameStatus, board, boardSize, config.targetFruit, fruitSet, levelId]);

  // Show hint (find a valid move)
  const showHint = useCallback(() => {
    if (gameStatus !== 'playing' || isAnimating) return false;

    // If betterHints upgrade is active, find the BEST move instead of just any valid move
    const hasBetterHints = activeUpgrades.betterHints || false;
    
    if (hasBetterHints) {
      // Find the best scoring move
      let bestMove = null;
      let bestScore = 0;
      
      for (let row = 0; row < boardSize; row++) {
        for (let col = 0; col < boardSize; col++) {
          if (board[row][col]?.isGap) continue;
          
          // Check right swap
          if (col < boardSize - 1 && !board[row][col + 1]?.isGap) {
            const testBoard = board.map(r => r.map(c => c ? { ...c } : null));
            [testBoard[row][col], testBoard[row][col + 1]] = [testBoard[row][col + 1], testBoard[row][col]];
            const { matches } = findMatches(testBoard, config.targetFruit, boardSize);
            // Score: match count + bonus for target fruit
            let score = matches.size;
            matches.forEach(key => {
              const [r, c] = key.split('-').map(Number);
              if (testBoard[r]?.[c]?.type === config.targetFruit) score += 2;
            });
            if (score > bestScore) {
              bestScore = score;
              bestMove = { row, col };
            }
          }
          
          // Check down swap
          if (row < boardSize - 1 && !board[row + 1][col]?.isGap) {
            const testBoard = board.map(r => r.map(c => c ? { ...c } : null));
            [testBoard[row][col], testBoard[row + 1][col]] = [testBoard[row + 1][col], testBoard[row][col]];
            const { matches } = findMatches(testBoard, config.targetFruit, boardSize);
            let score = matches.size;
            matches.forEach(key => {
              const [r, c] = key.split('-').map(Number);
              if (testBoard[r]?.[c]?.type === config.targetFruit) score += 2;
            });
            if (score > bestScore) {
              bestScore = score;
              bestMove = { row, col };
            }
          }
        }
      }
      
      if (bestMove) {
        setHintCell(bestMove);
        setTimeout(() => setHintCell(null), 3000); // Longer duration for better hint
        return true;
      }
    }

    // Standard hint: find ANY valid move
    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize; col++) {
        if (board[row][col]?.isGap) continue;

        // Check right swap
        if (col < boardSize - 1 && !board[row][col + 1]?.isGap) {
          const testBoard = board.map(r => r.map(c => c ? { ...c } : null));
          [testBoard[row][col], testBoard[row][col + 1]] = [testBoard[row][col + 1], testBoard[row][col]];
          if (findMatches(testBoard, config.targetFruit, boardSize).size > 0) {
            setHintCell({ row, col });
            setTimeout(() => setHintCell(null), 2000);
            return true;
          }
        }

        // Check down swap
        if (row < boardSize - 1 && !board[row + 1][col]?.isGap) {
          const testBoard = board.map(r => r.map(c => c ? { ...c } : null));
          [testBoard[row][col], testBoard[row + 1][col]] = [testBoard[row + 1][col], testBoard[row][col]];
          if (findMatches(testBoard, config.targetFruit, boardSize).size > 0) {
            setHintCell({ row, col });
            setTimeout(() => setHintCell(null), 2000);
            return true;
          }
        }
      }
    }

    return false;
  }, [gameStatus, isAnimating, board, boardSize, config.targetFruit, activeUpgrades.betterHints]);

  // Activate 2X XP consumable for 20 turns
  const activateDoubleXP = useCallback(() => {
    if (computedGameStatus !== 'playing') return false;
    setDoubleXPActive(true);
    setDoubleXPTurnsLeft(20);
    return true;
  }, [computedGameStatus]);

  // Decrement 2X XP turns when a move is made (called from handleSwipe/handleCellClick)
  // This is handled in the move processing

  // === WRAPPED FRUIT PLACEMENT MODE ===
  const [wrappedMode, setWrappedMode] = useState(false);
  
  const activateWrappedMode = useCallback(() => {
    if (gameStatus !== 'playing' || isAnimating) return false;
    setWrappedMode(true);
    setBombMode(false);
    setColorBombMode(false);
    setSwordMode(false);
    return true;
  }, [gameStatus, isAnimating]);

  const cancelWrappedMode = useCallback(() => {
    setWrappedMode(false);
  }, []);

  const executeWrappedPlacement = useCallback((row, col) => {
    if (!wrappedMode || isAnimating || gameStatus !== 'playing') return false;
    
    // Validate position
    if (typeof row !== 'number' || typeof col !== 'number') return false;
    if (row < 0 || row >= boardSize || col < 0 || col >= boardSize) return false;
    if (!board[row] || !board[row][col]) return false;
    if (board[row][col]?.isGap || board[row][col]?.isIngredient) return false;
    
    // Place wrapped fruit at position
    const newBoard = board.map(r => r.map(cell => cell ? { ...cell } : null));
    const originalType = newBoard[row][col].type;
    
    newBoard[row][col] = {
      ...newBoard[row][col],
      special: SPECIAL_TYPES.WRAPPED,
      type: originalType // Keep the original fruit type
    };
    
    setBoard([...newBoard.map(r => [...r])]);
    setWrappedMode(false);
    return true;
  }, [wrappedMode, isAnimating, gameStatus, board, boardSize]);

  // === DISCO FRUIT PLACEMENT MODE ===
  const [discoMode, setDiscoMode] = useState(false);
  
  const activateDiscoMode = useCallback(() => {
    if (gameStatus !== 'playing' || isAnimating) return false;
    setDiscoMode(true);
    setBombMode(false);
    setColorBombMode(false);
    setSwordMode(false);
    setWrappedMode(false);
    return true;
  }, [gameStatus, isAnimating]);

  const cancelDiscoMode = useCallback(() => {
    setDiscoMode(false);
  }, []);

  const executeDiscoPlacement = useCallback((row, col) => {
    if (!discoMode || isAnimating || gameStatus !== 'playing') return false;
    
    // Validate position
    if (typeof row !== 'number' || typeof col !== 'number') return false;
    if (row < 0 || row >= boardSize || col < 0 || col >= boardSize) return false;
    if (!board[row] || !board[row][col]) return false;
    if (board[row][col]?.isGap || board[row][col]?.isIngredient) return false;
    
    // Place disco fruit at position
    const newBoard = board.map(r => r.map(cell => cell ? { ...cell } : null));
    
    newBoard[row][col] = {
      ...newBoard[row][col],
      special: SPECIAL_TYPES.DISCO,
      type: 'disco' // Disco fruit has its own type
    };
    
    setBoard([...newBoard.map(r => [...r])]);
    setDiscoMode(false);
    return true;
  }, [discoMode, isAnimating, gameStatus, board, boardSize]);

  // === STRIPED FRUIT PLACEMENT MODE ===
  const [stripedMode, setStripedMode] = useState(false);
  
  const activateStripedMode = useCallback(() => {
    if (gameStatus !== 'playing' || isAnimating) return false;
    setStripedMode(true);
    setBombMode(false);
    setColorBombMode(false);
    setSwordMode(false);
    setWrappedMode(false);
    setDiscoMode(false);
    return true;
  }, [gameStatus, isAnimating]);

  const cancelStripedMode = useCallback(() => {
    setStripedMode(false);
  }, []);

  const executeStripedPlacement = useCallback((row, col) => {
    if (!stripedMode || isAnimating || gameStatus !== 'playing') return false;
    
    // Validate position
    if (typeof row !== 'number' || typeof col !== 'number') return false;
    if (row < 0 || row >= boardSize || col < 0 || col >= boardSize) return false;
    if (!board[row] || !board[row][col]) return false;
    if (board[row][col]?.isGap || board[row][col]?.isIngredient) return false;
    
    // Place striped fruit at position (random horizontal or vertical)
    const newBoard = board.map(r => r.map(cell => cell ? { ...cell } : null));
    const originalType = newBoard[row][col].type;
    const stripedType = Math.random() < 0.5 ? SPECIAL_TYPES.STRIPED_H : SPECIAL_TYPES.STRIPED_V;
    
    newBoard[row][col] = {
      ...newBoard[row][col],
      special: stripedType,
      type: originalType // Keep the original fruit type
    };
    
    setBoard([...newBoard.map(r => [...r])]);
    setStripedMode(false);
    return true;
  }, [stripedMode, isAnimating, gameStatus, board, boardSize]);

  return {
    board,
    selectedCell,
    score,
    movesLeft,
    fruitsCollected,
    dualFruitsCollected,
    gameStatus,  // Direct state, updated by useEffect
    highScore,
    handleCellClick,
    handleSwipe,
    resetGame,
    isAnimating,
    lastMatchInfo,
    explodingCells,
    clearEffect,
    boardSize,
    swappingCells,
    // Challenge tracking
    lastEliminatedFruits,
    lastDiscoCombo,
    lastStripedCreated,
    lastWrappedCreated,
    lastSpecialComboTriggered,
    // Combo system
    doublePointsActive,
    showDoublePointsAlert,
    consecutive4Combos,
    // Insta-elimination
    instaEliminationActive,
    showInstaEliminationPicker,
    useInstaElimination,
    cancelInstaElimination,
    openInstaEliminationPicker,
    // Shuffle alert
    showShuffleAlert,
    // Sword ability
    swordMode,
    swordAbilityUsed,
    swordUsesRemaining,
    maxSwordUses,
    activateSwordMode,
    cancelSwordMode,
    useSwordAbility,
    swordTier,
    swordSlashAnimation,
    // Special combo animation
    specialComboAnimation,
    // Consumable functions
    addBonusMoves,
    triggerShuffle,
    bombMode,
    activateBombMode,
    cancelBombMode,
    executeBomb,
    colorBombMode,
    activateColorBombMode,
    cancelColorBombMode,
    executeColorBomb,
    hintCell,
    showHint,
    // 2X XP consumable
    doubleXPActive,
    doubleXPTurnsLeft,
    activateDoubleXP,
    // Wrapped fruit placement
    wrappedMode,
    activateWrappedMode,
    cancelWrappedMode,
    executeWrappedPlacement,
    // Disco fruit placement
    discoMode,
    activateDiscoMode,
    cancelDiscoMode,
    executeDiscoPlacement,
    // Striped fruit placement
    stripedMode,
    activateStripedMode,
    cancelStripedMode,
    executeStripedPlacement,
    // Mission system (levels 51-100)
    missionType,
    missionProgress,
    ingredientsOnBoard,
    // Enhanced animation states
    lastMatchSize,
    showFruityCrush,
    screenShake,
    scorePopups,
    cascadeCount,
    showComboMultiplier,
    showVictoryAnimation,
    showLoseAnimation,
    isIdle,
    showLevelIntro,
    setShowLevelIntro,
    showUrgency,
    // Combo name system
    comboName,
    showComboName,
    newSpecialFruits,
    // Animation setters (for external control)
    setShowFruityCrush,
    setScreenShake,
    setScorePopups,
    setCascadeCount,
    setShowComboMultiplier,
    setShowVictoryAnimation,
    setShowLoseAnimation,
    setIsIdle,
    // God Sword ability (legendary equipment)
    godSwordMode,
    godSwordUsesRemaining,
    godSwordAnimation,
    activateGodSwordMode,
    cancelGodSwordMode,
    activateGodSword,
    initializeGodSwordUses,
    // === MILESTONE ITEM ABILITIES ===
    // Crystal Ball
    crystalBallActive,
    crystalBallPreview,
    activateCrystalBall,
    // Moon Stone
    moonStoneActive,
    activateMoonStone,
    // Thunder Strike
    thunderStrikeAnimation,
    activateThunderStrike,
    // Dragon Scale
    dragonScaleActive,
    activateDragonScale,
    // Ocean Pearl
    oceanPearlActive,
    activateOceanPearl,
    deactivateOceanPearl,
    // Trident Shard
    tridentShardAnimation,
    activateTridentShard,
    // Frost Crystal
    frostCrystalAnimation,
    activateFrostCrystal,
    // Aurora Essence
    auroraEssenceActive,
    activateAuroraEssence,
    
    // === NEW ITEM ABILITIES ===
    // Fruit Magnet
    activateFruitMagnet,
    // Tsunami Wave
    activateTsunamiWave,
    // Guardian Shield
    guardianShieldActive,
    activateGuardianShield,
    deactivateGuardianShield,
    // Eye of Ra
    activateEyeOfRa,
    // Yeti Fur
    yetiFurTurns,
    activateYetiFur,
    // Snow Globe
    snowGlobeTurns,
    activateSnowGlobe,
    // Ice Pick
    icePickMode,
    activateIcePick,
    // Pharaoh's Scarab
    activatePharaohScarab,
    // Ankh of Life
    ankhOfLifeActive,
    activateAnkhOfLife,
    // ===== VIP ITEM ABILITIES =====
    addMoves,
    triggerVIPShuffle,
    spawnRandomSpecials,
    clearObstacleType,
    executeCrossBlast,
    // ===== WORLD MECHANICS =====
    worldMechanic,
    // Volcanic Core
    lavaLevel,
    movesSinceLavaRise,
    lavaRiseRate,
    // Moonlight Garden
    isDayTime,
    movesSinceCycleChange,
    dayNightTransition,
    cycleLength,
    // Cosmic Nebula
    gravityDirection,
    gravityShiftPending,
    // Storm Kingdom
    lightningCells,
    showLightning,
    // Casino Royale
    activeMultiplier,
    showJackpot,
    // Alchemist's Lab
    potionColors,
    potionBonus,
    // Circus Carnival
    spinningCells,
    showSpinEffect,
    // Pirate Shipwreck
    waterLevel,
    movesSinceWaterRise,
    // Cherry Blossom Temple
    harmonyStreak,
    harmonyBonus,
    // Mushroom Cavern
    sporeCount,
    sporeCells,
  };
}
